"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { logger } from "@/src/logging/logger";
import { useAccess } from "@/src/access/access-context";
import { emitBrowserVoiceSignalingEvent } from "./browser-voice-observability";
import { acquireAuthNavigationHold } from "@/src/auth/auth-navigation-hold";
import { sparkOneAuthChangedEvent } from "@/src/auth/token-storage";
import type {
  ContactCenterBrowserRegistrationChangedPayload,
  ContactCenterVoiceCallChangedPayload,
  EmployeeVoiceChangedPayload,
} from "../realtime/contact-center-realtime-types";
import { VoiceRequestRejectedError, voiceApi } from "./api";
import type { BrowserOriginatePreparedCall } from "./browser-originated-call-contract";
import {
  getBrowserActivationId,
  getBrowserDeviceInstanceId,
  getBrowserTabId,
} from "./browser-device-instance";
import {
  BrowserPreparationCoordinator,
  BrowserRegistrationCoordinator,
  browserActivationCollisionCode,
  browserInstallationCredentialTarget,
  browserInstallationLimitCode,
  browserPreparationInProgressCode,
  ensureTabBrowserInstallation,
  isBrowserInstallationPreparing,
  type BrowserRegistrationAuthorityPath,
} from "./browser-registration-coordinator";
import {
  AudioPreferenceError,
  createAudioDevicePreferences,
  readAudioDevicePreferences,
  writeAudioDevicePreferences,
  type AudioDevicePreferencesScope,
  type AudioPreferenceErrorCode,
} from "./audio-device-preferences";
import { probeStoredSessionAuthority } from "@/src/auth/session-authority";
import {
  browserSessionAuthLostEvent,
  useBrowserPhoneLeadership,
  type BrowserRuntimeLifecycle,
  type BrowserRuntimeNotice,
  type BrowserRuntimePublicState,
  type MirroredBrowserDialog,
} from "./browser-session-owner";
import {
  SipSoftphone,
  type SipAudioDevice,
  type SipDialogSnapshot,
  type SipSoftphoneDiagnosticEvent,
  type SipSoftphoneEvent,
} from "./sip-softphone";
import type {
  VoiceBrowserActivationLease,
  VoiceBrowserActivationProof,
  VoiceBrowserPreparedPlacement,
  VoiceBrowserSession,
  VoiceCallRecord,
  LocalSipRegistrationState,
  ProviderRegistrationState,
} from "./types";
import type { BrowserCallingCapability } from "../../voice-platform/api";
import { browserCallingTerminalPresentation } from "../../voice-platform/browser-calling-capability";

type RuntimeContext = {
  ownsBrowserPhone: boolean;
  lifecycle: BrowserRuntimeLifecycle;
  incomingDialogs: MirroredBrowserDialog[];
  hasActiveCall: boolean;
  isTenantSwitchBlocked: () => boolean;
  runtimeMessage: string | null;
  localRegistrationState: LocalSipRegistrationState;
  providerRegistrationState: ProviderRegistrationState;
  runtimeNotice: BrowserRuntimeNotice | null;
  mediaState: BrowserRuntimePublicState["mediaState"];
  audioDevices: SipAudioDevice[];
  selectedInputDeviceId: string;
  selectedOutputDeviceId: string;
  supportsOutputSelection: boolean;
  audioPreferenceWarning: string | null;
  sipStatus: SipSoftphoneEvent | null;
  terminalEvent: BrowserCallTerminalEvent | null;
  isMuted: boolean;
  isTransitioning: boolean;
  isBrowserPhoneOwner: () => boolean;
  isSessionActive: (sessionId: string) => boolean;
  getActivePersistentRegistration: () => ActivePersistentBrowserRegistration | null;
  getActiveCallIdentity: (callSessionId: string) => ActiveBrowserCallIdentity | null;
  answerIncoming: (dialogId: string) => Promise<void>;
  rejectIncoming: (dialogId: string) => Promise<void>;
  refreshAudioDevices: () => Promise<void>;
  selectInputDevice: (deviceId: string) => Promise<void>;
  selectOutputDevice: (deviceId: string) => Promise<void>;
  retryAudio: () => Promise<void>;
  retryRegistration: () => Promise<void>;
  observeReadiness: (capability: BrowserCallingCapability) => void;
  isCallActive: (callSessionId: string) => boolean;
  hasActiveManagedCallerDialog: (callSessionId: string) => boolean;
  setMuted: (callSessionId: string, muted: boolean) => void;
  start: (placement: VoiceBrowserPreparedPlacement) => Promise<void>;
  attachCall: (
    sessionId: string,
    callSessionId: string,
    callerSipCorrelationId?: string | null,
  ) => void;
  armManagedCaller: (input: {
    connectorId: string;
    sessionId: string;
    callSessionId: string;
    callerSipCorrelationId: string;
  }) => Promise<void>;
  originatePreparedCall: (
    prepared: BrowserOriginatePreparedCall,
  ) => Promise<void>;
  bindPreparedCall: (input: {
    sessionId: string;
    preparedCallId: string;
    correlationId: string;
    callSessionId: string;
  }) => boolean;
  cancelPreparedCall: (preparedCallId: string) => Promise<boolean>;
  disarmManagedCaller: (callSessionId: string) => boolean;
  endCall: (callSessionId: string) => Promise<BrowserEndCallOutcome>;
  release: (session?: BrowserRuntimeSessionIdentity) => Promise<void>;
};

export type ActiveBrowserCallIdentity = {
  connectorId: string;
  clientUserRef: string;
  endpointDeviceId: string;
};

export type ActivePersistentBrowserRegistration = {
  connectorId: string;
  activationProof: VoiceBrowserActivationProof;
  sessionId: string;
  extensionId: string;
  endpointDeviceId: string;
};

export type BrowserCallTerminalEvent = {
  callSessionId: string;
  status: "ended" | "failed";
  message?: string;
  occurredAt: string;
};

export type BrowserEndCallOutcome =
  "terminated" | "already_ended" | "ambiguous_or_no_match";

export const browserAudioErrorMessages: Record<
  AudioPreferenceErrorCode,
  string
> = {
  AUDIO_PREFERENCES_NOT_OWNER:
    "Audio settings are active in another SparkOne tab.",
  AUDIO_PREFERENCES_RUNTIME_UNAVAILABLE:
    "Browser call audio is not available yet.",
  AUDIO_PREFERENCES_MICROPHONE_UNAVAILABLE:
    "Browser microphone is not available.",
  AUDIO_PREFERENCES_MICROPHONE_SWITCH_FAILED:
    "Microphone selection could not be changed. Your previous microphone is still in use.",
  AUDIO_PREFERENCES_SPEAKER_UNSUPPORTED:
    "This browser uses the system speaker and does not support selecting an output device.",
  AUDIO_PREFERENCES_SPEAKER_SWITCH_FAILED:
    "Speaker selection could not be changed. Your previous speaker is still in use.",
  AUDIO_PREFERENCES_DEVICE_LIST_UNAVAILABLE:
    "Audio device settings could not be refreshed. Try again.",
  AUDIO_PREFERENCES_SELECTION_REMOVED:
    "The selected audio device is no longer available.",
  AUDIO_PREFERENCES_OPERATION_SUPERSEDED:
    "Browser audio selection is no longer current. Choose a device again.",
  AUDIO_PREFERENCES_STORAGE_UNAVAILABLE:
    "Browser call audio was changed for this call but could not be saved.",
  AUDIO_PREFERENCES_INVALID_PREFERENCES:
    "Browser audio preferences are unavailable.",
};

function browserAudioError(
  code: AudioPreferenceErrorCode,
): AudioPreferenceError {
  return new AudioPreferenceError(code);
}

function hasBrowserAudioErrorCode(
  error: unknown,
  code: AudioPreferenceErrorCode,
): error is AudioPreferenceError {
  return error instanceof AudioPreferenceError && error.code === code;
}

export type BrowserRuntimeSessionIdentity = {
  connectorId: string;
  sessionId: string;
};

type BrowserReadinessTimingEventName =
  | "browser_voice.registration.ensure_started"
  | "browser_voice.registration.retry_authorized"
  | "browser_voice.registration.credentials_delivered"
  | "browser_voice.registration.local_registered"
  | "browser_voice.registration.authoritative_ready"
  | "browser_voice.registration.public_ready";

type BrowserReadinessTiming = {
  bootstrapGeneration: number;
  startedAt: number;
  emitted: Set<BrowserReadinessTimingEventName>;
};

type ActiveBrowserSession = {
  connectorId: string;
  clientUserRef: string;
  callSessionId: string | null;
  sessionId: string;
  persistent: boolean;
  extensionId: string;
  endpointDeviceId: string;
  authorityRevoked: boolean;
  releaseAuthNavigationHold: (() => void) | null;
  registrationStartedAt: number;
  registrationReadyObserved: boolean;
  expiresAtMs: number;
  mediaCredentialsExpiresAtMs: number | null;
  mediaCredentialsRefreshLeadMs: number;
  mediaCredentialsJitMinimumMs: number;
  mediaCredentialsRefresh: Promise<void> | null;
  mediaCredentialsBlocked: boolean;
  pendingRenewal: VoiceBrowserSession | null;
  pendingInstallationTarget: {
    installationId: string;
    generation: number;
    sessionId: string;
  } | null;
  pendingRotationKind: "provider_successor" | "independent_replacement" | null;
  renewalStartedAt: number | null;
  replacementPending: boolean;
  credentials: VoiceBrowserSession;
  pendingReplacementIdentity: {
    connectorId: string;
    clientUserRef: string;
    extensionId: string;
    endpointDeviceId: string;
  } | null;
  pendingBrowserOriginatedCall: {
    preparedCallId: string;
    correlationId: string;
  } | null;
  installationId: string | null;
  installationGeneration: number | null;
  installationStateVersion: number | null;
  activationLease: VoiceBrowserActivationLease | null;
  revocationActivationProof: VoiceBrowserActivationProof | null;
  authorityCleanupPromise: Promise<void> | null;
  localRegistrationState: LocalSipRegistrationState;
  providerRegistrationState: ProviderRegistrationState;
  localRegistrationEpisodes: Map<
    string,
    {
      clientObservationId: string;
      observedAt: string;
      observedAtMonotonic: number;
      submitted: boolean;
      active: boolean;
      abortController: AbortController | null;
    }
  >;
};

function readActiveBrowserSession(ref: {
  current: ActiveBrowserSession | null;
}): ActiveBrowserSession | null {
  return ref.current;
}

type RenewalFinalizationAttempt = {
  current: ActiveBrowserSession;
  pending: VoiceBrowserSession;
  predecessorSessionId: string;
  promise: Promise<void>;
};

type PendingCanonicalAction = {
  action: "answer" | "reject";
  startedAt: number;
  sipCorrelationId: string | null;
};

type CanonicalDialogTarget = {
  connectorId: string;
  callSessionId: string;
  participantId: string;
  endpointDeviceId: string;
  sipCorrelationId: string;
  answerAuthorized: boolean;
  rejectAuthorized: boolean;
};

type DialogClassification =
  | "unresolved_correlated"
  | "unresolved_inbound"
  | "actionable_inbound"
  | "managed_caller";

type CanonicalCorrelationClassification = {
  callSessionId: string;
  kind:
    "unresolved_inbound" | "actionable_inbound" | "managed_caller" | "terminal";
  role: "caller" | "device_leg";
};

type RecoveredInboundAuthorization = {
  call: VoiceCallRecord & { callSessionId: string };
  participant: NonNullable<VoiceCallRecord["participants"]>[number] & {
    endpointDeviceId: string;
    sipCorrelationId: string;
  };
};

function exactRecoveredInboundAuthorization(
  calls: VoiceCallRecord[],
  current: Pick<
    ActiveBrowserSession,
    "connectorId" | "clientUserRef" | "endpointDeviceId"
  >,
  sipCorrelationId: string,
): RecoveredInboundAuthorization | null {
  const matches: RecoveredInboundAuthorization[] = [];
  for (const call of calls) {
    if (
      !call.callSessionId ||
      call.connectorId !== current.connectorId ||
      call.state !== "RINGING" ||
      (call.direction !== "INBOUND" && call.direction !== "INTERNAL") ||
      call.availableCommands?.includes("answer") !== true
    ) {
      continue;
    }
    for (const participant of call.participants ?? []) {
      if (
        participant.role !== "device_leg" ||
        participant.state.toLowerCase() !== "ringing" ||
        participant.endpointType !== "browser_webrtc" ||
        participant.endpointDeviceId !== current.endpointDeviceId ||
        participant.handleUserRef !== current.clientUserRef ||
        participant.sipCorrelationId !== sipCorrelationId
      ) {
        continue;
      }
      matches.push({
        call: call as VoiceCallRecord & { callSessionId: string },
        participant: participant as RecoveredInboundAuthorization["participant"],
      });
    }
  }
  return matches.length === 1 ? matches[0] : null;
}

const INVITE_ENRICHMENT_TIMEOUT_MS = 2_000;
// Realtime PJSIP rows can take longer than the SIP client callback to become
// visible through Asterisk and the authoritative AMI observation path. Keep
// the same credential alive long enough for that convergence instead of
// rotating a valid installation into an identity-mismatch loop.
const REGISTRATION_STATUS_POLL_INTERVAL_MS = 500;
const REGISTRATION_STATUS_MAX_ATTEMPTS = 119;
// The authoritative per-tab installation path is the product default. Keep the
// legacy branch only as short-lived rollback code, not as an environment toggle.
const USE_AUTHORITATIVE_BROWSER_INSTALLATIONS = true;
const INSTALLATION_REGISTRATION_TIMEOUT_MS = 30_000;
const REGISTRATION_RENEWAL_TARGET_LEAD_MS = 90_000;
const REGISTRATION_RENEWAL_MINIMUM_LEAD_MS =
  INSTALLATION_REGISTRATION_TIMEOUT_MS + 1_000;
const MEDIA_CREDENTIAL_MAXIMUM_LEAD_MS = 15 * 60_000;
const MEDIA_CREDENTIAL_MINIMUM_LEAD_MS = 30_000;
const MEDIA_CREDENTIAL_MAXIMUM_JIT_MS = 5 * 60_000;
const MEDIA_CREDENTIAL_REFRESH_RETRY_MS = 5 * 60_000;
const MEDIA_CREDENTIAL_REFRESH_ROTATION_DEFER_MS = 1_000;
const ACTIVATION_LEASE_MINIMUM_LEAD_MS = 5_000;
const ACTIVATION_LEASE_MAXIMUM_LEAD_MS = 30_000;
const MAX_BROWSER_TIMER_DELAY_MS = 2_147_000_000;

type BrowserRuntimeTimerRef = { current: number | null };

function cancelBrowserRuntimeTimer(timerRef: BrowserRuntimeTimerRef): void {
  if (timerRef.current !== null) {
    window.clearTimeout(timerRef.current);
    timerRef.current = null;
  }
}

function replaceBrowserRuntimeTimer(
  timerRef: BrowserRuntimeTimerRef,
  callback: () => void,
  delay: number,
): void {
  cancelBrowserRuntimeTimer(timerRef);
  const timer = window.setTimeout(() => {
    // A callback that was already queued when a replacement was installed must
    // not clear or execute the newer timer's work.
    if (timerRef.current !== timer) return;
    timerRef.current = null;
    callback();
  }, delay);
  timerRef.current = timer;
}

function mediaCredentialsExpiryMs(session: VoiceBrowserSession): number | null {
  if (!session.mediaCredentialsExpiresAt) return null;
  const value = Date.parse(session.mediaCredentialsExpiresAt);
  return Number.isFinite(value) ? value : null;
}

export function mediaCredentialRefreshPolicy(input: {
  issuedAtMs: number;
  expiresAtMs: number;
  jitter?: number;
}) {
  const issuedTtlMs = Math.max(1_000, input.expiresAtMs - input.issuedAtMs);
  const baseLeadMs = Math.min(
    MEDIA_CREDENTIAL_MAXIMUM_LEAD_MS,
    Math.max(MEDIA_CREDENTIAL_MINIMUM_LEAD_MS, issuedTtlMs * 0.2),
    issuedTtlMs,
  );
  const jitter = Math.max(-0.1, Math.min(0.1, input.jitter ?? 0));
  const refreshLeadMs = Math.min(
    issuedTtlMs,
    MEDIA_CREDENTIAL_MAXIMUM_LEAD_MS,
    Math.max(
      Math.min(MEDIA_CREDENTIAL_MINIMUM_LEAD_MS, issuedTtlMs),
      Math.round(baseLeadMs * (1 + jitter)),
    ),
  );
  return {
    issuedTtlMs,
    refreshLeadMs,
    jitMinimumMs: Math.min(MEDIA_CREDENTIAL_MAXIMUM_JIT_MS, issuedTtlMs),
  };
}

export function activationLeaseHeartbeatDelay(
  lease: VoiceBrowserActivationLease,
  now = Date.now(),
  jitter = 0,
): number {
  const expiresAtMs = Date.parse(lease.expiresAt);
  const issuedAtMs = Date.parse(lease.issuedAt);
  const declaredTtlMs = Math.max(1_000, lease.ttlSeconds * 1_000);
  const issuedTtlMs =
    Number.isFinite(expiresAtMs) && Number.isFinite(issuedAtMs)
      ? Math.max(1_000, expiresAtMs - issuedAtMs)
      : declaredTtlMs;
  const boundedJitter = Math.max(-0.1, Math.min(0.1, jitter));
  const leadMs = Math.min(
    issuedTtlMs,
    ACTIVATION_LEASE_MAXIMUM_LEAD_MS,
    Math.max(
      Math.min(ACTIVATION_LEASE_MINIMUM_LEAD_MS, issuedTtlMs),
      Math.round(issuedTtlMs * 0.2 * (1 + boundedJitter)),
    ),
  );
  const authoritativeExpiryMs = Number.isFinite(expiresAtMs)
    ? expiresAtMs
    : issuedAtMs + declaredTtlMs;
  return Math.max(1_000, authoritativeExpiryMs - now - leadMs);
}

function browserSessionMediaPolicy(session: VoiceBrowserSession) {
  const expiresAtMs = mediaCredentialsExpiryMs(session);
  if (expiresAtMs === null) {
    return {
      expiresAtMs: null,
      refreshLeadMs: MEDIA_CREDENTIAL_MINIMUM_LEAD_MS,
      jitMinimumMs: MEDIA_CREDENTIAL_MINIMUM_LEAD_MS,
    };
  }
  const declaredIssuedAtMs = session.mediaCredentialsIssuedAt
    ? Date.parse(session.mediaCredentialsIssuedAt)
    : Number.NaN;
  const declaredTtlMs =
    session.mediaCredentialsTtlSeconds && session.mediaCredentialsTtlSeconds > 0
      ? session.mediaCredentialsTtlSeconds * 1_000
      : Number.NaN;
  const issuedAtMs = Number.isFinite(declaredIssuedAtMs)
    ? declaredIssuedAtMs
    : Number.isFinite(declaredTtlMs)
      ? expiresAtMs - declaredTtlMs
      : null;
  if (issuedAtMs === null) {
    return {
      expiresAtMs: null,
      refreshLeadMs: MEDIA_CREDENTIAL_MINIMUM_LEAD_MS,
      jitMinimumMs: MEDIA_CREDENTIAL_MINIMUM_LEAD_MS,
    };
  }
  const policy = mediaCredentialRefreshPolicy({
    issuedAtMs,
    expiresAtMs,
    jitter: Math.random() * 0.2 - 0.1,
  });
  return {
    expiresAtMs,
    refreshLeadMs: policy.refreshLeadMs,
    jitMinimumMs: policy.jitMinimumMs,
  };
}

async function waitForAuthoritativeRegistration(
  sessionId: string,
  isCurrent: () => boolean,
) {
  for (
    let attempt = 0;
    attempt <= REGISTRATION_STATUS_MAX_ATTEMPTS;
    attempt += 1
  ) {
    const observed = await voiceApi.getBrowserSessionStatus(sessionId);
    if (!isCurrent()) {
      throw new Error("Browser registration preparation was cancelled.");
    }
    if (observed.status === "registered") return observed;
    if (
      ["expired", "failed", "revocation_pending", "revoked"].includes(
        observed.status,
      )
    ) {
      throw new Error(
        `Browser registration became ${observed.status.replaceAll("_", " ")} before renewal cutover.`,
      );
    }
    if (attempt === REGISTRATION_STATUS_MAX_ATTEMPTS) {
      throw new Error(
        "The persistent Browser registration did not become ready before renewal cutover.",
      );
    }
    await new Promise<void>((resolve) => {
      window.setTimeout(resolve, REGISTRATION_STATUS_POLL_INTERVAL_MS);
    });
  }
  throw new Error("The persistent Browser registration did not become ready.");
}

const MAX_RUNTIME_RETENTION = 128;
const LOCAL_REGISTRATION_OBSERVATION_TIMEOUT_MS = 5_000;
const TERMINAL_PARTICIPANT_STATES = new Set([
  "answered_elsewhere",
  "rejected",
  "cancelled",
  "ended",
  "failed",
]);
const SIP_CORRELATION_PATTERN = /^(?:svp1|pcorr1)\.[A-Za-z0-9_-]{12,128}$/;
const LEGACY_PERSISTENT_DENIAL_STORAGE_KEY =
  "sparkone.browser_voice.terminal_denial.v1";

type PersistentBootstrapDenial = {
  readinessVersion: number | null;
  reasonCodes: readonly string[];
  message: string;
};

/**
 * A line denial is only safe to suppress for this mounted runtime. Browser
 * session restoration can retain sessionStorage across a browser restart, so
 * restoring the old marker would prevent a recovered PBX configuration from
 * ever being retried. Discard the legacy marker once, then keep the guard
 * in-memory until readiness changes or the user explicitly retries.
 */
function discardLegacyPersistentBootstrapDenial(): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(LEGACY_PERSISTENT_DENIAL_STORAGE_KEY);
  } catch {
    // Storage is only legacy cleanup; the in-memory guard remains sufficient.
  }
}

function isStickyPersistentDenial(
  error: unknown,
): error is VoiceRequestRejectedError {
  return (
    error instanceof VoiceRequestRejectedError &&
    ((error.status === 403 && error.code === "VOICE_LINE_NOT_ALLOWED") ||
      (error.status === 404 && error.code === "VOICE_BINDING_NOT_FOUND"))
  );
}

function stickyDenialMessage(error: VoiceRequestRejectedError): string {
  const state = ["AVAILABLE", "PROVISIONING", "UNAVAILABLE", "UNKNOWN"].includes(
    error.voiceState ?? "",
  )
    ? (error.voiceState as BrowserCallingCapability["state"])
    : "UNAVAILABLE";
  const presentation = browserCallingTerminalPresentation({
    startMode: "BLOCKED",
    state,
    reasonCodes: [...error.reasonCodes],
  });
  return (
    presentation?.summary ??
    "Browser phone preparation is unavailable until voice readiness changes."
  );
}

function validSipCorrelation(value: string | null): value is string {
  return Boolean(value && SIP_CORRELATION_PATTERN.test(value));
}

function telemetrySipResponseStatus(
  status: 400 | 404 | 405 | 415 | 416 | 481 | 482 | 488 | 500,
): 400 | 415 | 416 | 482 | 488 | 500 | undefined {
  switch (status) {
    case 400:
    case 415:
    case 416:
    case 482:
    case 488:
    case 500:
      return status;
    default:
      return undefined;
  }
}

function setCappedMap<K, V>(map: Map<K, V>, key: K, value: V): void {
  map.delete(key);
  map.set(key, value);
  while (map.size > MAX_RUNTIME_RETENTION) {
    const oldest = map.keys().next().value as K | undefined;
    if (oldest === undefined) break;
    map.delete(oldest);
  }
}

function addCappedSet<T>(set: Set<T>, value: T): void {
  set.delete(value);
  set.add(value);
  while (set.size > MAX_RUNTIME_RETENTION) {
    const oldest = set.values().next().value as T | undefined;
    if (oldest === undefined) break;
    set.delete(oldest);
  }
}

type BrowserAuthorityLossReason =
  | "auth_lost"
  | "voice_disabled"
  | "renewal_denied"
  | "session_expired"
  | "administrative_revoke"
  | "activation_lease_lost";

type RenewalFailureOutcome =
  "authority_revoked" | "replace_session" | "reconcile_conflict" | "retry";

const TERMINAL_RENEWAL_CODES = new Set([
  "VOICE_BROWSER_SESSION_AUTHORITY_REVOKED",
  "VOICE_BROWSER_SESSION_INELIGIBLE",
]);
const REPLACE_RENEWAL_CODES = new Set([
  "VOICE_BROWSER_SESSION_NOT_FOUND",
  "VOICE_BROWSER_SESSION_EXPIRED",
]);

function renewalFailureOutcome(error: unknown): RenewalFailureOutcome {
  if (!(error instanceof VoiceRequestRejectedError)) return "retry";
  if (error.code && TERMINAL_RENEWAL_CODES.has(error.code))
    return "authority_revoked";
  if (error.code && REPLACE_RENEWAL_CODES.has(error.code))
    return "replace_session";
  if (
    error.code === "VOICE_BROWSER_SESSION_RENEWAL_CONFLICT" ||
    (!error.code && error.status === 409)
  )
    return "reconcile_conflict";
  // A code-less 404 remains safe to recover by preparing a replacement. In
  // particular, generic 401/403/422 responses are not proof of authority loss.
  if (!error.code && error.status === 404) return "replace_session";
  return "retry";
}

function sessionExpiryMs(
  session: VoiceBrowserSession,
  now = Date.now(),
): number {
  const parsed = session.expiresAt ? Date.parse(session.expiresAt) : Number.NaN;
  return Number.isFinite(parsed)
    ? parsed
    : now + Math.max(1, session.registrationTtlSeconds) * 1_000;
}

function browserRuntimeSessionKey(
  identity: BrowserRuntimeSessionIdentity,
): string {
  return JSON.stringify([identity.connectorId, identity.sessionId]);
}

export type VoiceRuntimeSubscribe = (subscriber: {
  onCallChanged?: (payload: ContactCenterVoiceCallChangedPayload) => void;
  onBrowserRegistrationChanged?: (
    payload: ContactCenterBrowserRegistrationChangedPayload,
  ) => void;
  onEmployeeVoiceChanged?: (payload: EmployeeVoiceChangedPayload) => void;
  onRecover?: () => void | Promise<void>;
  includeOrganization?: boolean;
}) => () => void;

const Context = createContext<RuntimeContext | null>(null);

export function renewalDelay(
  session: VoiceBrowserSession,
  now = Date.now(),
): number {
  const expiresAt = sessionExpiryMs(session, now);
  const remainingMs = Math.max(0, expiresAt - now);
  const renewalLeadMs = Math.min(
    REGISTRATION_RENEWAL_TARGET_LEAD_MS,
    Math.max(REGISTRATION_RENEWAL_MINIMUM_LEAD_MS, Math.floor(remainingMs / 2)),
  );
  return Math.max(1_000, remainingMs - renewalLeadMs);
}

function renewalDelayWithJitter(session: VoiceBrowserSession): number {
  // Renew up to five seconds earlier. Never jitter past credential expiry.
  return Math.max(
    1_000,
    renewalDelay(session) - Math.floor(Math.random() * 5_001),
  );
}

function definitiveAvailabilityFailure(error: unknown): boolean {
  return (
    error instanceof VoiceRequestRejectedError &&
    [401, 403, 404, 409, 422].includes(error.status)
  );
}

function retryableAvailabilityFailure(error: unknown): boolean {
  if (!(error instanceof VoiceRequestRejectedError)) return true;
  return error.status === 429 || error.status >= 500;
}

function boundedRetryDelay(attempt: number): number {
  const base = Math.min(
    60_000,
    1_000 * 2 ** Math.min(Math.max(0, attempt - 1), 6),
  );
  return base + Math.floor(Math.random() * 1_000);
}

function boundedLatency(startedAt: number, now = Date.now()): number {
  return Math.min(300_000, Math.max(0, now - startedAt));
}

function monotonicNow(): number {
  return typeof performance === "undefined" ? Date.now() : performance.now();
}

function boundedMonotonicLatency(startedAt: number): number {
  return boundedElapsedMs(monotonicNow() - startedAt);
}

function authorityPathForPayload(
  payload: ContactCenterBrowserRegistrationChangedPayload,
): BrowserRegistrationAuthorityPath {
  return payload.observationSource === "handle_hint_verified"
    ? "verified_hint"
    : payload.observationSource === "ami_event"
      ? "provider_event"
      : payload.observationSource === "snapshot"
        ? "provider_snapshot"
        : "provider_event";
}

function trustedProviderToSocketLatency(
  payload: ContactCenterBrowserRegistrationChangedPayload,
  receivedAt = Date.now(),
): number | null {
  const source =
    payload.status === "registered" && payload.registeredAt
      ? payload.registeredAt
      : payload.occurredAt;
  const sourceMs = Date.parse(source);
  const raw = receivedAt - sourceMs;
  if (!Number.isFinite(sourceMs) || raw < -5_000 || raw > 24 * 60 * 60 * 1_000) {
    return null;
  }
  return boundedElapsedMs(raw);
}

function boundedElapsedMs(value: number): number {
  return Math.min(300_000, Math.max(0, Math.floor(value)));
}

function participantOutcomeCategory(value: string): string {
  switch (value.toLowerCase()) {
    case "ringing":
    case "answered":
    case "answered_elsewhere":
    case "rejected":
    case "cancelled":
    case "ended":
    case "failed":
      return value.toLowerCase();
    default:
      return "other";
  }
}

function callStateCategory(value: string): string {
  switch (value) {
    case "RINGING":
    case "ANSWERED":
    case "HELD":
    case "ENDED":
    case "FAILED":
      return value.toLowerCase();
    default:
      return "other";
  }
}

function publicDialog(
  dialog: SipDialogSnapshot,
  target: CanonicalDialogTarget | null,
): MirroredBrowserDialog | null {
  if (dialog.direction !== "incoming") return null;
  if (!["ringing", "answering", "connected"].includes(dialog.state))
    return null;
  return {
    dialogId: dialog.dialogId,
    state: dialog.state as MirroredBrowserDialog["state"],
    sipCorrelationId: dialog.sipCorrelationId,
    answerAuthorized: target?.answerAuthorized === true,
    ...(target?.answerAuthorized
      ? {
          authorizedCallSessionId: target.callSessionId,
          authorizedParticipantId: target.participantId,
          authorizedEndpointDeviceId: target.endpointDeviceId,
        }
      : {}),
  };
}

function lifecycleForSip(
  event: SipSoftphoneEvent,
): BrowserRuntimeLifecycle | null {
  return (
    (
      {
        "requesting-mic": "requesting_permission",
        registering: "registering",
        registered: "registering",
        dialing: "registering",
        outgoing_ringing: "ringing",
        incoming: "incoming_unenriched",
        answering: "answering",
        connected: "connected",
        rejected: "ready",
        ended: "ready",
        failed: "failed",
        idle: "idle",
      } satisfies Record<string, BrowserRuntimeLifecycle>
    )[event.status] ?? null
  );
}

const initialPublicState: BrowserRuntimePublicState = {
  lifecycle: "idle",
  dialogs: [],
  hasActiveCall: false,
  isMuted: false,
  mediaState: "idle",
  localRegistrationState: "idle",
  providerRegistrationState: "unknown",
  message: null,
  notice: null,
};

export function BrowserVoiceRuntimeProvider({
  children,
  subscribeVoice,
  persistentEnabled = false,
}: {
  children: ReactNode;
  subscribeVoice: VoiceRuntimeSubscribe;
  persistentEnabled?: boolean;
}) {
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [voiceSubscriptionReady, setVoiceSubscriptionReady] = useState(false);
  const voiceSubscriptionReadyRef = useRef(false);
  const [sipStatus, setSipStatus] = useState<SipSoftphoneEvent | null>(null);
  const [terminalEvent, setTerminalEvent] =
    useState<BrowserCallTerminalEvent | null>(null);
  const [publicState, setPublicState] = useState<BrowserRuntimePublicState>({
    ...initialPublicState,
    lifecycle: "idle",
  });
  const [audioDevices, setAudioDevices] = useState<SipAudioDevice[]>([]);
  const [selectedInputDeviceId, setSelectedInputDeviceId] = useState("");
  const [selectedOutputDeviceId, setSelectedOutputDeviceId] = useState("");
  const [supportsOutputSelection, setSupportsOutputSelection] = useState(false);
  const [audioPreferenceWarning, setAudioPreferenceWarning] = useState<
    string | null
  >(null);
  const { activeTenant, user } = useAccess();
  // Multi-tab leader election. Only the lock-holding tab is "active" and may
  // ensure the installation / start the SIP UA; other tabs stay dormant. Falls
  // back to always-active when Web Locks is unavailable so calling is never
  // blocked. See useBrowserPhoneLeadership for the safety kill-switch.
  const browserPhoneLeadership = useBrowserPhoneLeadership(user?.id);
  const ownsBrowserPhone = browserPhoneLeadership.owned;
  const browserPhoneLeadershipEpoch = browserPhoneLeadership.epoch;
  // Latest-value ref so isBrowserPhoneOwner() (a stable empty-deps callback)
  // reflects leadership changes without forcing every dependent callback to
  // re-memoize on each ownership transition.
  const ownsBrowserPhoneRef = useRef(ownsBrowserPhone);
  ownsBrowserPhoneRef.current = ownsBrowserPhone;
  const audioPreferencesScope = useMemo<AudioDevicePreferencesScope | null>(
    () =>
      activeTenant?.id && user?.id
        ? { tenantId: activeTenant.id, userId: user.id }
        : null,
    [activeTenant, user],
  );
  const softphoneRef = useRef<SipSoftphone | null>(null);
  const canonicalDialogTargetsRef = useRef(
    new Map<string, CanonicalDialogTarget>(),
  );
  const dialogClassificationsRef = useRef(
    new Map<string, DialogClassification>(),
  );
  const canonicalByCorrelationRef = useRef(
    new Map<string, CanonicalCorrelationClassification>(),
  );
  const canonicalSequenceByCallRef = useRef(new Map<string, number>());
  const canonicalCallerCorrelationByCallRef = useRef(new Map<string, string>());
  const callerSipCorrelationHintByCallRef = useRef(new Map<string, string>());
  const terminalCallIdsRef = useRef(new Set<string>());
  const consumedCallerCorrelationsRef = useRef(new Set<string>());
  const autoAnsweringDialogsRef = useRef(new Set<string>());
  const callerSipCorrelationHintRef = useRef<string | null>(null);
  const canonicalCallDialogsRef = useRef(new Map<string, string>());
  const sessionRef = useRef<ActiveBrowserSession | null>(null);
  const registrationCoordinatorRef =
    useRef<BrowserRegistrationCoordinator | null>(null);
  if (!registrationCoordinatorRef.current) {
    registrationCoordinatorRef.current = new BrowserRegistrationCoordinator({
      readInstallation: (installationId) =>
        voiceApi.getBrowserInstallation(installationId),
      timeoutMs: INSTALLATION_REGISTRATION_TIMEOUT_MS,
    });
  }
  const preparationCoordinatorRef =
    useRef<BrowserPreparationCoordinator | null>(null);
  if (!preparationCoordinatorRef.current) {
    preparationCoordinatorRef.current = new BrowserPreparationCoordinator({
      readInstallation: (installationId, signal) =>
        voiceApi.getBrowserInstallation(installationId, signal),
    });
  }
  const preparationAbortRef = useRef<AbortController | null>(null);
  const recoveryEpisodeRef = useRef(0);
  const authoritativeRegistrationSessionIdsRef = useRef(new Set<string>());
  const renewalRef = useRef<number | null>(null);
  const mediaCredentialsRefreshRef = useRef<number | null>(null);
  const activationLeaseHeartbeatRef = useRef<number | null>(null);
  const bootstrapRetryRef = useRef<number | null>(null);
  const registrationPollRef = useRef<number | null>(null);
  const localConfirmationWarningRef = useRef<number | null>(null);
  const bootstrapAttemptRef = useRef(0);
  const registrationPollAttemptsRef = useRef(0);
  const replacementPollAttemptsRef = useRef(0);
  const confirmRegistrationRef = useRef<() => void>(() => undefined);
  const renewalAttemptRef = useRef(0);
  const renewalFinalizationRef = useRef<RenewalFinalizationAttempt | null>(
    null,
  );
  // The browser runtime, rather than a React effect, owns this lock. It
  // therefore survives modal, route, realtime, and ordinary component renders.
  const transitionPromiseRef = useRef<Promise<void> | null>(null);
  const transitionActiveRef = useRef(false);
  const replacementTransitionPromiseRef = useRef<Promise<void> | null>(null);
  const releasePromisesRef = useRef(new Map<string, Promise<void>>());
  const mountedRef = useRef(true);
  const persistentEnabledRef = useRef(persistentEnabled);
  // Terminal auth loss is permanent for this provider lifetime. It fences
  // dormant followers and every asynchronous bootstrap/activation boundary.
  const authEligibleRef = useRef(true);
  const bootstrapGenerationRef = useRef(0);
  const browserReadinessTimingRef = useRef<BrowserReadinessTiming | null>(null);
  // Pending activations do not own the public runtime until every async step
  // succeeds under this generation fence.
  const activationGenerationRef = useRef(0);
  const activationInFlightRef = useRef<number | null>(null);
  const audioDeviceGenerationRef = useRef(0);
  const audioPreferenceScopeGenerationRef = useRef(0);
  const audioPreferenceScopeIdentity = audioPreferencesScope
    ? `${audioPreferencesScope.tenantId}\0${audioPreferencesScope.userId}`
    : null;
  const previousAudioPreferenceScopeIdentityRef = useRef(
    audioPreferenceScopeIdentity,
  );
  const audioPreferencesScopeRef = useRef<AudioDevicePreferencesScope | null>(
    audioPreferencesScope,
  );
  // Every media-affecting or preference-reconciling operation joins this queue.
  // Its own promise is still returned to the initiating caller for error handling.
  const audioDeviceOperationQueueRef = useRef<Promise<void>>(Promise.resolve());
  // Retains a scoped output preference while no current remote route can
  // authoritatively apply it. It is never broadcast between tabs.
  const pendingOutputPreferenceRef = useRef("");
  const restoreAudioDevicePreferencesRef = useRef<
    (phone: SipSoftphone) => Promise<void>
  >(async () => undefined);
  const bootstrapRunningRef = useRef(false);
  const bootstrapRequestedRef = useRef(false);
  const readinessVersionRef = useRef<number | null>(null);
  const persistentDenialRef = useRef<PersistentBootstrapDenial | null>(null);
  useEffect(() => {
    discardLegacyPersistentBootstrapDenial();
  }, []);
  const bootstrapRef = useRef<() => Promise<void>>(async () => undefined);
  const scheduleRef = useRef<
    (session: VoiceBrowserSession, delay?: number) => void
  >(() => undefined);
  const scheduleMediaCredentialsRefreshRef = useRef<
    (current: ActiveBrowserSession, phone: SipSoftphone, delay?: number) => void
  >(() => undefined);
  const scheduleInstallationRef = useRef<
    (current: ActiveBrowserSession, delay?: number) => void
  >(() => undefined);
  const scheduleActivationLeaseHeartbeatRef = useRef<
    (current: ActiveBrowserSession, delay?: number) => void
  >(() => undefined);
  const replacePersistentRegistrationRef = useRef<
    (current: ActiveBrowserSession, reason: string) => Promise<void>
  >(async () => undefined);
  const finalizeRenewalRef = useRef<
    (current: ActiveBrowserSession) => Promise<void>
  >(async () => undefined);
  const confirmIndependentReplacementRef = useRef<
    (current: ActiveBrowserSession) => Promise<void>
  >(async () => undefined);
  const disarmManagedCallerRef = useRef<(callSessionId: string) => boolean>(
    () => false,
  );
  const inviteObservedAtRef = useRef(new Map<string, number>());
  const inviteUiObservedRef = useRef(new Set<string>());
  const inviteEnrichedRef = useRef(new Set<string>());
  const inviteEnrichmentTimeoutRef = useRef(new Map<string, number>());
  const pendingCanonicalActionsRef = useRef(
    new Map<string, PendingCanonicalAction>(),
  );
  useLayoutEffect(() => {
    persistentEnabledRef.current = persistentEnabled;
  }, [persistentEnabled]);

  const cancelBrowserPreparationWait = useCallback(() => {
    preparationAbortRef.current?.abort();
    preparationAbortRef.current = null;
  }, []);

  useEffect(() => {
    audioPreferencesScopeRef.current = audioPreferencesScope;
    if (
      previousAudioPreferenceScopeIdentityRef.current ===
      audioPreferenceScopeIdentity
    ) {
      return;
    }
    previousAudioPreferenceScopeIdentityRef.current =
      audioPreferenceScopeIdentity;
    audioPreferenceScopeGenerationRef.current += 1;
    cancelBrowserPreparationWait();
    const scopeGeneration = audioPreferenceScopeGenerationRef.current;
    audioDeviceGenerationRef.current += 1;
    pendingOutputPreferenceRef.current = "";
    // Reset after the effect returns so a real tenant/user identity transition
    // does not perform synchronous React state writes. Initial mount is already
    // empty and must not race preference restoration with a queued reset.
    queueMicrotask(() => {
      if (
        !mountedRef.current ||
        scopeGeneration !== audioPreferenceScopeGenerationRef.current
      ) {
        return;
      }
      setSelectedInputDeviceId("");
      setSelectedOutputDeviceId("");
      setSupportsOutputSelection(false);
      setAudioPreferenceWarning(null);
    });
  }, [
    audioPreferenceScopeIdentity,
    audioPreferencesScope,
    cancelBrowserPreparationWait,
  ]);

  const updatePublicState = useCallback(
    (patch: Partial<BrowserRuntimePublicState>) => {
      setPublicState((current) => ({ ...current, ...patch }));
    },
    [],
  );

  const setTransitioning = useCallback((active: boolean) => {
    transitionActiveRef.current = active;
    setIsTransitioning(active);
  }, []);

  const runSerializedTransition = useCallback(
    (operation: () => Promise<void>) => {
      const existing = transitionPromiseRef.current;
      if (existing) return existing;
      setTransitioning(true);
      let transition: Promise<void>;
      try {
        transition = operation();
      } catch (error) {
        transition = Promise.reject(error);
      }
      transitionPromiseRef.current = transition;
      void transition.then(
        () => {
          if (transitionPromiseRef.current === transition) {
            transitionPromiseRef.current = null;
            if (
              !sessionRef.current?.pendingRenewal &&
              !sessionRef.current?.replacementPending &&
              replacementTransitionPromiseRef.current === null
            ) {
              setTransitioning(false);
            }
          }
        },
        (error: unknown) => {
          if (transitionPromiseRef.current === transition) {
            transitionPromiseRef.current = null;
            setTransitioning(false);
          }
          logger.error("sparkone.browser_voice.transition_unhandled", {
            failureCategory:
              error instanceof VoiceRequestRejectedError
                ? "request_rejected"
                : "unexpected_failure",
          });
        },
      );
      return transition;
    },
    [setTransitioning],
  );

  const stopPhone = useCallback((phone: SipSoftphone | null) => {
    if (!phone) return;
    try {
      phone.hangup();
    } catch {
      logger.warn("sparkone.browser_voice.phone_stop_failed", {
        failureCategory: "local_cleanup_failed",
      });
    }
  }, []);

  const invalidatePendingActivations = useCallback(() => {
    activationGenerationRef.current += 1;
  }, []);

  const activeActivationProof = useCallback(
    (
      current: ActiveBrowserSession | null = sessionRef.current,
    ): VoiceBrowserActivationProof => {
      if (
        !current?.activationLease ||
        current.activationLease.state !== "ACTIVE"
      ) {
        throw new VoiceRequestRejectedError(
          "This tab no longer owns the Browser activation lease.",
          403,
          "voice_browser_session_authority_revoked",
        );
      }
      return {
        leaseId: current.activationLease.leaseId,
        tabId: getBrowserTabId(),
        reclaimToken: getBrowserActivationId(),
      };
    },
    [],
  );

  const emitRuntimeVoiceEvent = useCallback(
    (event: Parameters<typeof emitBrowserVoiceSignalingEvent>[0]) => {
      const current = sessionRef.current;
      const callSessionId =
        event.callSessionId ??
        (event.sipCorrelationId
          ? canonicalByCorrelationRef.current.get(event.sipCorrelationId)
              ?.callSessionId
          : null) ??
        current?.callSessionId ??
        undefined;
      emitBrowserVoiceSignalingEvent({
        ...event,
        ...(callSessionId ? { callSessionId } : {}),
        ...(current?.sessionId ? { browserSessionId: current.sessionId } : {}),
      });
    },
    [],
  );

  const emitBrowserReadinessTiming = useCallback(
    (
      eventName: BrowserReadinessTimingEventName,
      options: {
        outcome?: "started" | "succeeded";
        registrationState?: "registering" | "registered";
        attempt?: number;
        authorityPath?: BrowserRegistrationAuthorityPath;
      } = {},
    ) => {
      const timing = browserReadinessTimingRef.current;
      if (
        !timing ||
        timing.bootstrapGeneration !== bootstrapGenerationRef.current ||
        timing.emitted.has(eventName)
      )
        return;
      timing.emitted.add(eventName);
      emitRuntimeVoiceEvent({
        eventName,
        severity: "INFO",
        phase: "registration",
        outcome: options.outcome ?? "succeeded",
        elapsedMs: boundedLatency(timing.startedAt),
        ...(options.registrationState
          ? { registrationState: options.registrationState }
          : {}),
        ...(options.attempt === undefined ? {} : { attempt: options.attempt }),
        ...(options.authorityPath
          ? { authorityPath: options.authorityPath }
          : {}),
      });
    },
    [emitRuntimeVoiceEvent],
  );

  const clearAuthorityTimers = useCallback(() => {
    cancelBrowserRuntimeTimer(renewalRef);
    cancelBrowserRuntimeTimer(mediaCredentialsRefreshRef);
    cancelBrowserRuntimeTimer(activationLeaseHeartbeatRef);
    cancelBrowserRuntimeTimer(bootstrapRetryRef);
    cancelBrowserRuntimeTimer(registrationPollRef);
    cancelBrowserRuntimeTimer(localConfirmationWarningRef);
    cancelBrowserPreparationWait();
    bootstrapAttemptRef.current = 0;
    registrationPollAttemptsRef.current = 0;
    replacementPollAttemptsRef.current = 0;
    renewalAttemptRef.current = 0;
  }, [cancelBrowserPreparationWait]);

  const clearInviteObservations = useCallback(() => {
    for (const timeout of inviteEnrichmentTimeoutRef.current.values()) {
      window.clearTimeout(timeout);
    }
    inviteEnrichmentTimeoutRef.current.clear();
    inviteObservedAtRef.current.clear();
    inviteUiObservedRef.current.clear();
    inviteEnrichedRef.current.clear();
    pendingCanonicalActionsRef.current.clear();
    canonicalDialogTargetsRef.current.clear();
    dialogClassificationsRef.current.clear();
    canonicalByCorrelationRef.current.clear();
    canonicalSequenceByCallRef.current.clear();
    canonicalCallerCorrelationByCallRef.current.clear();
    callerSipCorrelationHintByCallRef.current.clear();
    terminalCallIdsRef.current.clear();
    consumedCallerCorrelationsRef.current.clear();
    autoAnsweringDialogsRef.current.clear();
    callerSipCorrelationHintRef.current = null;
    canonicalCallDialogsRef.current.clear();
  }, []);

  const revokeRegistration = useCallback(
    (
      identity: BrowserRuntimeSessionIdentity,
      activationProof?: VoiceBrowserActivationProof,
    ): Promise<void> => {
      const releaseKey = browserRuntimeSessionKey(identity);
      const existing = releasePromisesRef.current.get(releaseKey);
      if (existing) return existing;
      const authorizedProof = activationProof ?? activeActivationProof();
      const pending = voiceApi
        .revokeBrowserSession({
          connectorId: identity.connectorId,
          sessionId: identity.sessionId,
          activationProof: authorizedProof,
        })
        .then(() => undefined)
        .catch(() => {
          logger.warn("sparkone.browser_voice.registration_revoke_failed", {
            failureCategory: "request_failed",
          });
        })
        .finally(() => {
          if (releasePromisesRef.current.get(releaseKey) === pending) {
            releasePromisesRef.current.delete(releaseKey);
          }
        });
      releasePromisesRef.current.set(releaseKey, pending);
      return pending;
    },
    [activeActivationProof],
  );

  const revokeSessionRegistrations = useCallback(
    (
      session: ActiveBrowserSession,
      activationProof?: VoiceBrowserActivationProof,
    ): Promise<void> => {
      // Runtime recovery owns only short-lived SIP generations. The stable
      // installation is a product identity and may be revoked only by an
      // explicit logout or administrative lifecycle command.
      const identities: BrowserRuntimeSessionIdentity[] = [
        { connectorId: session.connectorId, sessionId: session.sessionId },
      ];
      if (session.pendingRenewal) {
        identities.push({
          connectorId:
            session.pendingReplacementIdentity?.connectorId ??
            session.connectorId,
          sessionId: session.pendingRenewal.sessionId,
        });
      }
      const uniqueIdentities = identities.filter(
        (identity, index) =>
          identities.findIndex(
            (candidate) =>
              candidate.connectorId === identity.connectorId &&
              candidate.sessionId === identity.sessionId,
          ) === index,
      );
      if (uniqueIdentities.length === 1) {
        return revokeRegistration(uniqueIdentities[0], activationProof);
      }
      return uniqueIdentities.reduce<Promise<void>>(
        (pending, identity) =>
          pending.then(() => revokeRegistration(identity, activationProof)),
        Promise.resolve(),
      );
    },
    [revokeRegistration],
  );

  const releaseActivationLease = useCallback(
    (current: ActiveBrowserSession): Promise<void> => {
      if (!current.activationLease) return Promise.resolve();
      const lease = current.activationLease;
      current.activationLease = null;
      return voiceApi
        .releaseBrowserActivationLease({
          leaseId: lease.leaseId,
          tabId: getBrowserTabId(),
          reclaimToken: getBrowserActivationId(),
        })
        .then(() => undefined)
        .catch(() => {
          logger.warn(
            "sparkone.browser_voice.activation_lease_release_failed",
            {
              failureCategory: "request_failed",
            },
          );
        });
    },
    [],
  );

  const release = useCallback(
    (
      expected?: BrowserRuntimeSessionIdentity,
      options?: { revokeRemoteRegistration?: boolean },
    ) => {
      const revokeRemoteRegistration =
        options?.revokeRemoteRegistration ?? true;
      const current = sessionRef.current;
      const releasesCurrent = Boolean(
        current &&
        (!expected ||
          (current.connectorId === expected.connectorId &&
            current.sessionId === expected.sessionId)),
      );
      const registrationActivationProof =
        revokeRemoteRegistration && releasesCurrent && current
          ? (current.revocationActivationProof ??
            activeActivationProof(current))
          : undefined;
      if (current && registrationActivationProof) {
        current.revocationActivationProof = registrationActivationProof;
      }
      if (!expected || !current || releasesCurrent) {
        invalidatePendingActivations();
      }
      if (releasesCurrent) {
        for (const episode of current?.localRegistrationEpisodes.values() ??
          []) {
          episode.active = false;
          episode.abortController?.abort();
          episode.abortController = null;
        }
        const releaseAuthNavigationHold = current?.releaseAuthNavigationHold;
        bootstrapGenerationRef.current += 1;
        clearAuthorityTimers();
        sessionRef.current = null;
        authoritativeRegistrationSessionIdsRef.current.clear();
        setTransitioning(false);
        clearInviteObservations();
        const phone = softphoneRef.current;
        softphoneRef.current = null;
        stopPhone(phone);
        setSipStatus(null);
        audioDeviceGenerationRef.current += 1;
        setAudioDevices([]);
        setSelectedInputDeviceId("");
        setSelectedOutputDeviceId("");
        setSupportsOutputSelection(false);
        pendingOutputPreferenceRef.current = "";
        setAudioPreferenceWarning(null);
        updatePublicState(initialPublicState);
        releaseAuthNavigationHold?.();
      }

      const leaseRelease =
        releasesCurrent && current
          ? releaseActivationLease(current)
          : Promise.resolve();
      if (!revokeRemoteRegistration) return leaseRelease;
      const registrationIdentity = expected ?? current;
      const registrationRelease =
        releasesCurrent && current
          ? revokeSessionRegistrations(current, registrationActivationProof)
          : registrationIdentity
            ? revokeRegistration(registrationIdentity)
            : Promise.resolve();
      return Promise.all([leaseRelease, registrationRelease]).then(
        () => undefined,
      );
    },
    [
      activeActivationProof,
      clearAuthorityTimers,
      clearInviteObservations,
      invalidatePendingActivations,
      releaseActivationLease,
      revokeRegistration,
      revokeSessionRegistrations,
      stopPhone,
      updatePublicState,
    ],
  );

  // A React provider unmount is also the normal browser page-reload path. Keep
  // a persistent registration alive remotely so the reloaded, ownership-proven
  // tab can obtain its successor credential; only dispose this document's SIP
  // UA and state. Explicit authority-loss and disable flows still use release()
  // and retain their remote revocation behavior.
  const releaseForProviderUnmount = useCallback(() => {
    const current = sessionRef.current;
    return release(undefined, {
      revokeRemoteRegistration: !current?.persistent,
    });
  }, [release]);

  const suspendRegistrationAuthority = useCallback(
    (reason: BrowserAuthorityLossReason): Promise<void> => {
      const current = sessionRef.current;
      const phone = softphoneRef.current;
      if (!current || !phone) return Promise.resolve();
      const identity = {
        connectorId: current.connectorId,
        sessionId: current.sessionId,
      };
      const activationProof =
        current.revocationActivationProof ?? activeActivationProof(current);
      current.revocationActivationProof = activationProof;
      if (current.authorityRevoked) {
        return current.authorityCleanupPromise ?? Promise.resolve();
      }

      const activeDialogs = phone.getDialogs();
      const preservedLifecycle = activeDialogs.some(
        (dialog) => dialog.state === "connected",
      )
        ? "connected"
        : activeDialogs.some((dialog) => dialog.state === "answering")
          ? "answering"
          : null;
      const preservesDialog = preservedLifecycle !== null;
      if (preservesDialog && !current.releaseAuthNavigationHold) {
        current.releaseAuthNavigationHold = acquireAuthNavigationHold();
      }
      current.authorityRevoked = true;
      clearAuthorityTimers();
      phone.suspendRegistrationAuthority();
      logger.info("sparkone.browser_voice.registration_authority_revoked", {
        reason,
        connectedDialogPreserved: preservesDialog,
      });
      emitRuntimeVoiceEvent({
        eventName: "browser_voice.registration.authority_revoked",
        severity: "WARN",
        phase: "registration",
        outcome: "rejected",
        reasonCode: "offer_authority_revoked",
        registrationState: "revoked",
        offerAuthority: "revoked",
        connectedDialogPreserved: preservesDialog,
      });
      const registrationRevocation = revokeSessionRegistrations(
        current,
        activationProof,
      );
      const localCleanup = preservesDialog
        ? releaseActivationLease(current)
        : release(identity, { revokeRemoteRegistration: false });

      if (preservesDialog) {
        updatePublicState({
          lifecycle: preservedLifecycle,
          message:
            "Calling access ended. The connected call will continue until hangup.",
        });
      }
      const cleanup = Promise.all([registrationRevocation, localCleanup]).then(
        () => undefined,
      );
      current.authorityCleanupPromise = cleanup;
      return cleanup;
    },
    [
      clearAuthorityTimers,
      emitRuntimeVoiceEvent,
      release,
      releaseActivationLease,
      revokeSessionRegistrations,
      updatePublicState,
    ],
  );

  const scheduleActivationLeaseHeartbeat = useCallback(
    (current: ActiveBrowserSession, delay?: number) => {
      const lease = current.activationLease;
      if (!lease || !current.installationId) return;
      replaceBrowserRuntimeTimer(
        activationLeaseHeartbeatRef,
        () => {
          if (
            sessionRef.current !== current ||
            current.authorityRevoked ||
            !current.activationLease
          )
            return;
          const expectedLeaseId = current.activationLease.leaseId;
          void voiceApi
            .renewBrowserActivationLease({
              leaseId: expectedLeaseId,
              tabId: getBrowserTabId(),
              reclaimToken: getBrowserActivationId(),
            })
            .then((renewed) => {
              if (
                sessionRef.current !== current ||
                current.authorityRevoked ||
                current.activationLease?.leaseId !== expectedLeaseId ||
                !renewed
              )
                return;
              current.activationLease = renewed;
              scheduleActivationLeaseHeartbeatRef.current(current);
            })
            .catch((error: unknown) => {
              if (sessionRef.current !== current || current.authorityRevoked)
                return;
              const expiresAtMs = current.activationLease
                ? Date.parse(current.activationLease.expiresAt)
                : Number.NaN;
              if (
                definitiveAvailabilityFailure(error) ||
                (Number.isFinite(expiresAtMs) && expiresAtMs <= Date.now())
              ) {
                void suspendRegistrationAuthority("activation_lease_lost");
                return;
              }
              const retryDelay = Number.isFinite(expiresAtMs)
                ? Math.max(250, Math.min(5_000, expiresAtMs - Date.now()))
                : 5_000;
              scheduleActivationLeaseHeartbeatRef.current(current, retryDelay);
            });
        },
        Math.min(
          MAX_BROWSER_TIMER_DELAY_MS,
          delay ??
            activationLeaseHeartbeatDelay(
              lease,
              Date.now(),
              Math.random() * 0.2 - 0.1,
            ),
        ),
      );
    },
    [suspendRegistrationAuthority],
  );
  useEffect(() => {
    scheduleActivationLeaseHeartbeatRef.current =
      scheduleActivationLeaseHeartbeat;
  }, [scheduleActivationLeaseHeartbeat]);

  const syncDialogs = useCallback(
    (phone: SipSoftphone) => {
      const snapshots = phone.getDialogs();
      const activeDialogIds = new Set(
        snapshots.map((dialog) => dialog.dialogId),
      );
      for (const dialogId of dialogClassificationsRef.current.keys()) {
        if (!activeDialogIds.has(dialogId)) {
          dialogClassificationsRef.current.delete(dialogId);
          canonicalDialogTargetsRef.current.delete(dialogId);
          // Keep the bounded pending action until authoritative call evidence
          // confirms its outcome, even though local SIP teardown is synchronous.
          inviteObservedAtRef.current.delete(dialogId);
          inviteUiObservedRef.current.delete(dialogId);
          inviteEnrichedRef.current.delete(dialogId);
          autoAnsweringDialogsRef.current.delete(dialogId);
          const timeout = inviteEnrichmentTimeoutRef.current.get(dialogId);
          if (timeout !== undefined) window.clearTimeout(timeout);
          inviteEnrichmentTimeoutRef.current.delete(dialogId);
        }
      }
      const dialogs = snapshots
        .filter((dialog) => {
          const classification = dialogClassificationsRef.current.get(
            dialog.dialogId,
          );
          return (
            classification !== "unresolved_correlated" &&
            classification !== "managed_caller"
          );
        })
        .map((dialog) =>
          publicDialog(
            dialog,
            dialogClassificationsRef.current.get(dialog.dialogId) ===
              "actionable_inbound"
              ? (canonicalDialogTargetsRef.current.get(dialog.dialogId) ?? null)
              : null,
          ),
        )
        .filter((dialog): dialog is MirroredBrowserDialog => dialog !== null);
      updatePublicState({
        dialogs,
        hasActiveCall: snapshots.some((dialog) =>
          ["answering", "connected"].includes(dialog.state),
        ),
      });
    },
    [updatePublicState],
  );

  const clearDialogEnrichmentTimeout = useCallback((dialogId: string) => {
    const timeout = inviteEnrichmentTimeoutRef.current.get(dialogId);
    if (timeout !== undefined) window.clearTimeout(timeout);
    inviteEnrichmentTimeoutRef.current.delete(dialogId);
  }, []);

  const answerManagedCallerDialog = useCallback(
    (phone: SipSoftphone, dialog: SipDialogSnapshot) => {
      const correlation = dialog.sipCorrelationId;
      if (!correlation) return;
      setCappedMap(
        dialogClassificationsRef.current,
        dialog.dialogId,
        "managed_caller",
      );
      clearDialogEnrichmentTimeout(dialog.dialogId);
      addCappedSet(inviteEnrichedRef.current, dialog.dialogId);
      syncDialogs(phone);

      if (autoAnsweringDialogsRef.current.has(dialog.dialogId)) return;
      if (consumedCallerCorrelationsRef.current.has(correlation)) {
        phone.terminateDialog(dialog.dialogId);
        return;
      }

      addCappedSet(autoAnsweringDialogsRef.current, dialog.dialogId);
      addCappedSet(consumedCallerCorrelationsRef.current, correlation);
      void Promise.resolve(phone.answerDialog(dialog.dialogId)).catch(
        (error: unknown) => {
          if (softphoneRef.current !== phone) return;
          phone.terminateDialog(dialog.dialogId);
          updatePublicState({
            lifecycle: "failed",
            message:
              error instanceof Error
                ? error.message
                : "Browser call audio could not start.",
            notice: {
              kind: "media_failure",
              message:
                "Browser call audio failed. Check microphone permission and audio devices.",
              observedAt: Date.now(),
            },
          });
          emitRuntimeVoiceEvent({
            eventName: "browser_voice.auto_answer.failed",
            severity: "WARN",
            phase: "auto_answer",
            outcome: "failed",
            reasonCode: "answer_threw",
            sipCorrelationId: correlation,
          });
        },
      );
    },
    [
      clearDialogEnrichmentTimeout,
      emitRuntimeVoiceEvent,
      syncDialogs,
      updatePublicState,
    ],
  );

  const applyCanonicalClassification = useCallback(
    (
      phone: SipSoftphone,
      dialog: SipDialogSnapshot,
      classification: CanonicalCorrelationClassification,
    ) => {
      if (classification.kind === "terminal") {
        emitRuntimeVoiceEvent({
          eventName: "browser_voice.sip.correlation_mismatch",
          severity: "WARN",
          phase: "correlation",
          outcome: "rejected",
          reasonCode: "managed_call_terminal",
          correlationState: "mismatched",
          sipCorrelationId: dialog.sipCorrelationId ?? undefined,
          callSessionId: classification.callSessionId,
        });
        setCappedMap(
          dialogClassificationsRef.current,
          dialog.dialogId,
          "managed_caller",
        );
        clearDialogEnrichmentTimeout(dialog.dialogId);
        phone.terminateDialog(dialog.dialogId);
        syncDialogs(phone);
        return;
      }
      if (classification.kind === "managed_caller") {
        // A Browser-originated call carries the same public caller
        // correlation used to bind the durable Spark VP call. It is an
        // outgoing RTCSession, not a managed inbound offer. Never apply the
        // inbound auto-answer path to it: JsSIP rejects answer() for outgoing
        // sessions and terminates the call before its destination can ring.
        if (dialog.direction !== "incoming") return;
        emitRuntimeVoiceEvent({
          eventName: "browser_voice.sip.correlation_matched",
          severity: "INFO",
          phase: "correlation",
          outcome: "succeeded",
          correlationState: "matched",
          sipCorrelationId: dialog.sipCorrelationId ?? undefined,
          callSessionId: classification.callSessionId,
        });
        answerManagedCallerDialog(phone, dialog);
        return;
      }
      if (classification.kind === "unresolved_inbound") {
        setCappedMap(
          dialogClassificationsRef.current,
          dialog.dialogId,
          "unresolved_inbound",
        );
        canonicalDialogTargetsRef.current.delete(dialog.dialogId);
        clearDialogEnrichmentTimeout(dialog.dialogId);
        syncDialogs(phone);
        updatePublicState({
          lifecycle: "incoming_unenriched",
          message: "Incoming call authorization is not available.",
        });
        return;
      }
      setCappedMap(
        dialogClassificationsRef.current,
        dialog.dialogId,
        "actionable_inbound",
      );
      clearDialogEnrichmentTimeout(dialog.dialogId);
      addCappedSet(inviteEnrichedRef.current, dialog.dialogId);
      syncDialogs(phone);
      updatePublicState({
        lifecycle: "incoming_unenriched",
        message: "Incoming call details synced.",
      });
    },
    [
      answerManagedCallerDialog,
      clearDialogEnrichmentTimeout,
      emitRuntimeVoiceEvent,
      syncDialogs,
      updatePublicState,
    ],
  );

  const recoverInboundAuthorization = useCallback(
    async (
      phone: SipSoftphone,
      dialog: SipDialogSnapshot,
      current: ActiveBrowserSession,
    ) => {
      const correlation = dialog.sipCorrelationId;
      if (!validSipCorrelation(correlation)) return;
      try {
        const response = await voiceApi.getActiveCalls();
        const dialogClassification =
          dialogClassificationsRef.current.get(dialog.dialogId);
        const existingTarget =
          canonicalDialogTargetsRef.current.get(dialog.dialogId);
        const awaitingAuthorizationRecovery =
          dialogClassification === "unresolved_correlated" ||
          dialogClassification === "unresolved_inbound" ||
          (dialogClassification === "actionable_inbound" &&
            existingTarget?.answerAuthorized !== true);
        if (
          softphoneRef.current !== phone ||
          sessionRef.current !== current ||
          current.authorityRevoked ||
          !awaitingAuthorizationRecovery ||
          !phone
            .getDialogs()
            .some(
              (candidate) =>
                candidate.dialogId === dialog.dialogId &&
                candidate.direction === "incoming" &&
                candidate.sipCorrelationId === correlation &&
                ["ringing", "answering", "connected"].includes(
                  candidate.state,
                ),
            )
        ) {
          return;
        }

        const realtimeClassification =
          canonicalByCorrelationRef.current.get(correlation);
        if (
          realtimeClassification &&
          (realtimeClassification.kind === "terminal" ||
            realtimeClassification.kind === "managed_caller" ||
            canonicalDialogTargetsRef.current.get(dialog.dialogId)
              ?.answerAuthorized === true)
        ) {
          applyCanonicalClassification(phone, dialog, realtimeClassification);
          return;
        }

        const recovered = exactRecoveredInboundAuthorization(
          response.calls,
          current,
          correlation,
        );
        if (!recovered) {
          logger.warn("sparkone.browser_voice.invite_recovery_unmatched", {
            activeCallCount: Math.min(response.calls.length, 10),
          });
          return;
        }

        const classification: CanonicalCorrelationClassification = {
          callSessionId: recovered.call.callSessionId,
          kind: "actionable_inbound",
          role: "device_leg",
        };
        setCappedMap(
          canonicalByCorrelationRef.current,
          correlation,
          classification,
        );
        if (recovered.call.stateSequence !== null) {
          setCappedMap(
            canonicalSequenceByCallRef.current,
            recovered.call.callSessionId,
            recovered.call.stateSequence,
          );
        }
        setCappedMap(
          canonicalCallDialogsRef.current,
          recovered.call.callSessionId,
          dialog.dialogId,
        );
        setCappedMap(canonicalDialogTargetsRef.current, dialog.dialogId, {
          connectorId: current.connectorId,
          callSessionId: recovered.call.callSessionId,
          participantId: recovered.participant.participantId,
          endpointDeviceId: recovered.participant.endpointDeviceId,
          sipCorrelationId: correlation,
          answerAuthorized: true,
          rejectAuthorized:
            recovered.call.availableCommands?.includes("reject") === true,
        });
        logger.info("sparkone.browser_voice.invite_authorization_recovered", {
          recoverySource: "active_calls",
          callState: "ringing",
          participantOutcome: "ringing",
        });
        applyCanonicalClassification(phone, dialog, classification);
      } catch (error) {
        logger.warn("sparkone.browser_voice.invite_recovery_read_failed", {
          failureCategory:
            error instanceof VoiceRequestRejectedError
              ? "request_rejected"
              : "request_failed",
        });
      }
    },
    [applyCanonicalClassification],
  );

  const failClosedCallerCorrelation = useCallback(
    (
      phone: SipSoftphone,
      callSessionIds: string[],
      correlations: Array<string | null | undefined>,
    ) => {
      const terminalCalls = new Set(callSessionIds.filter(Boolean));
      const terminalCorrelations = new Set(
        correlations.filter(
          (value): value is string =>
            typeof value === "string" && validSipCorrelation(value),
        ),
      );
      emitRuntimeVoiceEvent({
        eventName: "browser_voice.sip.correlation_conflict",
        severity: "WARN",
        phase: "correlation",
        outcome: "rejected",
        reasonCode: "invite_correlation_conflict",
        correlationState: "conflict",
        ...(terminalCalls.values().next().value
          ? { callSessionId: terminalCalls.values().next().value as string }
          : {}),
        ...(terminalCorrelations.values().next().value
          ? {
              sipCorrelationId: terminalCorrelations.values().next()
                .value as string,
            }
          : {}),
      });
      for (const callSessionId of terminalCalls) {
        addCappedSet(terminalCallIdsRef.current, callSessionId);
      }
      for (const correlation of terminalCorrelations) {
        const existing = canonicalByCorrelationRef.current.get(correlation);
        const callSessionId =
          existing?.callSessionId ?? terminalCalls.values().next().value;
        if (!callSessionId) continue;
        setCappedMap(canonicalByCorrelationRef.current, correlation, {
          callSessionId,
          kind: "terminal",
          role: existing?.role ?? "caller",
        });
        addCappedSet(consumedCallerCorrelationsRef.current, correlation);
      }
      for (const dialog of phone.getDialogs()) {
        const correlation = dialog.sipCorrelationId;
        if (!correlation || !terminalCorrelations.has(correlation)) continue;
        const terminalClassification =
          canonicalByCorrelationRef.current.get(correlation);
        if (terminalClassification) {
          applyCanonicalClassification(phone, dialog, terminalClassification);
        }
      }
      updatePublicState({
        lifecycle: "failed",
        message: "Browser call identity could not be verified.",
        notice: {
          kind: "correlation_conflict",
          message: "Browser call identity could not be verified.",
          observedAt: Date.now(),
        },
      });
    },
    [applyCanonicalClassification, emitRuntimeVoiceEvent, updatePublicState],
  );

  const submitLocalRegistrationObservation = useCallback(
    (current: ActiveBrowserSession, sessionId: string) => {
      const episode = current.localRegistrationEpisodes.get(sessionId);
      if (!episode || episode.submitted || current.authorityRevoked) return;
      const target =
        sessionId === current.sessionId &&
        current.installationId &&
        current.installationGeneration !== null
          ? {
              installationId: current.installationId,
              generation: current.installationGeneration,
            }
          : current.pendingRenewal?.sessionId === sessionId &&
              current.pendingInstallationTarget
            ? {
                installationId:
                  current.pendingInstallationTarget.installationId,
                generation: current.pendingInstallationTarget.generation,
              }
            : null;
      if (!target) return;

      episode.submitted = true;
      const localToSendMs = boundedMonotonicLatency(
        episode.observedAtMonotonic,
      );
      logger.info(
        "sparkone.browser_voice.local_registration_observation_sent",
        { generation: target.generation, localToSendMs },
      );
      emitRuntimeVoiceEvent({
        eventName: "browser_voice.registration.observation_sent",
        severity: "INFO",
        phase: "registration",
        outcome: "started",
        registrationState: "registered",
        elapsedMs: localToSendMs,
      });
      const abortController = new AbortController();
      episode.abortController = abortController;
      let timedOut = false;
      let timeout: number | null = null;
      const request = voiceApi.submitBrowserRegistrationObservation(
        {
          ...target,
          sessionId,
          clientObservationId: episode.clientObservationId,
          observedAt: episode.observedAt,
        },
        abortController.signal,
      );
      void Promise.race([
        request,
        new Promise<never>((_resolve, reject) => {
          timeout = window.setTimeout(() => {
            timedOut = true;
            abortController.abort();
            reject(new Error("Browser registration observation timed out."));
          }, LOCAL_REGISTRATION_OBSERVATION_TIMEOUT_MS);
        }),
      ])
        .then((result) => {
          if (!episode.active || abortController.signal.aborted) return;
          const localToOutcomeMs = boundedMonotonicLatency(
            episode.observedAtMonotonic,
          );
          logger.info(
            "sparkone.browser_voice.local_registration_observation_accepted",
            {
              generation: target.generation,
              outcome: result.outcome,
              localToOutcomeMs,
            },
          );
          emitRuntimeVoiceEvent({
            eventName: "browser_voice.registration.observation_accepted",
            severity: "INFO",
            phase: "registration",
            outcome: "succeeded",
            registrationState: "registered",
            elapsedMs: localToOutcomeMs,
          });
        })
        .catch((error: unknown) => {
          if (!episode.active || (abortController.signal.aborted && !timedOut)) {
            return;
          }
          const failureCategory = timedOut
            ? "request_timed_out"
            : error instanceof VoiceRequestRejectedError
              ? "request_rejected"
              : "request_failed";
          logger.warn(
            "sparkone.browser_voice.local_registration_observation_failed",
            { generation: target.generation, failureCategory },
          );
          emitRuntimeVoiceEvent({
            eventName: "browser_voice.registration.observation_failed",
            severity: "WARN",
            phase: "registration",
            outcome: timedOut
              ? "timed_out"
              : error instanceof VoiceRequestRejectedError
                ? "rejected"
                : "failed",
            registrationState: "registered",
            elapsedMs: boundedMonotonicLatency(episode.observedAtMonotonic),
          });
        })
        .finally(() => {
          if (timeout !== null) {
            window.clearTimeout(timeout);
            timeout = null;
          }
          if (episode.abortController === abortController) {
            episode.abortController = null;
          }
        });
    },
    [emitRuntimeVoiceEvent],
  );

  const endLocalRegistrationEpisode = useCallback(
    (current: ActiveBrowserSession, sessionId: string) => {
      const episode = current.localRegistrationEpisodes.get(sessionId);
      if (!episode) return;
      episode.active = false;
      episode.abortController?.abort();
      episode.abortController = null;
      current.localRegistrationEpisodes.delete(sessionId);
    },
    [],
  );

  const recordLocalRegistration = useCallback(
    (current: ActiveBrowserSession, sessionId: string) => {
      let episode = current.localRegistrationEpisodes.get(sessionId);
      if (!episode) {
        episode = {
          clientObservationId: globalThis.crypto.randomUUID(),
          observedAt: new Date().toISOString(),
          observedAtMonotonic: monotonicNow(),
          submitted: false,
          active: true,
          abortController: null,
        };
        while (
          current.localRegistrationEpisodes.size >= MAX_RUNTIME_RETENTION
        ) {
          const oldestSessionId = current.localRegistrationEpisodes
            .keys()
            .next().value as string | undefined;
          if (oldestSessionId === undefined) break;
          const oldestEpisode =
            current.localRegistrationEpisodes.get(oldestSessionId);
          if (oldestEpisode) oldestEpisode.active = false;
          oldestEpisode?.abortController?.abort();
          current.localRegistrationEpisodes.delete(oldestSessionId);
        }
        current.localRegistrationEpisodes.set(sessionId, episode);
        logger.info("sparkone.browser_voice.local_registration_observed", {
          registrationState: "registered",
          episodeKind:
            sessionId === current.sessionId ? "current" : "pending_cutover",
        });
      }
      if (sessionId === current.sessionId) {
        current.localRegistrationState = "local_registered";
        const providerReady =
          current.providerRegistrationState === "registered";
        updatePublicState({
          lifecycle: providerReady ? "ready" : "registering",
          localRegistrationState: "local_registered",
          providerRegistrationState: current.providerRegistrationState,
          message: providerReady
            ? "Browser phone ready."
            : "Browser phone connected. Confirming availability…",
        });
        if (providerReady) {
          emitBrowserReadinessTiming(
            "browser_voice.registration.public_ready",
            { registrationState: "registered" },
          );
        }
        cancelBrowserRuntimeTimer(localConfirmationWarningRef);
        if (current.providerRegistrationState !== "registered") {
          replaceBrowserRuntimeTimer(
            localConfirmationWarningRef,
            () => {
              if (
                sessionRef.current === current &&
                !current.authorityRevoked &&
                current.localRegistrationState === "local_registered" &&
                current.providerRegistrationState !== "registered"
              ) {
                updatePublicState({
                  lifecycle: "registering",
                  message:
                    "Browser phone connected. Availability confirmation is taking longer than expected…",
                });
              }
            },
            10_000,
          );
        }
      }
      submitLocalRegistrationObservation(current, sessionId);
    },
    [
      emitBrowserReadinessTiming,
      submitLocalRegistrationObservation,
      updatePublicState,
    ],
  );

  const handleSipDiagnostic = useCallback(
    (event: SipSoftphoneDiagnosticEvent) => {
      const shared =
        event.kind === "offer_received" ||
        event.kind === "correlation_present" ||
        event.kind === "correlation_missing" ||
        event.kind === "correlation_malformed"
          ? {
              ...(typeof event.sipCorrelationId === "string"
                ? { sipCorrelationId: event.sipCorrelationId }
                : {}),
              dialogDirection: event.dialogDirection,
            }
          : "sipCorrelationId" in event &&
              typeof event.sipCorrelationId === "string"
            ? { sipCorrelationId: event.sipCorrelationId }
            : {};
      switch (event.kind) {
        case "offer_received":
          emitRuntimeVoiceEvent({
            eventName: "browser_voice.sip.offer_received",
            severity: "INFO",
            phase: "offer",
            outcome: "started",
            ...shared,
          });
          break;
        case "offer_rejected_authority":
          emitRuntimeVoiceEvent({
            eventName: "browser_voice.sip.offer_rejected_authority",
            severity: "WARN",
            phase: "offer",
            outcome: "rejected",
            reasonCode: "offer_authority_revoked",
            offerAuthority: "revoked",
          });
          break;
        case "correlation_present":
          emitRuntimeVoiceEvent({
            eventName: "browser_voice.sip.correlation_present",
            severity: "INFO",
            phase: "correlation",
            correlationState: "present",
            ...shared,
          });
          break;
        case "correlation_missing":
          emitRuntimeVoiceEvent({
            eventName: "browser_voice.sip.correlation_missing",
            severity: "WARN",
            phase: "correlation",
            outcome: "rejected",
            reasonCode: "invite_correlation_missing",
            correlationState: "missing",
            ...shared,
          });
          break;
        case "correlation_malformed":
          emitRuntimeVoiceEvent({
            eventName: "browser_voice.sip.correlation_mismatch",
            severity: "WARN",
            phase: "correlation",
            outcome: "rejected",
            reasonCode: "invite_correlation_mismatch",
            correlationState: "mismatched",
            ...shared,
          });
          break;
        case "auto_answer_started":
          emitRuntimeVoiceEvent({
            eventName: "browser_voice.auto_answer.started",
            severity: "INFO",
            phase: "auto_answer",
            outcome: "started",
            ...shared,
          });
          break;
        case "answer_invoked":
          emitRuntimeVoiceEvent({
            eventName: "browser_voice.auto_answer.answer_invoked",
            severity: "INFO",
            phase: "auto_answer",
            outcome: "succeeded",
            ...shared,
          });
          break;
        case "auto_answer_failed":
          emitRuntimeVoiceEvent({
            eventName: "browser_voice.auto_answer.failed",
            severity: "WARN",
            phase: "auto_answer",
            outcome: "failed",
            reasonCode: "microphone_denied",
            mediaState: "blocked",
            ...shared,
          });
          break;
        case "remote_playback_blocked":
          emitRuntimeVoiceEvent({
            eventName: "browser_voice.remote_playback.blocked",
            severity: "WARN",
            phase: "remote_playback",
            outcome: "failed",
            reasonCode: "remote_playback_blocked",
            mediaState: "blocked",
            ...shared,
          });
          break;
        case "session_confirmed":
          emitRuntimeVoiceEvent({
            eventName: "browser_voice.sip.session_confirmed",
            severity: "INFO",
            phase: "session",
            outcome: "succeeded",
            ...shared,
          });
          break;
        case "session_failed":
          emitRuntimeVoiceEvent({
            eventName: "browser_voice.sip.session_failed",
            severity: "WARN",
            phase: "session",
            outcome: "failed",
            reasonCode: "session_failed_before_confirmed",
            ...shared,
          });
          break;
        case "session_ended":
          emitRuntimeVoiceEvent({
            eventName: "browser_voice.sip.session_ended",
            severity: "INFO",
            phase: "session",
            outcome: "ended",
            ...shared,
          });
          break;
        case "transport_message_received":
          emitRuntimeVoiceEvent({
            eventName: "browser_voice.sip.transport_message_received",
            severity: "DEBUG",
            phase: "offer",
            outcome: "started",
            offerAuthority:
              event.authority === "revoked" ? "revoked" : "accepting",
          });
          break;
        case "transport_invite_observed":
          emitRuntimeVoiceEvent({
            eventName: "browser_voice.sip.transport_invite_observed",
            severity: "INFO",
            phase: "offer",
            outcome: "started",
            offerAuthority:
              event.authority === "revoked" ? "revoked" : "accepting",
          });
          break;
        case "invite_callback_invoked":
          emitRuntimeVoiceEvent({
            eventName: "browser_voice.sip.invite_callback_invoked",
            severity: "INFO",
            phase: "offer",
            outcome: "succeeded",
            offerAuthority:
              event.authority === "revoked" ? "revoked" : "accepting",
          });
          break;
        case "invite_callback_timeout":
          emitRuntimeVoiceEvent({
            eventName: "browser_voice.sip.invite_callback_timeout",
            severity: "WARN",
            phase: "offer",
            outcome: "timed_out",
            offerAuthority:
              event.authority === "revoked" ? "revoked" : "accepting",
            elapsedMs: boundedElapsedMs(event.elapsedMs),
          });
          break;
        case "invite_rejected_before_callback":
          emitRuntimeVoiceEvent({
            eventName: "browser_voice.sip.invite_rejected_before_callback",
            severity: "WARN",
            phase: "offer",
            outcome: "rejected",
            reasonCode: "invite_rejected_before_callback",
            offerAuthority:
              event.authority === "revoked" ? "revoked" : "accepting",
            sipResponseStatus: telemetrySipResponseStatus(event.responseStatus),
          });
          break;
        case "registration_ready":
          emitBrowserReadinessTiming(
            "browser_voice.registration.local_registered",
            { registrationState: "registered" },
          );
          emitRuntimeVoiceEvent({
            eventName: "browser_voice.registration.ready",
            severity: "INFO",
            phase: "registration",
            outcome: "succeeded",
            registrationState: "registered",
          });
          break;
        case "registration_disconnected":
          emitRuntimeVoiceEvent({
            eventName: "browser_voice.registration.disconnected",
            severity: "WARN",
            phase: "registration",
            outcome: "failed",
            reasonCode: "wss_disconnected",
            registrationState: "disconnected",
          });
          break;
        default: {
          const diagnostic = event as { kind?: unknown };
          logger.warn("sparkone.browser_voice.diagnostic_unhandled", {
            diagnosticKind:
              typeof diagnostic.kind === "string" ? diagnostic.kind : "unknown",
          });
        }
      }
    },
    [emitBrowserReadinessTiming, emitRuntimeVoiceEvent],
  );

  const handleSipStatus = useCallback(
    (phone: SipSoftphone, event: SipSoftphoneEvent) => {
      if (softphoneRef.current !== phone) return;
      if (event.mediaState) {
        setSipStatus(event);
        const delayed = event.mediaState === "delayed";
        const blocked = event.mediaState === "blocked";
        const logContext = {
          mediaState: event.mediaState,
          mediaStage: event.mediaStage ?? "unknown",
          ...(event.mediaElapsedMs === undefined
            ? {}
            : { latencyMs: event.mediaElapsedMs }),
        };
        if (delayed || blocked) {
          logger.warn("sparkone.browser_voice.media_readiness", logContext);
        } else {
          logger.info("sparkone.browser_voice.media_readiness", logContext);
        }
        setPublicState((current) => ({
          ...current,
          mediaState: event.mediaState!,
          message: event.message ?? null,
          notice:
            delayed || blocked
              ? {
                  kind: "media_failure",
                  message:
                    event.message ??
                    "Browser audio is not ready. Select Enable audio to retry.",
                  observedAt: Date.now(),
                }
              : current.notice?.kind === "media_failure"
                ? null
                : current.notice,
        }));
        // A stored speaker preference is deliberately deferred until a current
        // remote audio route exposes setSinkId. Media readiness is the first
        // lifecycle point at which that capability is authoritative.
        if (event.mediaState === "ready") {
          void restoreAudioDevicePreferencesRef.current(phone);
        }
        return;
      }
      if (event.status === "connected") {
        logger.info("sparkone.browser_voice.media_readiness", {
          mediaState: "connecting",
          mediaStage: "signaling-confirmed",
        });
      }
      if (event.status === "incoming" && event.dialog) {
        if (!inviteObservedAtRef.current.has(event.dialog.dialogId)) {
          const dialogId = event.dialog.dialogId;
          const correlationPresent = validSipCorrelation(
            event.dialog.sipCorrelationId,
          );
          setCappedMap(inviteObservedAtRef.current, dialogId, Date.now());
          if (validSipCorrelation(event.dialog.sipCorrelationId)) {
            setCappedMap(
              dialogClassificationsRef.current,
              dialogId,
              "unresolved_correlated",
            );
            const canonical = canonicalByCorrelationRef.current.get(
              event.dialog.sipCorrelationId,
            );
            if (canonical) {
              applyCanonicalClassification(phone, event.dialog, canonical);
              const current = sessionRef.current;
              if (
                current &&
                !current.authorityRevoked &&
                (canonical.kind === "actionable_inbound" ||
                  canonical.kind === "unresolved_inbound") &&
                canonicalDialogTargetsRef.current.get(dialogId)
                  ?.answerAuthorized !== true
              ) {
                void recoverInboundAuthorization(phone, event.dialog, current);
              }
            } else {
              const current = sessionRef.current;
              if (current && !current.authorityRevoked) {
                void recoverInboundAuthorization(phone, event.dialog, current);
              }
              const enrichmentTimeout = window.setTimeout(() => {
                inviteEnrichmentTimeoutRef.current.delete(dialogId);
                const stillUnresolved =
                  softphoneRef.current === phone &&
                  dialogClassificationsRef.current.get(dialogId) ===
                    "unresolved_correlated" &&
                  phone
                    .getDialogs()
                    .some(
                      (dialog) =>
                        dialog.dialogId === dialogId &&
                        ["ringing", "answering", "connected"].includes(
                          dialog.state,
                        ),
                    );
                if (!stillUnresolved) return;
                // Surface a timeout as a generic inbound dialog, never as
                // an authorized/actionable dialog. This preserves a safe
                // Browser-local decline while Answer remains fail-closed.
                setCappedMap(
                  dialogClassificationsRef.current,
                  dialogId,
                  "unresolved_inbound",
                );
                syncDialogs(phone);
                updatePublicState({
                  lifecycle: "incoming_unenriched",
                  notice: {
                    kind: "correlation_timeout",
                    message:
                      "Call authorization is still syncing. Answer remains unavailable; you may decline this Browser only.",
                    observedAt: Date.now(),
                  },
                });
                logger.warn(
                  "sparkone.browser_voice.invite_enrichment_timeout",
                  {
                    elapsedMs: INVITE_ENRICHMENT_TIMEOUT_MS,
                    correlationPresent,
                  },
                );
              }, INVITE_ENRICHMENT_TIMEOUT_MS);
              if (
                !inviteEnrichmentTimeoutRef.current.has(dialogId) &&
                inviteEnrichmentTimeoutRef.current.size >= MAX_RUNTIME_RETENTION
              ) {
                const oldest = inviteEnrichmentTimeoutRef.current.keys().next()
                  .value as string | undefined;
                if (oldest !== undefined) {
                  const oldestTimeout =
                    inviteEnrichmentTimeoutRef.current.get(oldest);
                  if (oldestTimeout !== undefined) {
                    window.clearTimeout(oldestTimeout);
                  }
                  inviteEnrichmentTimeoutRef.current.delete(oldest);
                }
              }
              inviteEnrichmentTimeoutRef.current.set(
                dialogId,
                enrichmentTimeout,
              );
            }
          } else {
            // An uncorrelated offer is safe to display and decline locally,
            // but cannot be answered because it has no exact provider proof.
            setCappedMap(
              dialogClassificationsRef.current,
              dialogId,
              "unresolved_inbound",
            );
            updatePublicState({
              lifecycle: "incoming_unenriched",
            });
            emitRuntimeVoiceEvent({
              eventName: "browser_voice.sip.correlation_missing",
              severity: "WARN",
              phase: "correlation",
              outcome: "rejected",
              reasonCode: "invite_correlation_missing",
              correlationState: "missing",
              dialogDirection: "incoming",
            });
            logger.warn("sparkone.browser_voice.invite_correlation_missing", {
              direction: "incoming",
            });
          }
        }
      }
      if (event.failureCategory) {
        updatePublicState({
          notice: {
            kind:
              event.failureCategory === "media"
                ? "media_failure"
                : "call_failed",
            message:
              event.failureCategory === "media"
                ? "Browser call audio failed. Check microphone permission and audio devices."
                : "The Browser call could not continue.",
            observedAt: Date.now(),
          },
        });
        logger.warn("sparkone.browser_voice.failure_observed", {
          failureCategory: event.failureCategory,
          dialogScoped: Boolean(event.dialog),
          lifecycleStatus: event.status,
        });
      }
      setSipStatus(event);
      syncDialogs(phone);
      const nextLifecycle = lifecycleForSip(event);
      const installationManagedRegistration =
        USE_AUTHORITATIVE_BROWSER_INSTALLATIONS &&
        (event.registrationSessionId
          ? authoritativeRegistrationSessionIdsRef.current.has(
              event.registrationSessionId,
            )
          : authoritativeRegistrationSessionIdsRef.current.size > 0);
      const hiddenDialog =
        event.dialog &&
        dialogClassificationsRef.current.get(event.dialog.dialogId) ===
          "managed_caller";
      const currentRegistration = sessionRef.current;
      const exactCurrentRegistrationEvent = Boolean(
        currentRegistration &&
        event.registrationSessionId === currentRegistration.sessionId,
      );
      const exactPendingRegistrationEvent = Boolean(
        currentRegistration?.pendingRenewal &&
        event.registrationSessionId ===
          currentRegistration.pendingRenewal.sessionId,
      );
      const registrationEpisodeEnded =
        event.status === "registering" ||
        (event.status === "failed" &&
          (event.failureCategory === "registration" ||
            event.failureCategory === "signaling"));
      if (
        currentRegistration &&
        event.registrationSessionId &&
        registrationEpisodeEnded &&
        (exactCurrentRegistrationEvent || exactPendingRegistrationEvent)
      ) {
        endLocalRegistrationEpisode(
          currentRegistration,
          event.registrationSessionId,
        );
      }
      if (
        event.status === "registering" &&
        exactCurrentRegistrationEvent &&
        currentRegistration
      ) {
        currentRegistration.localRegistrationState = "connecting";
        updatePublicState({
          localRegistrationState: "connecting",
          providerRegistrationState:
            currentRegistration.providerRegistrationState,
          message: "Connecting Browser phone…",
        });
      }
      if (
        event.status === "registered" &&
        event.registrationSessionId &&
        currentRegistration
      ) {
        const exactKnownEpisode =
          event.registrationSessionId === currentRegistration.sessionId ||
          event.registrationSessionId ===
            currentRegistration.pendingRenewal?.sessionId;
        if (exactKnownEpisode) {
          recordLocalRegistration(
            currentRegistration,
            event.registrationSessionId,
          );
        }
      }
      const exactCurrentRegistrationFailure =
        event.status === "failed" &&
        exactCurrentRegistrationEvent &&
        currentRegistration !== null &&
        (event.failureCategory === "registration" ||
          event.failureCategory === "signaling");
      if (exactCurrentRegistrationFailure) {
        currentRegistration.localRegistrationState =
          event.failureCategory === "signaling" ? "disconnected" : "failed";
        cancelBrowserRuntimeTimer(localConfirmationWarningRef);
        updatePublicState({
          lifecycle: "degraded",
          localRegistrationState: currentRegistration.localRegistrationState,
          providerRegistrationState:
            currentRegistration.providerRegistrationState,
          message: "Browser connection lost. Reconnecting…",
        });
      }
      if (
        !exactCurrentRegistrationFailure &&
        nextLifecycle &&
        !(event.status === "incoming" && hiddenDialog) &&
        !(event.status === "registered" && installationManagedRegistration)
      ) {
        updatePublicState({
          lifecycle: nextLifecycle,
          message: event.message ?? null,
        });
      }
      if (
        event.status === "registered" &&
        !installationManagedRegistration &&
        !sessionRef.current?.authorityRevoked
      ) {
        const current = sessionRef.current;
        const pending = current?.pendingRenewal;
        if (
          current &&
          pending &&
          event.registrationSessionId === pending.sessionId
        ) {
          if (current.pendingRotationKind === "independent_replacement") {
            void confirmIndependentReplacementRef.current(current);
          } else {
            void finalizeRenewalRef.current(current);
          }
        } else {
          confirmRegistrationRef.current();
        }
      }

      if (
        event.status === "failed" &&
        !installationManagedRegistration &&
        event.registrationSessionId &&
        sessionRef.current?.pendingRenewal?.sessionId ===
          event.registrationSessionId
      ) {
        const current = sessionRef.current;
        const failedSuccessor = current.pendingRenewal;
        const failedSuccessorConnectorId =
          current.pendingReplacementIdentity?.connectorId ??
          current.connectorId;
        const wasReplacement = current.replacementPending;
        current.pendingRenewal = null;
        current.pendingInstallationTarget = null;
        current.pendingRotationKind = null;
        current.renewalStartedAt = null;
        current.replacementPending = false;
        current.pendingReplacementIdentity = null;
        if (failedSuccessor) {
          void revokeRegistration(
            {
              connectorId: failedSuccessorConnectorId,
              sessionId: failedSuccessor.sessionId,
            },
            current.revocationActivationProof ?? activeActivationProof(current),
          );
        }
        renewalAttemptRef.current += 1;
        const retryDelay = boundedRetryDelay(renewalAttemptRef.current);
        logger.warn("sparkone.browser_voice.renewal_retry_scheduled", {
          attempt: renewalAttemptRef.current,
          retryDelayMs: retryDelay,
          failureCategory: "successor_registration_failed",
        });
        if (wasReplacement) {
          replaceBrowserRuntimeTimer(
            bootstrapRetryRef,
            () => {
              void replacePersistentRegistrationRef.current(
                current,
                "replacement_registration_failed",
              );
            },
            retryDelay,
          );
        } else if (Date.now() + retryDelay < current.expiresAtMs) {
          scheduleRef.current(
            {
              ...current.credentials,
              expiresAt: new Date(current.expiresAtMs).toISOString(),
            },
            retryDelay,
          );
        } else {
          void replacePersistentRegistrationRef.current(
            current,
            "successor_registration_failed",
          );
        }
      }

      if (
        event.dialog &&
        ["rejected", "ended", "failed"].includes(event.dialog.state)
      ) {
        if (event.dialog.state === "rejected") {
          updatePublicState({
            notice: {
              kind: "rejected",
              message: "Browser call rejected.",
              observedAt: Date.now(),
            },
          });
        }
        clearDialogEnrichmentTimeout(event.dialog.dialogId);
        inviteObservedAtRef.current.delete(event.dialog.dialogId);
        inviteUiObservedRef.current.delete(event.dialog.dialogId);
        inviteEnrichedRef.current.delete(event.dialog.dialogId);
        dialogClassificationsRef.current.delete(event.dialog.dialogId);
        autoAnsweringDialogsRef.current.delete(event.dialog.dialogId);
      }

      if (event.dialog && ["ended", "failed"].includes(event.dialog.state)) {
        const current = sessionRef.current;
        const callSessionId = current?.callSessionId;
        if (callSessionId) {
          setTerminalEvent({
            callSessionId,
            status: event.dialog.state as "ended" | "failed",
            ...(event.message ? { message: event.message } : {}),
            occurredAt: new Date().toISOString(),
          });
          disarmManagedCallerRef.current(callSessionId);
        }
        if (
          current?.authorityRevoked &&
          !phone
            .getDialogs()
            .some(
              (dialog) =>
                dialog.state === "answering" || dialog.state === "connected",
            )
        ) {
          void release(
            {
              connectorId: current.connectorId,
              sessionId: current.sessionId,
            },
            { revokeRemoteRegistration: false },
          );
        } else if (
          current?.replacementPending &&
          !phone
            .getDialogs()
            .some(
              (dialog) =>
                dialog.state === "answering" || dialog.state === "connected",
            )
        ) {
          void replacePersistentRegistrationRef.current(
            current,
            "deferred_until_call_ended",
          );
        }
      }

      // Compatibility for existing softphone adapters that report a terminal
      // call without dialog metadata. New JsSIP paths always include dialog.
      if (!event.dialog && ["ended", "failed"].includes(event.status)) {
        const current = sessionRef.current;
        if (current?.callSessionId) {
          setTerminalEvent({
            callSessionId: current.callSessionId,
            status: event.status as "ended" | "failed",
            ...(event.message ? { message: event.message } : {}),
            occurredAt: new Date().toISOString(),
          });
          if (!current.persistent || current.authorityRevoked) {
            void release(undefined, {
              revokeRemoteRegistration: !current.authorityRevoked,
            });
          }
        }
      }

      // A terminal registrar rejection cannot be repaired with the existing
      // credential. A bounded in-place recovery likewise graduates to an
      // independently provisioned successor. Preserve live media in both cases.
      if (
        event.status === "failed" &&
        !event.dialog &&
        (event.registrationFailure === "terminal" ||
          event.registrationRecoveryExhausted)
      ) {
        const current = sessionRef.current;
        const hasConnected = phone
          .getDialogs()
          .some(
            (dialog) =>
              dialog.state === "connected" || dialog.state === "answering",
          );
        if (current?.persistent && !hasConnected) {
          if (event.registrationRecoveryExhausted) {
            void replacePersistentRegistrationRef.current(
              current,
              "registration_recovery_exhausted",
            );
          } else {
            void release().then(() => {
              if (!mountedRef.current || !authEligibleRef.current) return;
              bootstrapRequestedRef.current = true;
              void bootstrapRef.current();
            });
          }
          return;
        }
      }

      // A persistent registration survives dialog terminal events. A global
      // registration failure with connected media is degraded, not a hangup.
      if (event.status === "failed" && !event.dialog) {
        const hasConnected = phone
          .getDialogs()
          .some(
            (dialog) =>
              dialog.state === "connected" || dialog.state === "answering",
          );
        if (sessionRef.current?.persistent || hasConnected) {
          updatePublicState({
            lifecycle: "degraded",
            message: exactCurrentRegistrationFailure
              ? "Browser connection lost. Reconnecting…"
              : (event.message ?? null),
            notice: {
              kind: "registration_degraded",
              message:
                "Browser registration is degraded. Connected media is being preserved.",
              observedAt: Date.now(),
            },
          });
        } else {
          void release();
        }
      }
    },
    [
      applyCanonicalClassification,
      clearDialogEnrichmentTimeout,
      emitRuntimeVoiceEvent,
      endLocalRegistrationEpisode,
      release,
      revokeRegistration,
      recordLocalRegistration,
      recoverInboundAuthorization,
      syncDialogs,
      updatePublicState,
    ],
  );

  useEffect(() => {
    if (!ownsBrowserPhone) return;
    for (const dialog of publicState.dialogs) {
      const startedAt = inviteObservedAtRef.current.get(dialog.dialogId);
      if (
        startedAt === undefined ||
        inviteUiObservedRef.current.has(dialog.dialogId)
      ) {
        continue;
      }
      addCappedSet(inviteUiObservedRef.current, dialog.dialogId);
      logger.info("sparkone.browser_voice.invite_ui_ready", {
        latencyMs: boundedLatency(startedAt),
        correlationPresent: Boolean(dialog.sipCorrelationId),
        uiMode: "generic_actionable",
      });
    }
  }, [ownsBrowserPhone, publicState.dialogs]);

  const replacePersistentRegistrationOperation = useCallback(
    async (current: ActiveBrowserSession, reason: string) => {
      if (
        sessionRef.current !== current ||
        current.authorityRevoked ||
        !current.persistent ||
        current.replacementPending
      )
        return;
      current.replacementPending = true;
      current.pendingRenewal = null;
      current.pendingInstallationTarget = null;
      current.renewalStartedAt = null;
      cancelBrowserRuntimeTimer(renewalRef);
      const phone = softphoneRef.current;
      const preservesDialog = Boolean(
        phone
          ?.getDialogs()
          .some(
            (dialog) =>
              dialog.state === "connected" || dialog.state === "answering",
          ),
      );
      logger.warn("sparkone.browser_voice.registration_rebootstrap_requested", {
        reason,
        connectedDialogPreserved: preservesDialog,
      });
      updatePublicState({
        lifecycle: "degraded",
        message: preservesDialog
          ? "Browser registration is recovering. The connected call is preserved."
          : "Browser registration is recovering.",
      });
      let preparedIdentity: BrowserRuntimeSessionIdentity | null = null;
      try {
        const prepared = await voiceApi.preparePersistentBrowserSession({
          browserDeviceInstanceId: getBrowserDeviceInstanceId(),
          activationProof: activeActivationProof(current),
        });
        if (
          sessionRef.current !== current ||
          current.authorityRevoked ||
          softphoneRef.current !== phone ||
          !phone
        ) {
          // A stale persistent consumer does not own compensation authority.
          // The surviving activation may share this exact prepared credential.
          return;
        }
        preparedIdentity = {
          connectorId: prepared.connectorId,
          sessionId: prepared.browserSession.sessionId,
        };
        current.pendingRenewal = prepared.browserSession;
        setTransitioning(true);
        if (
          prepared.renewalPredecessorSessionId &&
          prepared.renewalPredecessorSessionId !== current.sessionId
        ) {
          throw new Error("Browser renewal predecessor changed unexpectedly.");
        }
        current.pendingRotationKind = prepared.renewalPredecessorSessionId
          ? "provider_successor"
          : "independent_replacement";
        current.pendingReplacementIdentity = {
          connectorId: prepared.connectorId,
          clientUserRef: prepared.clientUserRef,
          extensionId: prepared.extensionId,
          endpointDeviceId: prepared.endpointDeviceId,
        };
        current.renewalStartedAt = Date.now();
        phone.renewCredentials(prepared.browserSession);
      } catch (error) {
        current.replacementPending = false;
        current.pendingReplacementIdentity = null;
        current.pendingRenewal = null;
        current.pendingInstallationTarget = null;
        current.pendingRotationKind = null;
        current.renewalStartedAt = null;
        if (preparedIdentity) void revokeRegistration(preparedIdentity);
        const outcome = renewalFailureOutcome(error);
        if (outcome === "authority_revoked") {
          void suspendRegistrationAuthority("renewal_denied");
          return;
        }
        bootstrapAttemptRef.current += 1;
        const retryDelay = boundedRetryDelay(bootstrapAttemptRef.current);
        logger.warn(
          "sparkone.browser_voice.registration_rebootstrap_retry_scheduled",
          {
            attempt: bootstrapAttemptRef.current,
            retryDelayMs: retryDelay,
            failureCategory: outcome,
          },
        );
        replaceBrowserRuntimeTimer(
          bootstrapRetryRef,
          () => {
            void replacePersistentRegistrationRef.current(current, reason);
          },
          retryDelay,
        );
      }
    },
    [revokeRegistration, suspendRegistrationAuthority, updatePublicState],
  );
  const replacePersistentRegistration = useCallback(
    (current: ActiveBrowserSession, reason: string): Promise<void> => {
      const existing = replacementTransitionPromiseRef.current;
      if (existing) return existing;
      const start = () =>
        runSerializedTransition(() =>
          replacePersistentRegistrationOperation(current, reason),
        );
      // Renewal failure handling can request a replacement while its own
      // serialized decision is still unwinding. Queue the replacement behind
      // that exact promise instead of recursively waiting on it.
      const activeTransition = transitionPromiseRef.current;
      const replacement = activeTransition
        ? activeTransition.then(start, start)
        : start();
      replacementTransitionPromiseRef.current = replacement;
      void replacement.then(
        () => {
          if (replacementTransitionPromiseRef.current === replacement) {
            replacementTransitionPromiseRef.current = null;
          }
        },
        (error: unknown) => {
          if (replacementTransitionPromiseRef.current === replacement) {
            replacementTransitionPromiseRef.current = null;
          }
          logger.error(
            "sparkone.browser_voice.replacement_transition_unhandled",
            {
              failureCategory:
                error instanceof VoiceRequestRejectedError
                  ? "request_rejected"
                  : "unexpected_failure",
            },
          );
        },
      );
      return replacement;
    },
    [replacePersistentRegistrationOperation, runSerializedTransition],
  );
  useEffect(() => {
    replacePersistentRegistrationRef.current = replacePersistentRegistration;
  }, [replacePersistentRegistration]);

  const finalizeRenewal = useCallback(
    (current: ActiveBrowserSession): Promise<void> => {
      const pending = current.pendingRenewal;
      const phone = softphoneRef.current;
      if (
        !pending ||
        !phone ||
        sessionRef.current !== current ||
        current.authorityRevoked
      )
        return Promise.resolve();
      const predecessorSessionId = current.sessionId;
      const activeAttempt = renewalFinalizationRef.current;
      if (
        activeAttempt?.current === current &&
        activeAttempt.pending === pending &&
        activeAttempt.predecessorSessionId === predecessorSessionId
      ) {
        return activeAttempt.promise;
      }
      const isCurrentAttempt = () =>
        sessionRef.current === current &&
        current.pendingRenewal === pending &&
        current.sessionId === predecessorSessionId &&
        softphoneRef.current === phone &&
        !current.authorityRevoked;
      const completeCutover = (successor: VoiceBrowserSession) => {
        if (!isCurrentAttempt()) return false;
        phone.confirmCredentialCutover(successor.sessionId);
        const startedAt = current.renewalStartedAt;
        current.sessionId = successor.sessionId;
        current.expiresAtMs = sessionExpiryMs(successor);
        current.credentials = successor;
        if (current.pendingReplacementIdentity) {
          current.connectorId = current.pendingReplacementIdentity.connectorId;
          current.clientUserRef =
            current.pendingReplacementIdentity.clientUserRef;
          current.extensionId = current.pendingReplacementIdentity.extensionId;
          current.endpointDeviceId =
            current.pendingReplacementIdentity.endpointDeviceId;
        }
        current.pendingReplacementIdentity = null;
        current.pendingRenewal = null;
        current.pendingInstallationTarget = null;
        current.pendingRotationKind = null;
        current.renewalStartedAt = null;
        current.replacementPending = false;
        setTransitioning(false);
        renewalAttemptRef.current = 0;
        logger.info("sparkone.browser_voice.renewal_successor_registered", {
          latencyMs: startedAt === null ? null : boundedLatency(startedAt),
        });
        scheduleRef.current(successor);
        return true;
      };
      const promise = (async () => {
        try {
          // Spark VP finalizes provider cutover idempotently only after it has
          // observed the successor registration. Retain the exact predecessor
          // identity until this attempt either converges or is fenced as stale.
          const finalized = await voiceApi.finalizeBrowserSessionRenewal(
            predecessorSessionId,
            activeActivationProof(current),
          );
          if (!isCurrentAttempt()) return;
          if (
            finalized.sessionId !== pending.sessionId ||
            finalized.status !== "registered"
          )
            throw new Error("Browser renewal cutover is not registered yet.");
          completeCutover(pending);
        } catch (error) {
          if (!isCurrentAttempt()) return;
          const outcome = renewalFailureOutcome(error);
          let predecessorCutoverObserved = false;
          if (
            outcome === "authority_revoked" ||
            outcome === "replace_session" ||
            outcome === "reconcile_conflict"
          ) {
            try {
              const predecessor =
                await voiceApi.getBrowserSessionStatus(predecessorSessionId);
              predecessorCutoverObserved = [
                "revoked",
                "revocation_pending",
                "expired",
              ].includes(predecessor.status);
            } catch (predecessorError) {
              predecessorCutoverObserved =
                renewalFailureOutcome(predecessorError) === "replace_session" ||
                renewalFailureOutcome(predecessorError) === "authority_revoked";
            }
            if (!isCurrentAttempt()) return;
            if (predecessorCutoverObserved) {
              try {
                const successor = await voiceApi.getBrowserSessionStatus(
                  pending.sessionId,
                );
                if (isCurrentAttempt() && successor.status === "registered") {
                  completeCutover(pending);
                  return;
                }
              } catch (successorError) {
                // Ambiguous cutover: retain both UAs and retry finalization.
                logger.warn(
                  "sparkone.browser_voice.renewal_successor_status_unavailable",
                  {
                    failureCategory:
                      successorError instanceof VoiceRequestRejectedError
                        ? "request_rejected"
                        : "request_failed",
                  },
                );
              }
            }
          }
          if (!isCurrentAttempt()) return;
          if (outcome === "authority_revoked") {
            // A still-active predecessor rules out the lost-response case above;
            // the explicit authority outcome is therefore terminal.
            if (!predecessorCutoverObserved) {
              void suspendRegistrationAuthority("renewal_denied");
              return;
            }
            // The predecessor has already lost authority, but successor status is
            // still ambiguous. Keep both local UAs and retry the idempotent
            // finalization/status reconciliation instead of wedging rotation.
          }
          renewalAttemptRef.current += 1;
          const retryDelay = boundedRetryDelay(renewalAttemptRef.current);
          logger.warn(
            "sparkone.browser_voice.renewal_finalize_retry_scheduled",
            {
              attempt: renewalAttemptRef.current,
              retryDelayMs: retryDelay,
              failureCategory: outcome,
            },
          );
          replaceBrowserRuntimeTimer(
            renewalRef,
            () => {
              void finalizeRenewalRef.current(current);
            },
            retryDelay,
          );
        }
      })();
      const attempt: RenewalFinalizationAttempt = {
        current,
        pending,
        predecessorSessionId,
        promise,
      };
      renewalFinalizationRef.current = attempt;
      void promise.then(
        () => {
          if (renewalFinalizationRef.current === attempt)
            renewalFinalizationRef.current = null;
        },
        () => {
          if (renewalFinalizationRef.current === attempt)
            renewalFinalizationRef.current = null;
        },
      );
      return promise;
    },
    [suspendRegistrationAuthority],
  );
  useEffect(() => {
    finalizeRenewalRef.current = finalizeRenewal;
  }, [finalizeRenewal]);

  const confirmIndependentReplacement = useCallback(
    async (current: ActiveBrowserSession) => {
      const replacement = current.pendingRenewal;
      const phone = softphoneRef.current;
      if (
        !replacement ||
        current.pendingRotationKind !== "independent_replacement" ||
        !phone ||
        sessionRef.current !== current ||
        current.authorityRevoked
      )
        return;

      const retryReplacementPreparation = (reason: string) => {
        phone.cancelCredentialRotation(replacement.sessionId);
        void revokeRegistration({
          connectorId:
            current.pendingReplacementIdentity?.connectorId ??
            current.connectorId,
          sessionId: replacement.sessionId,
        });
        current.pendingRenewal = null;
        current.pendingInstallationTarget = null;
        current.pendingRotationKind = null;
        current.pendingReplacementIdentity = null;
        current.renewalStartedAt = null;
        current.replacementPending = false;
        setTransitioning(false);
        replacementPollAttemptsRef.current = 0;
        bootstrapAttemptRef.current += 1;
        const retryDelay = boundedRetryDelay(bootstrapAttemptRef.current);
        logger.warn(
          "sparkone.browser_voice.registration_rebootstrap_retry_scheduled",
          {
            attempt: bootstrapAttemptRef.current,
            retryDelayMs: retryDelay,
            failureCategory: reason,
          },
        );
        replaceBrowserRuntimeTimer(
          bootstrapRetryRef,
          () => {
            void replacePersistentRegistrationRef.current(current, reason);
          },
          retryDelay,
        );
      };

      try {
        const observed = await voiceApi.getBrowserSessionStatus(
          replacement.sessionId,
        );
        if (
          sessionRef.current !== current ||
          current.pendingRenewal !== replacement ||
          current.authorityRevoked
        )
          return;
        if (observed.status === "registered") {
          const replacementStartedAt = current.renewalStartedAt;
          const predecessor = {
            connectorId: current.connectorId,
            sessionId: current.sessionId,
          };
          const replacementIdentity = current.pendingReplacementIdentity;
          phone.confirmCredentialCutover(replacement.sessionId);
          current.sessionId = replacement.sessionId;
          current.expiresAtMs = sessionExpiryMs(replacement);
          current.credentials = replacement;
          if (replacementIdentity) {
            current.connectorId = replacementIdentity.connectorId;
            current.clientUserRef = replacementIdentity.clientUserRef;
            current.extensionId = replacementIdentity.extensionId;
            current.endpointDeviceId = replacementIdentity.endpointDeviceId;
          }
          current.pendingRenewal = null;
          current.pendingInstallationTarget = null;
          current.pendingRotationKind = null;
          current.pendingReplacementIdentity = null;
          current.renewalStartedAt = null;
          current.replacementPending = false;
          setTransitioning(false);
          current.registrationReadyObserved = true;
          replacementPollAttemptsRef.current = 0;
          bootstrapAttemptRef.current = 0;
          logger.info("sparkone.browser_voice.replacement_registered", {
            latencyMs:
              replacementStartedAt === null
                ? null
                : boundedLatency(replacementStartedAt),
            predecessorCleanup: "requested",
          });
          updatePublicState({
            lifecycle: "ready",
            message: "Browser phone ready.",
          });
          scheduleRef.current(replacement);
          void revokeRegistration(predecessor);
          return;
        }
        if (
          ["failed", "expired", "revoked", "revocation_pending"].includes(
            observed.status,
          ) ||
          replacementPollAttemptsRef.current >= 39
        ) {
          retryReplacementPreparation(`replacement_${observed.status}`);
          return;
        }
        replacementPollAttemptsRef.current += 1;
        replaceBrowserRuntimeTimer(
          registrationPollRef,
          () => {
            void confirmIndependentReplacementRef.current(current);
          },
          500,
        );
      } catch (error) {
        const outcome = renewalFailureOutcome(error);
        if (outcome === "authority_revoked") {
          void suspendRegistrationAuthority("renewal_denied");
          return;
        }
        if (outcome === "replace_session") {
          retryReplacementPreparation("replacement_unavailable");
          return;
        }
        replacementPollAttemptsRef.current += 1;
        const retryDelay = boundedRetryDelay(
          replacementPollAttemptsRef.current,
        );
        logger.warn(
          "sparkone.browser_voice.replacement_status_retry_scheduled",
          {
            attempt: replacementPollAttemptsRef.current,
            retryDelayMs: retryDelay,
            failureCategory: outcome,
          },
        );
        replaceBrowserRuntimeTimer(
          registrationPollRef,
          () => {
            void confirmIndependentReplacementRef.current(current);
          },
          retryDelay,
        );
      }
    },
    [revokeRegistration, suspendRegistrationAuthority, updatePublicState],
  );
  useEffect(() => {
    confirmIndependentReplacementRef.current = confirmIndependentReplacement;
  }, [confirmIndependentReplacement]);

  const schedule = useCallback(
    (session: VoiceBrowserSession, delay?: number) => {
      replaceBrowserRuntimeTimer(
        renewalRef,
        () => {
          const current = sessionRef.current;
          const phone = softphoneRef.current;
          if (
            !current ||
            current.authorityRevoked ||
            current.pendingRenewal ||
            current.replacementPending ||
            !phone ||
            !mountedRef.current
          )
            return;
          void runSerializedTransition(async () => {
            try {
              const renewed = await voiceApi.renewBrowserSession(
                current.sessionId,
                activeActivationProof(current),
              );
              if (
                !mountedRef.current ||
                sessionRef.current !== current ||
                current.authorityRevoked ||
                softphoneRef.current !== phone
              ) {
                // Persistent credentials can be shared with the surviving
                // same-tab activation. Only one-shot call sessions compensate
                // a result that arrives after their local consumer is stale.
                if (!current.persistent) {
                  void revokeRegistration(
                    {
                      connectorId: current.connectorId,
                      sessionId: renewed.sessionId,
                    },
                    current.revocationActivationProof ??
                      activeActivationProof(current),
                  );
                }
                return;
              }
              current.pendingRenewal = renewed;
              setTransitioning(true);
              current.pendingRotationKind = "provider_successor";
              current.renewalStartedAt = Date.now();
              phone.renewCredentials(renewed);
            } catch (error) {
              current.pendingRenewal = null;
              current.pendingInstallationTarget = null;
              current.pendingRotationKind = null;
              current.renewalStartedAt = null;
              const outcome = renewalFailureOutcome(error);
              logger.warn("sparkone.browser_voice.session_renewal_failed", {
                failureCategory: outcome,
              });
              const connected = phone
                .getDialogs()
                .some(
                  (dialog) =>
                    dialog.state === "connected" ||
                    dialog.state === "answering",
                );
              if (outcome === "authority_revoked") {
                void suspendRegistrationAuthority("renewal_denied");
                return;
              }
              if (outcome === "replace_session") {
                void replacePersistentRegistrationRef.current(
                  current,
                  "renewal_session_unavailable",
                );
                return;
              }
              if (outcome === "reconcile_conflict") {
                try {
                  const observed = await voiceApi.getBrowserSessionStatus(
                    current.sessionId,
                  );
                  if (
                    sessionRef.current !== current ||
                    current.authorityRevoked
                  )
                    return;
                  if (observed.status === "revoked") {
                    if (current.persistent) {
                      void replacePersistentRegistrationRef.current(
                        current,
                        "renewal_conflict_session_revoked",
                      );
                    } else {
                      void suspendRegistrationAuthority("session_expired");
                    }
                    return;
                  }
                  if (["expired", "failed"].includes(observed.status)) {
                    void replacePersistentRegistrationRef.current(
                      current,
                      "renewal_conflict_session_unavailable",
                    );
                    return;
                  }
                } catch (statusError) {
                  if (
                    renewalFailureOutcome(statusError) === "replace_session"
                  ) {
                    void replacePersistentRegistrationRef.current(
                      current,
                      "renewal_conflict_session_missing",
                    );
                    return;
                  }
                }
              }
              if (!current.persistent && !connected) {
                void release();
                return;
              }
              renewalAttemptRef.current += 1;
              const backoff = boundedRetryDelay(renewalAttemptRef.current);
              const retryDelay =
                Date.now() < current.expiresAtMs
                  ? Math.min(
                      backoff,
                      Math.max(1_000, current.expiresAtMs - Date.now()),
                    )
                  : backoff;
              updatePublicState({
                lifecycle: "degraded",
                message: connected
                  ? "Registration renewal failed. The connected call is preserved."
                  : "Browser registration is reconnecting.",
              });
              logger.warn("sparkone.browser_voice.renewal_retry_scheduled", {
                attempt: renewalAttemptRef.current,
                retryDelayMs: retryDelay,
                failureCategory: outcome,
              });
              scheduleRef.current(current.credentials, retryDelay);
            }
          });
        },
        delay ?? renewalDelayWithJitter(session),
      );
    },
    [
      activeActivationProof,
      release,
      revokeRegistration,
      runSerializedTransition,
      suspendRegistrationAuthority,
      updatePublicState,
    ],
  );
  useEffect(() => {
    scheduleRef.current = schedule;
  }, [schedule]);

  const scheduleInstallation = useCallback(
    (current: ActiveBrowserSession, delay?: number) => {
      if (!current.installationId) return;
      replaceBrowserRuntimeTimer(
        renewalRef,
        () => {
          void runSerializedTransition(async () => {
            const phone = softphoneRef.current;
            if (
              sessionRef.current !== current ||
              current.authorityRevoked ||
              !phone ||
              !current.installationId
            )
              return;

            const completePendingCutover = (
              pending: VoiceBrowserSession,
              target: NonNullable<
                ActiveBrowserSession["pendingInstallationTarget"]
              >,
              stateVersion: number,
              authorityPath: BrowserRegistrationAuthorityPath,
            ) => {
              if (
                sessionRef.current !== current ||
                current.pendingRenewal !== pending ||
                current.pendingInstallationTarget !== target ||
                current.authorityRevoked
              )
                return false;
              phone.confirmCredentialCutover(target.sessionId);
              current.sessionId = target.sessionId;
              authoritativeRegistrationSessionIdsRef.current.clear();
              authoritativeRegistrationSessionIdsRef.current.add(
                target.sessionId,
              );
              current.credentials = pending;
              current.expiresAtMs = sessionExpiryMs(pending);
              current.installationGeneration = target.generation;
              current.installationStateVersion = Math.max(
                current.installationStateVersion ?? 0,
                stateVersion,
              );
              const successorLocallyRegistered =
                current.localRegistrationEpisodes.has(target.sessionId);
              current.localRegistrationState = successorLocallyRegistered
                ? "local_registered"
                : "connecting";
              current.providerRegistrationState = "registered";
              cancelBrowserRuntimeTimer(localConfirmationWarningRef);
              updatePublicState({
                lifecycle: successorLocallyRegistered
                  ? "ready"
                  : "registering",
                localRegistrationState: current.localRegistrationState,
                providerRegistrationState: "registered",
                message: successorLocallyRegistered
                  ? "Browser phone ready."
                  : "Connecting Browser phone…",
              });
              current.pendingRenewal = null;
              current.pendingInstallationTarget = null;
              current.pendingRotationKind = null;
              current.renewalStartedAt = null;
              setTransitioning(false);
              renewalAttemptRef.current = 0;
              logger.info(
                "sparkone.browser_voice.renewal_successor_registered",
                {
                  observationSource: authorityPath,
                },
              );
              scheduleInstallationRef.current(current);
              return true;
            };

            const abandonPendingCutover = (
              pending: VoiceBrowserSession,
              target: NonNullable<
                ActiveBrowserSession["pendingInstallationTarget"]
              >,
            ) => {
              if (
                current.pendingRenewal !== pending ||
                current.pendingInstallationTarget !== target
              )
                return;
              phone.cancelCredentialRotation(target.sessionId);
              endLocalRegistrationEpisode(current, target.sessionId);
              authoritativeRegistrationSessionIdsRef.current.clear();
              authoritativeRegistrationSessionIdsRef.current.add(
                current.sessionId,
              );
              current.pendingRenewal = null;
              current.pendingInstallationTarget = null;
              current.pendingRotationKind = null;
              current.renewalStartedAt = null;
              setTransitioning(false);
            };

            const reconcilePendingCutover = async (): Promise<
              "none" | "pending" | "completed" | "abandoned"
            > => {
              const pending = current.pendingRenewal;
              const target = current.pendingInstallationTarget;
              if (!pending || !target) return "none";
              try {
                const projection = await voiceApi.getBrowserInstallation(
                  target.installationId,
                );
                if (
                  sessionRef.current !== current ||
                  current.pendingRenewal !== pending ||
                  current.pendingInstallationTarget !== target ||
                  current.authorityRevoked
                )
                  return "pending";
                current.installationStateVersion = Math.max(
                  current.installationStateVersion ?? 0,
                  projection.stateVersion,
                );
                const exact = [
                  {
                    generation: projection.generation,
                    sessionId: projection.sessionId,
                    status: projection.status,
                  },
                  projection.pending,
                ].find(
                  (candidate) =>
                    candidate?.generation === target.generation &&
                    candidate.sessionId === target.sessionId,
                );
                if (exact?.status === "registered") {
                  return completePendingCutover(
                    pending,
                    target,
                    projection.stateVersion,
                    "recovery_read",
                  )
                    ? "completed"
                    : "pending";
                }
                if (exact?.status === "issued") return "pending";
                abandonPendingCutover(pending, target);
                return "abandoned";
              } catch {
                // A failed or inconclusive recovery read is not permission to mint
                // another successor. Preserve this exact credential and retry its
                // authoritative projection before calling ensure again.
                return "pending";
              }
            };

            const schedulePendingReconciliation = () => {
              authoritativeRegistrationSessionIdsRef.current.clear();
              authoritativeRegistrationSessionIdsRef.current.add(
                current.sessionId,
              );
              setTransitioning(Boolean(current.pendingRenewal));
              const connected = phone
                .getDialogs()
                .some(
                  (dialog) =>
                    dialog.state === "connected" ||
                    dialog.state === "answering",
                );
              updatePublicState({
                lifecycle: "degraded",
                message: connected
                  ? "Registration renewal is still converging. The connected call is preserved."
                  : "Browser registration is reconnecting.",
              });
              renewalAttemptRef.current += 1;
              scheduleInstallationRef.current(
                current,
                boundedRetryDelay(renewalAttemptRef.current),
              );
            };

            try {
              const priorPending = await reconcilePendingCutover();
              if (priorPending === "completed") return;
              if (priorPending === "pending") {
                schedulePendingReconciliation();
                return;
              }

              let ensured = await ensureTabBrowserInstallation();
              while (isBrowserInstallationPreparing(ensured)) {
                if (
                  sessionRef.current !== current ||
                  current.authorityRevoked ||
                  ensured.installationId !== current.installationId
                )
                  return;
                updatePublicState({
                  lifecycle: "degraded",
                  message: "Browser registration is reconnecting.",
                });
                const preparationAbort = new AbortController();
                preparationAbortRef.current = preparationAbort;
                let retryAuthorization: Awaited<
                  ReturnType<
                    BrowserPreparationCoordinator["waitUntilRetryAuthorized"]
                  >
                >;
                try {
                  retryAuthorization =
                    await preparationCoordinatorRef.current!.waitUntilRetryAuthorized(
                      ensured,
                      preparationAbort.signal,
                    );
                } catch (error) {
                  if (preparationAbort.signal.aborted) return;
                  throw error;
                } finally {
                  if (preparationAbortRef.current === preparationAbort) {
                    preparationAbortRef.current = null;
                  }
                }
                if (retryAuthorization.action === "terminal") return;
                if (
                  !mountedRef.current ||
                  !authEligibleRef.current ||
                  sessionRef.current !== current ||
                  current.authorityRevoked ||
                  !persistentEnabledRef.current ||
                  !isBrowserPhoneOwner() ||
                  preparationAbort.signal.aborted
                )
                  return;
                ensured = await ensureTabBrowserInstallation();
              }
              if (
                sessionRef.current !== current ||
                current.authorityRevoked ||
                ensured.installationId !== current.installationId
              )
                return;
              if (ensured.activationLease) {
                current.activationLease = ensured.activationLease;
                scheduleActivationLeaseHeartbeatRef.current(current);
              }
              if (!ensured.browserSession) {
                current.installationStateVersion = Math.max(
                  current.installationStateVersion ?? 0,
                  ensured.stateVersion,
                );
                scheduleInstallationRef.current(
                  current,
                  renewalDelay({
                    ...current.credentials,
                    expiresAt: ensured.expiresAt,
                  }),
                );
                return;
              }
              const target = browserInstallationCredentialTarget(ensured);
              if (!target) {
                throw new Error(
                  "Browser installation successor identity was not delivered.",
                );
              }
              if (target.sessionId === current.sessionId) {
                current.installationStateVersion = Math.max(
                  current.installationStateVersion ?? 0,
                  ensured.stateVersion,
                );
                scheduleInstallationRef.current(
                  current,
                  renewalDelay({
                    ...current.credentials,
                    expiresAt: ensured.expiresAt,
                  }),
                );
                return;
              }
              const authority =
                registrationCoordinatorRef.current!.waitForRegistered(
                  target,
                  `renewal:${target.installationId}:${target.generation}:${target.sessionId}`,
                );
              // dispose() rejects outstanding waiters. Attach a handler at creation
              // time because an unmount can occur before this transition reaches
              // its await below.
              void authority.catch(() => undefined);
              authoritativeRegistrationSessionIdsRef.current.add(
                target.sessionId,
              );
              current.pendingRenewal = ensured.browserSession;
              current.pendingInstallationTarget = target;
              current.pendingRotationKind = "provider_successor";
              current.renewalStartedAt = Date.now();
              setTransitioning(true);
              phone.renewCredentials(ensured.browserSession);
              try {
                const observation = await authority;
                completePendingCutover(
                  ensured.browserSession,
                  target,
                  observation.stateVersion,
                  observation.authorityPath,
                );
                return;
              } catch {
                if (sessionRef.current !== current || current.authorityRevoked)
                  return;
                const reconciled = await reconcilePendingCutover();
                if (reconciled === "completed") return;
                schedulePendingReconciliation();
                return;
              }
            } catch {
              if (sessionRef.current !== current || current.authorityRevoked)
                return;
              if (current.pendingRenewal && current.pendingInstallationTarget) {
                schedulePendingReconciliation();
                return;
              }
              authoritativeRegistrationSessionIdsRef.current.clear();
              authoritativeRegistrationSessionIdsRef.current.add(
                current.sessionId,
              );
              current.pendingRenewal = null;
              current.pendingInstallationTarget = null;
              current.pendingRotationKind = null;
              current.renewalStartedAt = null;
              setTransitioning(false);
              const connected = phone
                .getDialogs()
                .some(
                  (dialog) =>
                    dialog.state === "connected" ||
                    dialog.state === "answering",
                );
              updatePublicState({
                lifecycle: "degraded",
                message: connected
                  ? "Registration renewal failed. The connected call is preserved."
                  : "Browser registration is reconnecting.",
              });
              renewalAttemptRef.current += 1;
              scheduleInstallationRef.current(
                current,
                boundedRetryDelay(renewalAttemptRef.current),
              );
            }
          });
        },
        delay ?? renewalDelayWithJitter(current.credentials),
      );
    },
    [endLocalRegistrationEpisode, runSerializedTransition, updatePublicState],
  );
  useEffect(() => {
    scheduleInstallationRef.current = scheduleInstallation;
  }, [scheduleInstallation]);

  const confirmRegistration = useCallback(async () => {
    const current = sessionRef.current;
    if (!current || current.authorityRevoked || !mountedRef.current) return;
    try {
      const observed = await voiceApi.getBrowserSessionStatus(
        current.sessionId,
      );
      if (sessionRef.current !== current || current.authorityRevoked) return;
      if (observed.status === "registered") {
        registrationPollAttemptsRef.current = 0;
        if (!current.registrationReadyObserved) {
          current.registrationReadyObserved = true;
          logger.info("sparkone.browser_voice.registration_ready", {
            latencyMs: boundedLatency(current.registrationStartedAt),
            observationSource: "provider_status",
          });
        }
        current.providerRegistrationState = "registered";
        cancelBrowserRuntimeTimer(localConfirmationWarningRef);
        updatePublicState({
          lifecycle:
            current.localRegistrationState === "local_registered"
              ? "ready"
              : "registering",
          localRegistrationState: current.localRegistrationState,
          providerRegistrationState: "registered",
          message:
            current.localRegistrationState === "local_registered"
              ? "Browser phone ready."
              : "Connecting Browser phone…",
        });
        return;
      }
      if (observed.status === "revoked") {
        if (current.persistent) {
          void replacePersistentRegistrationRef.current(
            current,
            "predecessor_revoked",
          );
        } else {
          void suspendRegistrationAuthority("session_expired");
        }
        return;
      }
      if (observed.status === "revocation_pending") {
        registrationPollAttemptsRef.current += 1;
        replaceBrowserRuntimeTimer(
          registrationPollRef,
          () => void confirmRegistrationRef.current(),
          boundedRetryDelay(registrationPollAttemptsRef.current),
        );
        return;
      }
      if (
        observed.status === "expired" ||
        observed.status === "failed" ||
        registrationPollAttemptsRef.current >= REGISTRATION_STATUS_MAX_ATTEMPTS
      ) {
        if (current.persistent) {
          void replacePersistentRegistrationRef.current(
            current,
            `predecessor_${observed.status}`,
          );
        } else {
          void suspendRegistrationAuthority("session_expired");
        }
        return;
      }
      registrationPollAttemptsRef.current += 1;
      replaceBrowserRuntimeTimer(
        registrationPollRef,
        () => {
          void confirmRegistrationRef.current();
        },
        REGISTRATION_STATUS_POLL_INTERVAL_MS,
      );
    } catch (error) {
      if (definitiveAvailabilityFailure(error)) {
        void suspendRegistrationAuthority("session_expired");
        return;
      }
      if (!retryableAvailabilityFailure(error)) {
        void suspendRegistrationAuthority("session_expired");
        return;
      }
      registrationPollAttemptsRef.current += 1;
      const retryDelay = boundedRetryDelay(registrationPollAttemptsRef.current);
      updatePublicState({
        lifecycle: "degraded",
        message: "Registration status is temporarily unavailable. Retrying…",
      });
      logger.warn(
        "sparkone.browser_voice.registration_status_retry_scheduled",
        {
          attempt: registrationPollAttemptsRef.current,
          retryDelayMs: retryDelay,
        },
      );
      replaceBrowserRuntimeTimer(
        registrationPollRef,
        () => {
          void confirmRegistrationRef.current();
        },
        retryDelay,
      );
    }
  }, [suspendRegistrationAuthority, updatePublicState]);
  useEffect(() => {
    confirmRegistrationRef.current = () => void confirmRegistration();
  }, [confirmRegistration]);

  const isBrowserPhoneOwner = useCallback(
    () => mountedRef.current && ownsBrowserPhoneRef.current,
    [],
  );

  const isSessionActive = useCallback(
    (sessionId: string) => {
      const current = sessionRef.current;
      const phone = softphoneRef.current;
      return (
        isBrowserPhoneOwner() &&
        current?.sessionId === sessionId &&
        !current.authorityRevoked &&
        !current.pendingRenewal &&
        !current.replacementPending &&
        !transitionActiveRef.current &&
        transitionPromiseRef.current === null &&
        phone !== null &&
        phone.getStatus() === "registered" &&
        phone.isAcceptingOffers()
      );
    },
    [isBrowserPhoneOwner],
  );

  const getActivePersistentRegistration = useCallback(() => {
    const current = sessionRef.current;
    const phone = softphoneRef.current;
    if (
      !isBrowserPhoneOwner() ||
      !current?.persistent ||
      current.authorityRevoked ||
      !current.activationLease ||
      current.pendingRenewal ||
      current.replacementPending ||
      transitionActiveRef.current ||
      transitionPromiseRef.current !== null ||
      current.callSessionId !== null ||
      !current.registrationReadyObserved ||
      !phone ||
      phone.getStatus() !== "registered" ||
      !phone.isAcceptingOffers() ||
      phone
        .getDialogs()
        .some((dialog) =>
          ["ringing", "answering", "connected"].includes(dialog.state),
        )
    )
      return null;
    return {
      connectorId: current.connectorId,
      activationProof: activeActivationProof(current),
      sessionId: current.sessionId,
      extensionId: current.extensionId,
      endpointDeviceId: current.endpointDeviceId,
    };
  }, [activeActivationProof, isBrowserPhoneOwner]);

  const isCallActive = useCallback(
    (callSessionId: string) =>
      isBrowserPhoneOwner() &&
      sessionRef.current?.callSessionId === callSessionId &&
      softphoneRef.current !== null,
    [isBrowserPhoneOwner],
  );

  const getActiveCallIdentity = useCallback(
    (callSessionId: string): ActiveBrowserCallIdentity | null => {
      const current = sessionRef.current;
      if (
        !isBrowserPhoneOwner() ||
        !current ||
        current.callSessionId !== callSessionId ||
        !softphoneRef.current
      ) return null;
      return {
        connectorId: current.connectorId,
        clientUserRef: current.clientUserRef,
        endpointDeviceId: current.endpointDeviceId,
      };
    },
    [isBrowserPhoneOwner],
  );

  const isTenantSwitchBlocked = useCallback(
    () =>
      softphoneRef.current
        ?.getDialogs()
        .some((dialog) => ["answering", "connected"].includes(dialog.state)) ??
      false,
    [],
  );

  const persistAudioDevicePreferences = useCallback(
    (
      scope: AudioDevicePreferencesScope | null,
      inputDeviceId: string,
      outputDeviceId: string,
    ): boolean => {
      if (!scope) return true;
      try {
        writeAudioDevicePreferences(
          scope,
          createAudioDevicePreferences(inputDeviceId, outputDeviceId),
        );
        return true;
      } catch {
        // The helper emits the fixed, device-safe storage failure warning.
        // Media state has already been applied and remains the source of truth.
        return false;
      }
    },
    [],
  );

  const runSerializedAudioDeviceOperation = useCallback(
    <T,>(operation: () => Promise<T>): Promise<T> => {
      const pending = audioDeviceOperationQueueRef.current.then(
        operation,
        operation,
      );
      // Do not let a rejected caller operation poison later reconciliation. The
      // initiating caller receives `pending` and handles its fixed safe error.
      audioDeviceOperationQueueRef.current = pending.then(
        () => undefined,
        () => undefined,
      );
      return pending;
    },
    [],
  );

  const reconcileAudioDevices = useCallback(
    (source: "activation" | "manual" | "devicechange") =>
      runSerializedAudioDeviceOperation(async () => {
        const phone = softphoneRef.current;
        if (!isBrowserPhoneOwner() || !phone) {
          if (source === "manual") {
            throw browserAudioError("AUDIO_PREFERENCES_RUNTIME_UNAVAILABLE");
          }
          return;
        }
        const generation = ++audioDeviceGenerationRef.current;
        const scopeGeneration = audioPreferenceScopeGenerationRef.current;
        const scope = audioPreferencesScopeRef.current;
        const isCurrentOperation = () =>
          mountedRef.current &&
          isBrowserPhoneOwner() &&
          audioDeviceGenerationRef.current === generation &&
          audioPreferenceScopeGenerationRef.current === scopeGeneration &&
          audioPreferencesScopeRef.current === scope &&
          softphoneRef.current === phone;
        try {
          const devices = await phone.getAudioDevices();
          if (!isCurrentOperation()) return;
          const outputSelectionSupported = phone.supportsAudioOutputSelection();
          setAudioDevices(devices);
          setSupportsOutputSelection(outputSelectionSupported);

          const applied = phone.getSelectedAudioDeviceIds();
          const deferredOutputDeviceId = pendingOutputPreferenceRef.current;
          let reconciledInputDeviceId = applied.inputDeviceId;
          // Before a route can support setSinkId, a deferred preference is the
          // only useful UI value. Once it can, the UI must instead report the
          // sink that the softphone actually committed.
          let reconciledOutputDeviceId = outputSelectionSupported
            ? applied.outputDeviceId
            : deferredOutputDeviceId || applied.outputDeviceId;
          let persistedOutputDeviceId =
            deferredOutputDeviceId || applied.outputDeviceId;
          let reconciledSelection =
            selectedInputDeviceId !== reconciledInputDeviceId ||
            selectedOutputDeviceId !== reconciledOutputDeviceId;
          let selectionRemoved = false;
          const inputSelectionRemoved = Boolean(
            applied.inputDeviceId &&
            !devices.some(
              (device) =>
                device.kind === "audioinput" &&
                device.deviceId === applied.inputDeviceId,
            ),
          );
          const hasActiveDialog = phone
            .getDialogs()
            .some((dialog) =>
              ["ringing", "answering", "connected"].includes(dialog.state),
            );
          // Device-change handling must not capture new media. If no dialog is
          // active, the selection is staged only and can be cleared locally. An
          // active dialog retains its proven media route until an explicit user
          // action or the browser reports a terminal media failure.
          if (inputSelectionRemoved && !hasActiveDialog) {
            try {
              phone.clearStagedInputDevice();
            } catch {
              logger.warn(
                "sparkone.browser_voice.audio_device_reconciliation_failed",
                {
                  failureCategory: "staged_input_clear_failed",
                  source,
                },
              );
              return;
            }
            if (!isCurrentOperation()) return;
            reconciledInputDeviceId =
              phone.getSelectedAudioDeviceIds().inputDeviceId;
            reconciledSelection = true;
            selectionRemoved = true;
            logger.info("sparkone.browser_voice.audio_device_reconciled", {
              deviceKind: "input",
              source,
            });
          }

          const outputDeviceId =
            deferredOutputDeviceId || applied.outputDeviceId;
          // An unavailable current route is not evidence that a stored speaker
          // disappeared. Only an enumeration made while setSinkId is currently
          // supported may prove an output preference is gone.
          const outputSelectionRemoved = Boolean(
            outputDeviceId &&
            outputSelectionSupported &&
            !devices.some(
              (device) =>
                device.kind === "audiooutput" &&
                device.deviceId === outputDeviceId,
            ),
          );
          if (outputSelectionRemoved && !hasActiveDialog) {
            try {
              if (applied.outputDeviceId === outputDeviceId) {
                phone.clearStagedOutputDevice();
              }
            } catch {
              logger.warn(
                "sparkone.browser_voice.audio_device_reconciliation_failed",
                {
                  failureCategory: "staged_output_clear_failed",
                  source,
                },
              );
              return;
            }
            if (!isCurrentOperation()) return;
            pendingOutputPreferenceRef.current = "";
            reconciledOutputDeviceId =
              phone.getSelectedAudioDeviceIds().outputDeviceId;
            persistedOutputDeviceId = reconciledOutputDeviceId;
            reconciledSelection = true;
            selectionRemoved = true;
            logger.info("sparkone.browser_voice.audio_device_reconciled", {
              deviceKind: "output",
              source,
            });
          }
          if (!isCurrentOperation()) return;
          setSelectedInputDeviceId(reconciledInputDeviceId);
          setSelectedOutputDeviceId(reconciledOutputDeviceId);
          if (selectionRemoved) {
            setAudioPreferenceWarning(
              browserAudioErrorMessages.AUDIO_PREFERENCES_SELECTION_REMOVED,
            );
          }
          if (reconciledSelection || audioPreferenceWarning) {
            if (
              persistAudioDevicePreferences(
                scope,
                reconciledInputDeviceId,
                persistedOutputDeviceId,
              ) &&
              !selectionRemoved
            ) {
              setAudioPreferenceWarning(null);
            }
          }
        } catch {
          logger.warn("sparkone.browser_voice.audio_devices_unavailable", {
            failureCategory: "device_enumeration_failed",
            source,
          });
          if (source === "manual") {
            throw browserAudioError(
              "AUDIO_PREFERENCES_DEVICE_LIST_UNAVAILABLE",
            );
          }
        }
      }),
    [
      isBrowserPhoneOwner,
      persistAudioDevicePreferences,
      runSerializedAudioDeviceOperation,
      audioPreferenceWarning,
      selectedInputDeviceId,
      selectedOutputDeviceId,
    ],
  );

  const restoreAudioDevicePreferences = useCallback(
    (phone: SipSoftphone) =>
      runSerializedAudioDeviceOperation(async () => {
        const scope = audioPreferencesScopeRef.current;
        if (!scope || !isBrowserPhoneOwner() || softphoneRef.current !== phone)
          return;
        const generation = ++audioDeviceGenerationRef.current;
        const scopeGeneration = audioPreferenceScopeGenerationRef.current;
        const isCurrentRestore = () =>
          mountedRef.current &&
          isBrowserPhoneOwner() &&
          audioDeviceGenerationRef.current === generation &&
          audioPreferenceScopeGenerationRef.current === scopeGeneration &&
          audioPreferencesScopeRef.current === scope &&
          softphoneRef.current === phone;
        let preferences;
        try {
          preferences = readAudioDevicePreferences(scope);
        } catch {
          logger.warn("sparkone.browser_voice.audio_device_restore_failed", {
            failureCategory: "preference_read_failed",
          });
          return;
        }
        if (!preferences || !isCurrentRestore()) return;

        let devices: SipAudioDevice[];
        try {
          devices = await phone.getAudioDevices();
        } catch {
          logger.warn("sparkone.browser_voice.audio_device_restore_failed", {
            failureCategory: "device_enumeration_failed",
          });
          return;
        }
        if (!isCurrentRestore()) return;

        const hasActiveDialog = phone
          .getDialogs()
          .some((dialog) =>
            ["ringing", "answering", "connected"].includes(dialog.state),
          );
        const inputPreferenceAvailable =
          preferences.inputDeviceId === "" ||
          devices.some(
            (device) =>
              device.kind === "audioinput" &&
              device.deviceId === preferences.inputDeviceId,
          );
        const outputSelectionSupported = phone.supportsAudioOutputSelection();
        const outputPreferenceAvailable =
          preferences.outputDeviceId === "" ||
          !outputSelectionSupported ||
          devices.some(
            (device) =>
              device.kind === "audiooutput" &&
              device.deviceId === preferences.outputDeviceId,
          );

        // Input restore only stages the future media constraint. It never calls
        // setInputDevice because restore must not replace an active route or ask
        // the browser to capture microphone media.
        if (!hasActiveDialog && inputPreferenceAvailable) {
          try {
            phone.stageInputDevice(preferences.inputDeviceId);
          } catch {
            logger.info(
              "sparkone.browser_voice.audio_device_restore_deferred",
              {
                deviceKind: "input",
                reason: "active_media_route",
              },
            );
          }
        } else if (!hasActiveDialog && !inputPreferenceAvailable) {
          try {
            phone.clearStagedInputDevice();
          } catch {
            logger.warn("sparkone.browser_voice.audio_device_restore_failed", {
              deviceKind: "input",
              failureCategory: "staged_device_clear_failed",
            });
            return;
          }
        }
        if (!isCurrentRestore()) return;
        let deferredOutputRestoreFailed = false;
        if (preferences.outputDeviceId && !outputSelectionSupported) {
          // Keep the scoped selection intact until a live remote audio route can
          // authoritatively apply it. Lack of a route is not a device-removal
          // signal and must never overwrite durable storage.
          pendingOutputPreferenceRef.current = preferences.outputDeviceId;
        } else if (preferences.outputDeviceId && outputPreferenceAvailable) {
          try {
            await phone.setOutputDevice(preferences.outputDeviceId);
            pendingOutputPreferenceRef.current = "";
          } catch {
            // A currently capable route rejected the sink change. Preserve the
            // prior durable preference; a later ready route may retry it.
            pendingOutputPreferenceRef.current = preferences.outputDeviceId;
            deferredOutputRestoreFailed = true;
            logger.warn("sparkone.browser_voice.audio_device_restore_failed", {
              deviceKind: "output",
              failureCategory: "device_restore_failed",
            });
          }
        } else if (!hasActiveDialog && !outputPreferenceAvailable) {
          try {
            if (
              phone.getSelectedAudioDeviceIds().outputDeviceId ===
              preferences.outputDeviceId
            ) {
              phone.clearStagedOutputDevice();
            }
          } catch {
            logger.warn("sparkone.browser_voice.audio_device_restore_failed", {
              deviceKind: "output",
              failureCategory: "staged_device_clear_failed",
            });
            return;
          }
          pendingOutputPreferenceRef.current = "";
        }
        if (!isCurrentRestore()) return;

        const selected = phone.getSelectedAudioDeviceIds();
        // A deferred preference is retry intent, not an applied sink. UI state
        // must always reflect the route the softphone has actually committed.
        const selectedOutputDeviceId = selected.outputDeviceId;
        const clearedUnavailableStagedPreference =
          !hasActiveDialog &&
          ((!inputPreferenceAvailable && preferences.inputDeviceId !== "") ||
            (!outputPreferenceAvailable && preferences.outputDeviceId !== ""));
        setSelectedInputDeviceId(selected.inputDeviceId);
        setSelectedOutputDeviceId(selectedOutputDeviceId);
        if (deferredOutputRestoreFailed) {
          setAudioPreferenceWarning(
            browserAudioErrorMessages.AUDIO_PREFERENCES_SPEAKER_SWITCH_FAILED,
          );
        } else if (clearedUnavailableStagedPreference) {
          setAudioPreferenceWarning(
            browserAudioErrorMessages.AUDIO_PREFERENCES_SELECTION_REMOVED,
          );
          persistAudioDevicePreferences(
            scope,
            inputPreferenceAvailable ? selected.inputDeviceId : "",
            outputPreferenceAvailable ? selectedOutputDeviceId : "",
          );
          logger.info("sparkone.browser_voice.audio_device_reconciled", {
            source: "restore",
            deviceKind: "staged",
          });
        }
      }),
    [
      isBrowserPhoneOwner,
      persistAudioDevicePreferences,
      runSerializedAudioDeviceOperation,
    ],
  );

  useEffect(() => {
    restoreAudioDevicePreferencesRef.current = restoreAudioDevicePreferences;
  }, [restoreAudioDevicePreferences]);

  const refreshAudioDevices = useCallback(
    () => reconcileAudioDevices("manual"),
    [reconcileAudioDevices],
  );

  const selectInputDevice = useCallback(
    (deviceId: string) => {
      const initiatingPhone = softphoneRef.current;
      const initiatingOwnership = isBrowserPhoneOwner();
      const initiatingAudioGeneration = audioDeviceGenerationRef.current;
      const initiatingActivationGeneration = activationGenerationRef.current;
      const initiatingScopeGeneration =
        audioPreferenceScopeGenerationRef.current;
      const initiatingScope = audioPreferencesScopeRef.current;
      if (!initiatingOwnership || !initiatingPhone) {
        return Promise.reject(
          browserAudioError("AUDIO_PREFERENCES_MICROPHONE_SWITCH_FAILED"),
        );
      }
      return runSerializedAudioDeviceOperation(async () => {
        const authorityIsCurrent =
          mountedRef.current &&
          initiatingOwnership &&
          isBrowserPhoneOwner() &&
          softphoneRef.current === initiatingPhone &&
          audioDeviceGenerationRef.current === initiatingAudioGeneration &&
          activationGenerationRef.current === initiatingActivationGeneration &&
          audioPreferenceScopeGenerationRef.current ===
            initiatingScopeGeneration &&
          audioPreferencesScopeRef.current === initiatingScope;
        if (!authorityIsCurrent) {
          throw browserAudioError("AUDIO_PREFERENCES_OPERATION_SUPERSEDED");
        }
        const generation = ++audioDeviceGenerationRef.current;
        try {
          await initiatingPhone.setInputDevice(deviceId);
          if (
            !mountedRef.current ||
            !isBrowserPhoneOwner() ||
            audioDeviceGenerationRef.current !== generation ||
            activationGenerationRef.current !==
              initiatingActivationGeneration ||
            audioPreferenceScopeGenerationRef.current !==
              initiatingScopeGeneration ||
            audioPreferencesScopeRef.current !== initiatingScope ||
            softphoneRef.current !== initiatingPhone
          )
            return;
          const selected = initiatingPhone.getSelectedAudioDeviceIds();
          const persistedOutputDeviceId =
            pendingOutputPreferenceRef.current || selected.outputDeviceId;
          const selectedOutputDeviceId =
            initiatingPhone.supportsAudioOutputSelection()
              ? selected.outputDeviceId
              : persistedOutputDeviceId;
          setSelectedInputDeviceId(selected.inputDeviceId);
          setSelectedOutputDeviceId(selectedOutputDeviceId);
          if (
            !persistAudioDevicePreferences(
              initiatingScope,
              selected.inputDeviceId,
              persistedOutputDeviceId,
            )
          ) {
            setAudioPreferenceWarning(
              browserAudioErrorMessages.AUDIO_PREFERENCES_STORAGE_UNAVAILABLE,
            );
          } else {
            setAudioPreferenceWarning(null);
          }
        } catch (error) {
          if (
            hasBrowserAudioErrorCode(
              error,
              "AUDIO_PREFERENCES_OPERATION_SUPERSEDED",
            )
          ) {
            throw error;
          }
          logger.warn("sparkone.browser_voice.audio_device_selection_failed", {
            deviceKind: "input",
            failureCategory: "device_selection_failed",
          });
          throw browserAudioError("AUDIO_PREFERENCES_MICROPHONE_SWITCH_FAILED");
        }
      });
    },
    [
      isBrowserPhoneOwner,
      persistAudioDevicePreferences,
      runSerializedAudioDeviceOperation,
    ],
  );

  const selectOutputDevice = useCallback(
    (deviceId: string) => {
      const initiatingPhone = softphoneRef.current;
      const initiatingOwnership = isBrowserPhoneOwner();
      const initiatingAudioGeneration = audioDeviceGenerationRef.current;
      const initiatingActivationGeneration = activationGenerationRef.current;
      const initiatingScopeGeneration =
        audioPreferenceScopeGenerationRef.current;
      const initiatingScope = audioPreferencesScopeRef.current;
      if (!initiatingOwnership || !initiatingPhone) {
        return Promise.reject(
          browserAudioError("AUDIO_PREFERENCES_SPEAKER_SWITCH_FAILED"),
        );
      }
      return runSerializedAudioDeviceOperation(async () => {
        const authorityIsCurrent =
          mountedRef.current &&
          initiatingOwnership &&
          isBrowserPhoneOwner() &&
          softphoneRef.current === initiatingPhone &&
          audioDeviceGenerationRef.current === initiatingAudioGeneration &&
          activationGenerationRef.current === initiatingActivationGeneration &&
          audioPreferenceScopeGenerationRef.current ===
            initiatingScopeGeneration &&
          audioPreferencesScopeRef.current === initiatingScope;
        if (!authorityIsCurrent) {
          throw browserAudioError("AUDIO_PREFERENCES_OPERATION_SUPERSEDED");
        }
        const generation = ++audioDeviceGenerationRef.current;
        try {
          await initiatingPhone.setOutputDevice(deviceId);
          if (
            !mountedRef.current ||
            !isBrowserPhoneOwner() ||
            audioDeviceGenerationRef.current !== generation ||
            activationGenerationRef.current !==
              initiatingActivationGeneration ||
            audioPreferenceScopeGenerationRef.current !==
              initiatingScopeGeneration ||
            audioPreferencesScopeRef.current !== initiatingScope ||
            softphoneRef.current !== initiatingPhone
          )
            return;
          const selected = initiatingPhone.getSelectedAudioDeviceIds();
          pendingOutputPreferenceRef.current = "";
          setSelectedInputDeviceId(selected.inputDeviceId);
          setSelectedOutputDeviceId(selected.outputDeviceId);
          if (
            !persistAudioDevicePreferences(
              initiatingScope,
              selected.inputDeviceId,
              selected.outputDeviceId,
            )
          ) {
            setAudioPreferenceWarning(
              browserAudioErrorMessages.AUDIO_PREFERENCES_STORAGE_UNAVAILABLE,
            );
          } else {
            setAudioPreferenceWarning(null);
          }
        } catch (error) {
          if (
            hasBrowserAudioErrorCode(
              error,
              "AUDIO_PREFERENCES_OPERATION_SUPERSEDED",
            )
          ) {
            throw error;
          }
          logger.warn("sparkone.browser_voice.audio_device_selection_failed", {
            deviceKind: "output",
            failureCategory: "device_selection_failed",
          });
          throw browserAudioError("AUDIO_PREFERENCES_SPEAKER_SWITCH_FAILED");
        }
      });
    },
    [
      isBrowserPhoneOwner,
      persistAudioDevicePreferences,
      runSerializedAudioDeviceOperation,
    ],
  );

  useEffect(() => {
    if (!ownsBrowserPhone || typeof navigator === "undefined") return;
    const mediaDevices = navigator.mediaDevices;
    if (!mediaDevices?.addEventListener) return;
    const onDeviceChange = () => {
      void reconcileAudioDevices("devicechange");
    };
    mediaDevices.addEventListener("devicechange", onDeviceChange);
    return () =>
      mediaDevices.removeEventListener("devicechange", onDeviceChange);
  }, [ownsBrowserPhone, reconcileAudioDevices]);

  const retryAudio = useCallback(async () => {
    const phone = softphoneRef.current;
    if (!phone)
      throw browserAudioError("AUDIO_PREFERENCES_RUNTIME_UNAVAILABLE");
    updatePublicState({
      mediaState: "connecting",
      message: "Connecting browser audio…",
    });
    await phone.retryRemoteAudio();
  }, [isBrowserPhoneOwner, updatePublicState]);

  const activate = useCallback(
    async (
      identity: Omit<
        ActiveBrowserSession,
        | "sessionId"
        | "authorityRevoked"
        | "releaseAuthNavigationHold"
        | "registrationStartedAt"
        | "registrationReadyObserved"
        | "expiresAtMs"
        | "mediaCredentialsExpiresAtMs"
        | "mediaCredentialsRefreshLeadMs"
        | "mediaCredentialsJitMinimumMs"
        | "mediaCredentialsRefresh"
        | "mediaCredentialsBlocked"
        | "pendingRenewal"
        | "pendingInstallationTarget"
        | "pendingRotationKind"
        | "renewalStartedAt"
        | "replacementPending"
        | "credentials"
        | "pendingReplacementIdentity"
        | "pendingBrowserOriginatedCall"
        | "installationId"
        | "installationGeneration"
        | "installationStateVersion"
        | "activationLease"
        | "revocationActivationProof"
        | "authorityCleanupPromise"
        | "localRegistrationState"
        | "providerRegistrationState"
        | "localRegistrationEpisodes"
      >,
      browserSession: VoiceBrowserSession,
      options: {
        scheduleLegacyRenewal?: boolean;
        activationProof?: VoiceBrowserActivationProof;
      } = {},
    ) => {
      if (!authEligibleRef.current) {
        throw new Error("Browser calling authority is unavailable.");
      }
      if (
        softphoneRef.current ||
        sessionRef.current ||
        activationInFlightRef.current !== null
      ) {
        throw new Error(
          "Browser phone activation requires the existing registration to be released first.",
        );
      }

      const generation = ++activationGenerationRef.current;
      activationInFlightRef.current = generation;
      const registrationIdentity: BrowserRuntimeSessionIdentity = {
        connectorId: identity.connectorId,
        sessionId: browserSession.sessionId,
      };
      const phone = new SipSoftphone(
        (event) => handleSipStatus(phone, event),
        handleSipDiagnostic,
      );
      const initialMediaPolicy = browserSessionMediaPolicy(browserSession);
      const activatedSession: ActiveBrowserSession = {
        ...identity,
        sessionId: browserSession.sessionId,
        authorityRevoked: false,
        releaseAuthNavigationHold: null,
        registrationStartedAt: Date.now(),
        registrationReadyObserved: false,
        expiresAtMs: sessionExpiryMs(browserSession),
        mediaCredentialsExpiresAtMs: initialMediaPolicy.expiresAtMs,
        mediaCredentialsRefreshLeadMs: initialMediaPolicy.refreshLeadMs,
        mediaCredentialsJitMinimumMs: initialMediaPolicy.jitMinimumMs,
        mediaCredentialsRefresh: null,
        mediaCredentialsBlocked: false,
        pendingRenewal: null,
        pendingInstallationTarget: null,
        pendingRotationKind: null,
        renewalStartedAt: null,
        replacementPending: false,
        credentials: browserSession,
        pendingReplacementIdentity: null,
        pendingBrowserOriginatedCall: null,
        installationId: null,
        installationGeneration: null,
        installationStateVersion: null,
        activationLease: null,
        revocationActivationProof: options.activationProof ?? null,
        authorityCleanupPromise: null,
        localRegistrationState: "connecting",
        providerRegistrationState: "issued",
        localRegistrationEpisodes: new Map(),
      };
      const isCurrentActivation = () =>
        mountedRef.current &&
        authEligibleRef.current &&
        generation === activationGenerationRef.current &&
        activationInFlightRef.current === generation &&
        isBrowserPhoneOwner() &&
        softphoneRef.current === phone &&
        sessionRef.current === activatedSession;

      // Install exact local authority before JsSIP starts. JsSIP may emit a
      // registered callback synchronously from start(), and that callback must
      // see the current phone/session rather than being discarded as stale.
      softphoneRef.current = phone;
      sessionRef.current = activatedSession;
      updatePublicState({
        lifecycle: "registering",
        localRegistrationState: "connecting",
        providerRegistrationState: "issued",
        message: "Connecting Browser phone…",
      });

      try {
        logger.info("sparkone.browser_voice.ua_start", {
          registrationState: "registering",
          sessionKind: activatedSession.persistent ? "persistent" : "ephemeral",
        });
        emitRuntimeVoiceEvent({
          eventName: "browser_voice.registration.ua_start_invoked",
          severity: "INFO",
          phase: "registration",
          outcome: "started",
          registrationState: "registering",
        });
        await phone.start(browserSession);
        if (!isCurrentActivation()) {
          throw new Error("Browser call preparation was cancelled.");
        }

        const outputSelectionSupported = phone.supportsAudioOutputSelection();
        if (!isCurrentActivation()) {
          throw new Error("Browser call preparation was cancelled.");
        }

        setSupportsOutputSelection(outputSelectionSupported);
        if (options.scheduleLegacyRenewal !== false) schedule(browserSession);
        scheduleMediaCredentialsRefreshRef.current(activatedSession, phone);

        // Preference restore is owner-only and generation-fenced. It never
        // blocks registration, and reconciliation runs after restore settles.
        void restoreAudioDevicePreferences(phone)
          .then(() => reconcileAudioDevices("activation"))
          .catch(() => {
            logger.warn("sparkone.browser_voice.audio_restore_cleanup_failed", {
              failureCategory: "local_cleanup_failed",
            });
          });
      } catch (error) {
        const activationCancelled = !isCurrentActivation();
        const ownsInstalledRuntime =
          softphoneRef.current === phone &&
          sessionRef.current === activatedSession;
        if (ownsInstalledRuntime) {
          softphoneRef.current = null;
          sessionRef.current = null;
          stopPhone(phone);
        }
        // A cancelled activation commonly belongs to a React development
        // remount or same-tab handoff. The authoritative installation may have
        // already handed the same credential response to the surviving owner,
        // so the stale persistent consumer must never revoke it. One-shot call
        // credentials are not shared and still receive compensating revocation.
        // Genuine startup failures while this activation still owns authority
        // remain compensating.
        if (!activationCancelled || !activatedSession.persistent) {
          await revokeRegistration(
            registrationIdentity,
            options.activationProof,
          );
        }
        if (activationCancelled) {
          throw new Error("Browser call preparation was cancelled.");
        }
        throw error;
      } finally {
        if (activationInFlightRef.current === generation) {
          activationInFlightRef.current = null;
        }
      }
    },
    [
      emitRuntimeVoiceEvent,
      handleSipDiagnostic,
      handleSipStatus,
      isBrowserPhoneOwner,
      reconcileAudioDevices,
      restoreAudioDevicePreferences,
      revokeRegistration,
      schedule,
      stopPhone,
      updatePublicState,
    ],
  );

  const bootstrapPersistentOperation = useCallback(async () => {
    if (
      !persistentEnabled ||
      !authEligibleRef.current ||
      !isBrowserPhoneOwner() ||
      sessionRef.current ||
      bootstrapRunningRef.current
    )
      return;
    if (persistentDenialRef.current) {
      updatePublicState({
        lifecycle: "unavailable",
        message: persistentDenialRef.current.message,
        dialogs: [],
      });
      return;
    }
    bootstrapRequestedRef.current = false;
    bootstrapRunningRef.current = true;
    const generation = ++bootstrapGenerationRef.current;
    browserReadinessTimingRef.current = {
      bootstrapGeneration: generation,
      startedAt: Date.now(),
      emitted: new Set(),
    };
    emitBrowserReadinessTiming("browser_voice.registration.ensure_started", {
      outcome: "started",
      registrationState: "registering",
      attempt: 1,
    });
    updatePublicState({
      lifecycle: "preparing",
      message: "Preparing browser phone…",
      dialogs: [],
    });
    try {
      if (USE_AUTHORITATIVE_BROWSER_INSTALLATIONS) {
        // A 202 is a successful asynchronous lifecycle response, not an error.
        // The first loop iteration consumes it and waits for an authoritative
        // retry_ensure projection. Repeated wait projections cannot issue PUTs.
        let ready: Extract<
          Awaited<ReturnType<typeof ensureTabBrowserInstallation>>,
          { outcome: "ready" }
        > | null = null;
        while (!ready) {
          const ensured = await ensureTabBrowserInstallation();
          if (!isBrowserInstallationPreparing(ensured)) {
            ready = ensured;
            break;
          }

          updatePublicState({
            lifecycle: "degraded",
            message:
              "Browser phone is reconnecting after the page reload. Please wait…",
            dialogs: [],
          });
          const preparationAbort = new AbortController();
          preparationAbortRef.current = preparationAbort;
          let retryAuthorization: Awaited<
            ReturnType<
              BrowserPreparationCoordinator["waitUntilRetryAuthorized"]
            >
          >;
          try {
            retryAuthorization =
              await preparationCoordinatorRef.current!.waitUntilRetryAuthorized(
                ensured,
                preparationAbort.signal,
              );
          } finally {
            if (preparationAbortRef.current === preparationAbort) {
              preparationAbortRef.current = null;
            }
          }
          if (retryAuthorization.action === "terminal") {
            updatePublicState({
              lifecycle: "unavailable",
              message:
                "Browser phone registration is no longer available. Please try again later.",
              dialogs: [],
            });
            return;
          }
          if (
            !mountedRef.current ||
            !authEligibleRef.current ||
            generation !== bootstrapGenerationRef.current ||
            !persistentEnabledRef.current ||
            !isBrowserPhoneOwner() ||
            preparationAbort.signal.aborted
          ) {
            return;
          }
          // This is the sole ensure PUT for the exact projection token above.
          // If it returns another 202, the next PUT must earn a new GET token.
          logger.info("sparkone.browser_voice.preparation_retry_authorized", {
            installationId: retryAuthorization.installationId,
            stateVersion: retryAuthorization.stateVersion,
            preparationGeneration: retryAuthorization.preparationGeneration,
          });
          emitBrowserReadinessTiming(
            "browser_voice.registration.retry_authorized",
            { attempt: 1 },
          );
        }

        const prepared = ready;
        if (!prepared.browserSession) {
          // A normal reload reclaimed the exact live installation. Spark VP
          // deliberately does not redeliver one-time credentials, so do not
          // rotate the tab identity or create a successor as a workaround.
          logger.warn(
            "sparkone.browser_voice.reload_reclaimed_without_credentials",
            {
              installationId: prepared.installationId,
              generation: prepared.generation,
              sessionId: prepared.sessionId,
            },
          );
          updatePublicState({
            lifecycle: "unavailable",
            message:
              "Browser phone registration is already active for this tab and cannot be safely resumed until the current lease ends.",
          });
          return;
        }
        emitBrowserReadinessTiming(
          "browser_voice.registration.credentials_delivered",
          { registrationState: "registering" },
        );
        if (!prepared.extensionId || !prepared.endpointDeviceId) {
          throw new Error(
            "Browser installation endpoint identity was not delivered for this activation.",
          );
        }
        authoritativeRegistrationSessionIdsRef.current.add(prepared.sessionId);
        const target = browserInstallationCredentialTarget(prepared);
        if (!target) {
          throw new Error(
            "Browser installation credential identity was not delivered.",
          );
        }
        // Register the exact waiter before JsSIP receives the credential. A
        // racing event is cached by the coordinator and cannot be missed.
        const authority = registrationCoordinatorRef.current!.waitForRegistered(
          target,
          `startup:${prepared.installationId}:${prepared.generation}`,
        );
        // A provider unmount can dispose the coordinator before activate()
        // reaches the await below. Attach a rejection consumer immediately so
        // cancellation never escapes as an unhandled promise rejection.
        void authority.catch(() => undefined);
        if (
          !mountedRef.current ||
          !authEligibleRef.current ||
          generation !== bootstrapGenerationRef.current ||
          !persistentEnabledRef.current
        )
          return;
        await activate(
          {
            connectorId: prepared.connectorId,
            clientUserRef: prepared.clientUserRef,
            callSessionId: null,
            persistent: true,
            extensionId: prepared.extensionId,
            endpointDeviceId: prepared.endpointDeviceId,
          },
          prepared.browserSession,
          { scheduleLegacyRenewal: false },
        );
        const activated = readActiveBrowserSession(sessionRef);
        if (!activated) {
          throw new Error("Browser installation activation was cancelled.");
        }
        activated.installationId = prepared.installationId;
        activated.installationGeneration = target.generation;
        activated.installationStateVersion = prepared.stateVersion;
        activated.activationLease = prepared.activationLease;
        submitLocalRegistrationObservation(activated, activated.sessionId);
        scheduleActivationLeaseHeartbeatRef.current(activated);
        const authorityObservation = await authority;
        if (
          readActiveBrowserSession(sessionRef) !== activated ||
          activated.authorityRevoked
        )
          return;
        emitBrowserReadinessTiming(
          "browser_voice.registration.authoritative_ready",
          {
            registrationState: "registered",
            authorityPath: authorityObservation.authorityPath,
          },
        );
        activated.registrationReadyObserved = true;
        activated.providerRegistrationState = "registered";
        cancelBrowserRuntimeTimer(localConfirmationWarningRef);
        authoritativeRegistrationSessionIdsRef.current.clear();
        authoritativeRegistrationSessionIdsRef.current.add(activated.sessionId);
        const exposesReady =
          activated.localRegistrationState === "local_registered";
        updatePublicState({
          lifecycle: exposesReady ? "ready" : "registering",
          localRegistrationState: activated.localRegistrationState,
          providerRegistrationState: "registered",
          message: exposesReady
            ? "Browser phone ready."
            : "Connecting Browser phone…",
        });
        if (exposesReady) {
          emitBrowserReadinessTiming(
            "browser_voice.registration.public_ready",
            { registrationState: "registered" },
          );
        }
        logger.info("sparkone.browser_voice.registration_ready", {
          latencyMs: boundedLatency(activated.registrationStartedAt),
          observationSource: authorityObservation.authorityPath,
        });
        scheduleInstallationRef.current(activated);
      } else {
        const prepared = await voiceApi.preparePersistentBrowserSession({
          browserDeviceInstanceId: getBrowserDeviceInstanceId(),
          activationProof: activeActivationProof(),
        });
        if (
          !mountedRef.current ||
          !authEligibleRef.current ||
          generation !== bootstrapGenerationRef.current ||
          !persistentEnabledRef.current ||
          !isBrowserPhoneOwner()
        ) {
          // The persistent credential may already belong to the surviving
          // activation. A stale bootstrap must never revoke shared authority.
          return;
        }
        await activate(
          {
            connectorId: prepared.connectorId,
            clientUserRef: prepared.clientUserRef,
            callSessionId: null,
            persistent: true,
            extensionId: prepared.extensionId,
            endpointDeviceId: prepared.endpointDeviceId,
          },
          prepared.browserSession,
        );
        if (prepared.renewalPredecessorSessionId) {
          const activated = readActiveBrowserSession(sessionRef);
          const isCurrentSuccessor = () =>
            mountedRef.current &&
            authEligibleRef.current &&
            generation === bootstrapGenerationRef.current &&
            persistentEnabledRef.current &&
            isBrowserPhoneOwner() &&
            readActiveBrowserSession(sessionRef) === activated &&
            activated?.sessionId === prepared.browserSession.sessionId &&
            !activated.authorityRevoked;
          await waitForAuthoritativeRegistration(
            prepared.browserSession.sessionId,
            isCurrentSuccessor,
          );
          const finalized = await voiceApi.finalizeBrowserSessionRenewal(
            prepared.renewalPredecessorSessionId,
            activeActivationProof(activated ?? null),
          );
          if (!isCurrentSuccessor()) return;
          if (
            finalized.sessionId !== prepared.browserSession.sessionId ||
            finalized.status !== "registered"
          ) {
            throw new Error("Browser renewal cutover is not registered yet.");
          }
          if (activated && !activated.registrationReadyObserved) {
            activated.registrationReadyObserved = true;
            registrationPollAttemptsRef.current = 0;
            updatePublicState({
              lifecycle: "ready",
              message: "Browser phone ready.",
            });
          }
        }
      }
      bootstrapAttemptRef.current = 0;
      cancelBrowserRuntimeTimer(bootstrapRetryRef);
    } catch (error) {
      if (generation !== bootstrapGenerationRef.current) return;
      if (USE_AUTHORITATIVE_BROWSER_INSTALLATIONS && !sessionRef.current) {
        authoritativeRegistrationSessionIdsRef.current.clear();
      }
      const priorBootstrapAttempt = bootstrapAttemptRef.current;
      const preservesPersistentRegistration = Boolean(
        USE_AUTHORITATIVE_BROWSER_INSTALLATIONS &&
        readActiveBrowserSession(sessionRef)?.persistent &&
        softphoneRef.current,
      );
      if (preservesPersistentRegistration) {
        // The exact SIP session may already be registered even when the CRM-side
        // projection/recovery read has not converged yet. Never turn that
        // inconclusive authority wait into a durable installation DELETE: doing
        // so retires the VP endpoint and guarantees all later bootstraps fail.
        // Keep the UA alive and let the provider session-status poll establish
        // readiness or prove a terminal outcome for this exact session.
        const current = sessionRef.current!;
        authoritativeRegistrationSessionIdsRef.current.clear();
        authoritativeRegistrationSessionIdsRef.current.add(current.sessionId);
        void confirmRegistrationRef.current();
      } else if (softphoneRef.current) {
        await release();
      }
      // A partially started phone is fully released above, but that cleanup
      // must not reset the retry history for this continuing bootstrap loop.
      bootstrapAttemptRef.current = priorBootstrapAttempt;
      const installationLimit =
        error instanceof VoiceRequestRejectedError &&
        error.code === browserInstallationLimitCode;
      // The server-side activation collision is the hard backstop for multi-tab
      // races: another tab holds this installation's live authority. Surface it
      // as the dormant "calling active in another tab" state rather than a hard
      // failure, and do not retry — leadership will hand off via the lock.
      const activationCollision =
        error instanceof VoiceRequestRejectedError &&
        error.code === browserActivationCollisionCode;
      const preparationInProgress =
        error instanceof VoiceRequestRejectedError &&
        error.code === browserPreparationInProgressCode;
      const retryable =
        !installationLimit &&
        !activationCollision &&
        (preparationInProgress || retryableAvailabilityFailure(error));
      if (isStickyPersistentDenial(error)) {
        persistentDenialRef.current = {
          readinessVersion: readinessVersionRef.current,
          reasonCodes: [...error.reasonCodes],
          message: stickyDenialMessage(error),
        };
      } else if (installationLimit) {
        persistentDenialRef.current = {
          readinessVersion: readinessVersionRef.current,
          reasonCodes: [],
          message:
            "Browser phone is already active in the maximum number of tabs.",
        };
      }
      updatePublicState({
        lifecycle: retryable
          ? "degraded"
          : installationLimit ||
              activationCollision ||
              definitiveAvailabilityFailure(error)
            ? "unavailable"
            : "failed",
        message: installationLimit
          ? "Browser phone is already active in the maximum number of tabs."
          : activationCollision
            ? "Calling is active in another tab. This tab is standing by."
            : preparationInProgress
              ? "Browser phone is reconnecting after the page reload. Retrying…"
              : retryable
                ? "Browser phone preparation is temporarily unavailable. Retrying…"
                : isStickyPersistentDenial(error)
                  ? stickyDenialMessage(error)
                  : error instanceof Error
                    ? error.message
                    : "Could not prepare browser calling.",
      });
      if (
        retryable &&
        !preservesPersistentRegistration &&
        mountedRef.current &&
        authEligibleRef.current &&
        persistentEnabled &&
        isBrowserPhoneOwner()
      ) {
        bootstrapAttemptRef.current += 1;
        const retryDelay = boundedRetryDelay(bootstrapAttemptRef.current);
        logger.warn("sparkone.browser_voice.bootstrap_retry_scheduled", {
          attempt: bootstrapAttemptRef.current,
          retryDelayMs: retryDelay,
        });
        replaceBrowserRuntimeTimer(
          bootstrapRetryRef,
          () => {
            void bootstrapRef.current();
          },
          retryDelay,
        );
      }
    } finally {
      bootstrapRunningRef.current = false;
      if (
        bootstrapRequestedRef.current &&
        mountedRef.current &&
        authEligibleRef.current &&
        persistentEnabledRef.current &&
        isBrowserPhoneOwner() &&
        !sessionRef.current &&
        !persistentDenialRef.current &&
        bootstrapRetryRef.current === null
      ) {
        replaceBrowserRuntimeTimer(
          bootstrapRetryRef,
          () => {
            void bootstrapRef.current();
          },
          0,
        );
      }
    }
  }, [
    activate,
    emitBrowserReadinessTiming,
    isBrowserPhoneOwner,
    persistentEnabled,
    release,
    revokeRegistration,
    updatePublicState,
  ]);
  const bootstrapPersistent = useCallback(
    () => runSerializedTransition(bootstrapPersistentOperation),
    [bootstrapPersistentOperation, runSerializedTransition],
  );
  useEffect(() => {
    bootstrapRef.current = bootstrapPersistent;
  }, [bootstrapPersistent]);

  const observeReadiness = useCallback(
    (capability: BrowserCallingCapability) => {
      const readinessVersion = capability.readinessVersion;
      readinessVersionRef.current = readinessVersion;
      const denial = persistentDenialRef.current;
      if (!denial) return;
      const eligible =
        capability.startMode === "IMMEDIATE" ||
        capability.startMode === "BROWSER_PREPARATION";
      if (denial.readinessVersion === null) {
        if (!eligible || readinessVersion === null) return;
        persistentDenialRef.current = null;
        bootstrapRequestedRef.current = true;
        void bootstrapRef.current();
        return;
      }
      if (
        !eligible ||
        readinessVersion === null ||
        readinessVersion <= denial.readinessVersion
      )
        return;
      persistentDenialRef.current = null;
      bootstrapRequestedRef.current = true;
      void bootstrapRef.current();
    },
    [],
  );

  const retryRegistration = useCallback(async () => {
    persistentDenialRef.current = null;
    const current = sessionRef.current;
    const phone = softphoneRef.current;
    if (current && phone && !current.authorityRevoked) {
      phone.recoverRegistration();
      if (!current.installationId) void confirmRegistrationRef.current();
      return;
    }
    bootstrapRequestedRef.current = true;
    await bootstrapRef.current();
  }, []);

  const ensureFreshMediaCredentials = useCallback(
    async (
      current: ActiveBrowserSession,
      phone: SipSoftphone,
      minimumRemainingMs: number,
      forNewMedia: boolean,
    ): Promise<void> => {
      const hasFreshMediaCredentials = () =>
        !current.mediaCredentialsBlocked &&
        current.mediaCredentialsExpiresAtMs !== null &&
        current.mediaCredentialsExpiresAtMs - Date.now() > minimumRemainingMs;
      const rejectNewMediaOperation = () => {
        current.mediaCredentialsBlocked = true;
        updatePublicState({
          mediaState: "blocked",
          message:
            "Browser media credentials could not be refreshed. New calls are unavailable; active calls continue.",
        });
        throw new Error(
          "Browser media credentials could not be refreshed. Try again.",
        );
      };

      if (hasFreshMediaCredentials()) return;
      if (!forNewMedia && current.pendingRenewal) return;
      if (current.mediaCredentialsRefresh) {
        await current.mediaCredentialsRefresh;
        // Background refresh failures are deliberately absorbed so they cannot
        // disrupt SIP or a connected dialog. A strict caller must nevertheless
        // re-check the shared result before creating a new media operation.
        if (hasFreshMediaCredentials()) return;
        if (forNewMedia) rejectNewMediaOperation();
        return;
      }

      const refreshOperation = async () => {
        try {
          const media = await voiceApi.refreshBrowserMediaCredentials(
            current.sessionId,
            activeActivationProof(current),
          );
          if (
            sessionRef.current !== current ||
            softphoneRef.current !== phone ||
            current.authorityRevoked ||
            media.sessionId !== current.sessionId ||
            !phone.updateMediaCredentials(media.sessionId, media.iceServers)
          ) {
            throw new Error(
              "Browser media credential refresh lost session authority.",
            );
          }
          const expiresAtMs = Date.parse(media.expiresAt);
          if (!Number.isFinite(expiresAtMs)) {
            throw new Error(
              "Browser media credential refresh returned an invalid expiry.",
            );
          }
          current.credentials = {
            ...current.credentials,
            iceServers: media.iceServers,
            mediaCredentialsExpiresAt: media.expiresAt,
            mediaCredentialsIssuedAt: media.issuedAt,
            mediaCredentialsTtlSeconds: media.ttlSeconds,
          };
          const refreshPolicy = mediaCredentialRefreshPolicy({
            issuedAtMs: Date.parse(media.issuedAt),
            expiresAtMs,
            jitter: Math.random() * 0.2 - 0.1,
          });
          current.mediaCredentialsExpiresAtMs = expiresAtMs;
          current.mediaCredentialsRefreshLeadMs = refreshPolicy.refreshLeadMs;
          current.mediaCredentialsJitMinimumMs = refreshPolicy.jitMinimumMs;
          if (current.mediaCredentialsBlocked) {
            current.mediaCredentialsBlocked = false;
            updatePublicState({ mediaState: "idle", message: undefined });
          }
        } catch (error) {
          logger.warn(
            "sparkone.browser_voice.media_credentials_refresh_failed",
            {
              failureCategory:
                error instanceof VoiceRequestRejectedError
                  ? "request_rejected"
                  : "request_failed",
              operation: forNewMedia ? "new_media" : "background",
            },
          );
          current.mediaCredentialsBlocked = true;
          if (forNewMedia) rejectNewMediaOperation();
          updatePublicState({
            mediaState: "blocked",
            message:
              "Browser media credentials need refresh. New calls will retry before connecting.",
          });
        }
      };
      const refresh = refreshOperation().finally(() => {
        if (current.mediaCredentialsRefresh === refresh) {
          current.mediaCredentialsRefresh = null;
        }
      });
      current.mediaCredentialsRefresh = refresh;
      return refresh;
    },
    [updatePublicState],
  );

  const scheduleMediaCredentialsRefresh = useCallback(
    (current: ActiveBrowserSession, phone: SipSoftphone, delay?: number) => {
      const untilLead =
        current.mediaCredentialsExpiresAtMs === null
          ? 0
          : Math.max(
              1_000,
              current.mediaCredentialsExpiresAtMs -
                Date.now() -
                current.mediaCredentialsRefreshLeadMs,
            );
      replaceBrowserRuntimeTimer(
        mediaCredentialsRefreshRef,
        () => {
          if (
            sessionRef.current !== current ||
            softphoneRef.current !== phone ||
            current.authorityRevoked
          ) {
            return;
          }
          if (current.pendingRenewal) {
            scheduleMediaCredentialsRefreshRef.current(
              current,
              phone,
              MEDIA_CREDENTIAL_REFRESH_ROTATION_DEFER_MS,
            );
            return;
          }
          void ensureFreshMediaCredentials(
            current,
            phone,
            current.mediaCredentialsRefreshLeadMs,
            false,
          ).finally(() => {
            if (
              sessionRef.current !== current ||
              softphoneRef.current !== phone
            ) {
              return;
            }
            const refreshStillDue =
              current.mediaCredentialsExpiresAtMs === null ||
              current.mediaCredentialsExpiresAtMs - Date.now() <=
                current.mediaCredentialsRefreshLeadMs;
            scheduleMediaCredentialsRefreshRef.current(
              current,
              phone,
              refreshStillDue ? MEDIA_CREDENTIAL_REFRESH_RETRY_MS : undefined,
            );
          });
        },
        Math.min(delay ?? untilLead, MAX_BROWSER_TIMER_DELAY_MS),
      );
    },
    [ensureFreshMediaCredentials],
  );
  scheduleMediaCredentialsRefreshRef.current = scheduleMediaCredentialsRefresh;

  const answerAsOwner = useCallback(
    async (dialogId: string) => {
      const phone = softphoneRef.current;
      const dialog = phone
        ?.getDialogs()
        .find((candidate) => candidate.dialogId === dialogId);
      const target = canonicalDialogTargetsRef.current.get(dialogId) ?? null;
      const current = sessionRef.current;
      const exactLiveAuthorization = Boolean(
        phone &&
        dialog &&
        target?.answerAuthorized &&
        current &&
        !current.authorityRevoked &&
        dialog.direction === "incoming" &&
        dialog.state === "ringing" &&
        validSipCorrelation(dialog.sipCorrelationId) &&
        dialog.sipCorrelationId === target.sipCorrelationId &&
        current.connectorId === target.connectorId &&
        current.endpointDeviceId === target.endpointDeviceId &&
        (!current.callSessionId ||
          current.callSessionId === target.callSessionId),
      );
      if (!exactLiveAuthorization || !phone || !dialog || !current) {
        logger.warn("sparkone.browser_voice.answer_authorization_rejected", {
          reason: "exact_dialog_not_authorized",
        });
        throw new Error("This incoming call is still being verified.");
      }
      setCappedMap(pendingCanonicalActionsRef.current, dialogId, {
        action: "answer",
        startedAt: Date.now(),
        sipCorrelationId: dialog.sipCorrelationId,
      });
      try {
        await ensureFreshMediaCredentials(
          current,
          phone,
          current.mediaCredentialsJitMinimumMs,
          true,
        );
        await phone.answerDialog(dialogId);
      } catch (error) {
        pendingCanonicalActionsRef.current.delete(dialogId);
        throw error;
      }
    },
    [ensureFreshMediaCredentials],
  );

  const answerIncoming = useCallback(
    async (dialogId: string) => {
      await answerAsOwner(dialogId);
    },
    [answerAsOwner, isBrowserPhoneOwner],
  );

  const rejectAsOwner = useCallback(
    async (dialogId: string) => {
      const phone = softphoneRef.current;
      const dialog = phone
        ?.getDialogs()
        .find((candidate) => candidate.dialogId === dialogId);
      if (!phone || !dialog) return;
      const target = canonicalDialogTargetsRef.current.get(dialogId) ?? null;
      setCappedMap(pendingCanonicalActionsRef.current, dialogId, {
        action: "reject",
        startedAt: Date.now(),
        sipCorrelationId: dialog?.sipCorrelationId ?? null,
      });
      phone.rejectDialog(dialogId);
      // Local decline is deliberately available before provider enrichment.
      // It only tears down this Browser dialog. The authoritative provider
      // reject command is sent only when that exact device leg permits it.
      if (!target?.rejectAuthorized) return;
      try {
        await voiceApi.rejectIncomingCall({
          connectorId: target.connectorId,
          callSessionId: target.callSessionId,
          participantId: target.participantId,
          endpointDeviceId: target.endpointDeviceId,
        });
        logger.info("sparkone.browser_voice.reject_submitted", {
          correlated: true,
          providerConfirmed: true,
        });
      } catch (error) {
        updatePublicState({
          lifecycle: "degraded",
          message:
            "The local call was declined; provider synchronization is recovering.",
          notice: {
            kind: "recovery_degraded",
            message:
              "The local call was declined, but other phones may continue ringing while call state recovers.",
            observedAt: Date.now(),
          },
        });
        logger.warn("sparkone.browser_voice.reject_sync_failed", {
          correlated: true,
          failureCategory:
            error instanceof VoiceRequestRejectedError
              ? "request_rejected"
              : "request_failed",
        });
      }
    },
    [updatePublicState],
  );

  const rejectIncoming = useCallback(
    async (dialogId: string) => {
      await rejectAsOwner(dialogId);
    },
    [isBrowserPhoneOwner, rejectAsOwner],
  );

  const setMuted = useCallback(
    (callSessionId: string, muted: boolean) => {
      const current = sessionRef.current;
      const phone = softphoneRef.current;
      if (current?.callSessionId !== callSessionId || !phone) {
        throw new Error("Browser microphone is not available for this call.");
      }
      phone.setMuted(muted);
      updatePublicState({ isMuted: phone.isMuted() });
    },
    [isBrowserPhoneOwner, updatePublicState],
  );

  const start = useCallback(
    async (placement: VoiceBrowserPreparedPlacement) => {
      if (sessionRef.current?.authorityRevoked) {
        throw new Error(
          "Browser registration authority is unavailable until the active call ends.",
        );
      }
      if (isSessionActive(placement.browserSession.sessionId)) return;
      const current = sessionRef.current;
      if (current?.persistent) {
        throw new Error(
          "The persistent Browser registration must become ready before placing a call.",
        );
      }
      if (current) await release();
      setTerminalEvent(null);
      updatePublicState({ ...initialPublicState, lifecycle: "preparing" });
      let installation: Awaited<
        ReturnType<typeof ensureTabBrowserInstallation>
      > | null = null;
      try {
        installation = USE_AUTHORITATIVE_BROWSER_INSTALLATIONS
          ? await ensureTabBrowserInstallation()
          : null;
        if (installation && isBrowserInstallationPreparing(installation)) {
          throw new Error(
            "Browser phone is reconnecting after the page reload.",
          );
        }
        if (
          installation &&
          (!installation.activationLease ||
            installation.activationLease.state !== "ACTIVE")
        ) {
          throw new VoiceRequestRejectedError(
            "This tab does not own an active Browser activation lease.",
            403,
            "voice_browser_session_authority_revoked",
          );
        }
        const activationProof = installation
          ? {
              leaseId: installation.activationLease!.leaseId,
              tabId: getBrowserTabId(),
              reclaimToken: getBrowserActivationId(),
            }
          : undefined;
        await activate(
          {
            connectorId: placement.connectorId,
            clientUserRef: placement.clientUserRef,
            callSessionId: null,
            persistent: false,
            extensionId: placement.extensionId,
            endpointDeviceId: placement.endpointDeviceId,
          },
          placement.browserSession,
          { activationProof },
        );
        const activated = readActiveBrowserSession(sessionRef);
        if (installation && activated) {
          activated.installationId = installation.installationId;
          activated.installationGeneration = installation.generation;
          activated.installationStateVersion = installation.stateVersion;
          activated.activationLease = installation.activationLease;
          scheduleActivationLeaseHeartbeatRef.current(activated);
        }
      } catch (error) {
        if (softphoneRef.current) {
          await release();
        } else if (
          installation &&
          !isBrowserInstallationPreparing(installation) &&
          installation.activationLease?.state === "ACTIVE"
        ) {
          const activationProof = {
            leaseId: installation.activationLease.leaseId,
            tabId: getBrowserTabId(),
            reclaimToken: getBrowserActivationId(),
          };
          await voiceApi
            .releaseBrowserActivationLease(activationProof)
            .then(() => undefined)
            .catch(() => {
              logger.warn(
                "sparkone.browser_voice.activation_lease_release_failed",
                { failureCategory: "request_failed" },
              );
            });
        }
        throw error;
      }
    },
    [
      activate,
      isBrowserPhoneOwner,
      isSessionActive,
      release,
      updatePublicState,
    ],
  );

  const attachCall = useCallback(
    (
      sessionId: string,
      callSessionId: string,
      callerSipCorrelationId?: string | null,
    ) => {
      const current = sessionRef.current;
      if (!current || current.sessionId !== sessionId) {
        throw new Error(
          "The active Browser registration does not match this call.",
        );
      }
      if (current.callSessionId && current.callSessionId !== callSessionId) {
        throw new Error(
          "The active Browser registration is already attached to another call.",
        );
      }
      current.callSessionId = callSessionId;
      const normalizedCallerSipCorrelationId = callerSipCorrelationId ?? null;
      const priorCorrelationHint =
        callerSipCorrelationHintByCallRef.current.get(callSessionId);
      if (
        priorCorrelationHint &&
        !validSipCorrelation(normalizedCallerSipCorrelationId)
      ) {
        callerSipCorrelationHintRef.current = priorCorrelationHint;
        return;
      }
      if (
        validSipCorrelation(normalizedCallerSipCorrelationId) &&
        priorCorrelationHint &&
        normalizedCallerSipCorrelationId !== priorCorrelationHint
      ) {
        const phone = softphoneRef.current;
        if (phone) {
          failClosedCallerCorrelation(
            phone,
            [callSessionId],
            [priorCorrelationHint, normalizedCallerSipCorrelationId],
          );
        }
        logger.warn(
          "sparkone.browser_voice.originate_correlation_hint_mismatch",
          { callSessionIdMatched: true },
        );
        return;
      }
      const canonicalCorrelation =
        canonicalCallerCorrelationByCallRef.current.get(callSessionId);
      if (
        validSipCorrelation(normalizedCallerSipCorrelationId) &&
        canonicalCorrelation &&
        normalizedCallerSipCorrelationId !== canonicalCorrelation
      ) {
        const phone = softphoneRef.current;
        if (phone) {
          failClosedCallerCorrelation(
            phone,
            [callSessionId],
            [normalizedCallerSipCorrelationId, canonicalCorrelation],
          );
        }
        logger.warn(
          "sparkone.browser_voice.originate_correlation_hint_mismatch",
          { callSessionIdMatched: true },
        );
        return;
      }
      callerSipCorrelationHintRef.current = normalizedCallerSipCorrelationId;
      if (validSipCorrelation(normalizedCallerSipCorrelationId)) {
        const existingClassification = canonicalByCorrelationRef.current.get(
          normalizedCallerSipCorrelationId,
        );
        if (
          existingClassification &&
          (existingClassification.callSessionId !== callSessionId ||
            existingClassification.role !== "caller" ||
            existingClassification.kind !== "managed_caller")
        ) {
          const phone = softphoneRef.current;
          if (phone) {
            failClosedCallerCorrelation(
              phone,
              [existingClassification.callSessionId, callSessionId],
              [normalizedCallerSipCorrelationId],
            );
          }
          logger.warn(
            "sparkone.browser_voice.originate_correlation_identity_conflict",
            {
              callSessionIdMatched:
                existingClassification.callSessionId === callSessionId,
              participantRoleMatched: existingClassification.role === "caller",
            },
          );
          return;
        }
        setCappedMap(
          callerSipCorrelationHintByCallRef.current,
          callSessionId,
          normalizedCallerSipCorrelationId,
        );
        const localClassification: CanonicalCorrelationClassification = {
          callSessionId,
          kind: "managed_caller",
          role: "caller",
        };
        setCappedMap(
          canonicalByCorrelationRef.current,
          normalizedCallerSipCorrelationId,
          localClassification,
        );
        const phone = softphoneRef.current;
        if (phone) {
          for (const dialog of phone.getDialogs()) {
            if (
              dialog.direction !== "incoming" ||
              !["ringing", "answering", "connected"].includes(dialog.state) ||
              dialog.sipCorrelationId !== normalizedCallerSipCorrelationId
            )
              continue;
            setCappedMap(
              canonicalCallDialogsRef.current,
              callSessionId,
              dialog.dialogId,
            );
            if (dialog.state === "ringing") {
              applyCanonicalClassification(phone, dialog, localClassification);
            } else {
              setCappedMap(
                dialogClassificationsRef.current,
                dialog.dialogId,
                "managed_caller",
              );
              clearDialogEnrichmentTimeout(dialog.dialogId);
              addCappedSet(inviteEnrichedRef.current, dialog.dialogId);
              syncDialogs(phone);
            }
          }
        }
      } else {
        callerSipCorrelationHintByCallRef.current.delete(callSessionId);
      }
    },
    [
      applyCanonicalClassification,
      clearDialogEnrichmentTimeout,
      failClosedCallerCorrelation,
      syncDialogs,
    ],
  );

  const armManagedCaller = useCallback(
    async ({
      connectorId,
      sessionId,
      callSessionId,
      callerSipCorrelationId,
    }: {
      connectorId: string;
      sessionId: string;
      callSessionId: string;
      callerSipCorrelationId: string;
    }) => {
      const requireReadyRuntime = () => {
        const current = sessionRef.current;
        const phone = softphoneRef.current;
        if (
          !isBrowserPhoneOwner() ||
          !current ||
          current.authorityRevoked ||
          current.connectorId !== connectorId ||
          current.sessionId !== sessionId ||
          !current.registrationReadyObserved ||
          !phone
        ) {
          throw new Error(
            "The owning Browser phone is not ready for this exact session.",
          );
        }
        if (phone.getStatus() !== "registered" || !phone.isAcceptingOffers()) {
          throw new Error(
            "The Browser phone is not ready to accept the caller offer.",
          );
        }
        if (
          (current.callSessionId && current.callSessionId !== callSessionId) ||
          phone
            .getDialogs()
            .some((dialog) =>
              ["ringing", "answering", "connected"].includes(dialog.state),
            )
        ) {
          throw new Error(
            "The Browser phone is already handling another call.",
          );
        }
        if (!validSipCorrelation(callerSipCorrelationId)) {
          throw new Error("Spark VP returned an invalid caller correlation.");
        }
        const existingClassification = canonicalByCorrelationRef.current.get(
          callerSipCorrelationId,
        );
        const existingCallCorrelation =
          canonicalCallerCorrelationByCallRef.current.get(callSessionId) ??
          callerSipCorrelationHintByCallRef.current.get(callSessionId);
        if (
          (existingClassification &&
            (existingClassification.callSessionId !== callSessionId ||
              existingClassification.role !== "caller" ||
              existingClassification.kind !== "managed_caller")) ||
          (existingCallCorrelation &&
            existingCallCorrelation !== callerSipCorrelationId)
        ) {
          throw new Error(
            "The Browser call identity conflicts with active state.",
          );
        }
        return phone;
      };

      emitRuntimeVoiceEvent({
        eventName: "browser_voice.managed_caller.prepare_started",
        severity: "INFO",
        phase: "prepare",
        outcome: "started",
        callSessionId,
        sipCorrelationId: callerSipCorrelationId,
        mediaState: "requesting",
      });
      const phone = requireReadyRuntime();
      try {
        await phone.prepareManagedCallerMedia();
        emitRuntimeVoiceEvent({
          eventName: "browser_voice.managed_caller.prepare_succeeded",
          severity: "INFO",
          phase: "prepare",
          outcome: "succeeded",
          callSessionId,
          sipCorrelationId: callerSipCorrelationId,
          mediaState: "ready",
        });
        requireReadyRuntime();

        // After the awaited media preflight, the exact signed correlation is
        // armed synchronously before SparkOne acknowledges readiness and Spark
        // VP dispatches INVITE.
        attachCall(sessionId, callSessionId, callerSipCorrelationId);
        emitRuntimeVoiceEvent({
          eventName: "browser_voice.managed_caller.armed",
          severity: "INFO",
          phase: "arm",
          outcome: "succeeded",
          callSessionId,
          sipCorrelationId: callerSipCorrelationId,
        });
      } catch (error) {
        phone.releasePreparedManagedCallerMedia();
        emitRuntimeVoiceEvent({
          eventName: "browser_voice.managed_caller.prepare_failed",
          severity: "WARN",
          phase: "prepare",
          outcome: "failed",
          reasonCode: "media_preflight_failed",
          callSessionId,
          sipCorrelationId: callerSipCorrelationId,
          mediaState: "blocked",
        });
        throw error;
      }
    },
    [attachCall, emitRuntimeVoiceEvent, isBrowserPhoneOwner],
  );

  const endCall = useCallback(
    async (callSessionId: string): Promise<BrowserEndCallOutcome> => {
      const current = sessionRef.current;
      const phone = softphoneRef.current;
      if (!current || !phone) return "already_ended";

      const correlation =
        canonicalCallerCorrelationByCallRef.current.get(callSessionId) ??
        callerSipCorrelationHintByCallRef.current.get(callSessionId) ??
        null;
      const activeDialogs = phone
        .getDialogs()
        .filter((candidate) =>
          ["dialing", "connected", "answering"].includes(candidate.state),
        );
      const exactAttachedCall = current.callSessionId === callSessionId;
      const canonicalDialogId =
        canonicalCallDialogsRef.current.get(callSessionId);
      const incomingActiveDialogs = activeDialogs.filter(
        (candidate) => candidate.direction === "incoming",
      );
      const correlatedDialogs = correlation
        ? phone
            .getDialogs()
            .filter(
              (candidate) =>
                candidate.sipCorrelationId === correlation &&
                ["dialing", "ringing", "answering", "connected"].includes(
                  candidate.state,
                ),
            )
        : [];
      const dialog = exactAttachedCall
        ? correlatedDialogs.length > 0
          ? undefined
          : canonicalDialogId
            ? activeDialogs.find(
                (candidate) => candidate.dialogId === canonicalDialogId,
              )
            : undefined
        : current.callSessionId === null
          ? canonicalDialogId
            ? incomingActiveDialogs.find(
                (candidate) => candidate.dialogId === canonicalDialogId,
              )
            : incomingActiveDialogs.length === 1
              ? incomingActiveDialogs[0]
              : undefined
          : undefined;

      if (!exactAttachedCall && !dialog && correlatedDialogs.length === 0) {
        if (
          current.callSessionId === null &&
          incomingActiveDialogs.length === 0
        ) {
          return "already_ended";
        }
        logger.warn("sparkone.browser_voice.hangup_local_match_failed", {
          reason:
            current.callSessionId === null && incomingActiveDialogs.length > 1
              ? "ambiguous"
              : "no_match",
        });
        return "ambiguous_or_no_match";
      }
      if (
        exactAttachedCall &&
        !dialog &&
        correlatedDialogs.length === 0 &&
        activeDialogs.length > 0
      ) {
        logger.warn("sparkone.browser_voice.hangup_local_match_failed", {
          reason: "ambiguous",
        });
        return "ambiguous_or_no_match";
      }
      if (phone?.isMuted()) {
        try {
          phone.setMuted(false);
        } catch {
          // Dialog teardown below remains authoritative if media already ended.
        }
      }
      addCappedSet(terminalCallIdsRef.current, callSessionId);
      let terminated = false;
      if (correlation) {
        setCappedMap(canonicalByCorrelationRef.current, correlation, {
          callSessionId,
          kind: "terminal",
          role: "caller",
        });
        addCappedSet(consumedCallerCorrelationsRef.current, correlation);
        for (const correlatedDialog of correlatedDialogs) {
          setCappedMap(
            dialogClassificationsRef.current,
            correlatedDialog.dialogId,
            "managed_caller",
          );
          clearDialogEnrichmentTimeout(correlatedDialog.dialogId);
          phone.terminateDialog(correlatedDialog.dialogId);
          terminated = true;
        }
        syncDialogs(phone);
      }
      if (
        dialog &&
        !correlatedDialogs.some(
          (candidate) => candidate.dialogId === dialog.dialogId,
        )
      ) {
        phone.terminateDialog(dialog.dialogId);
        terminated = true;
      }
      canonicalCallDialogsRef.current.delete(callSessionId);
      if (exactAttachedCall) {
        current.callSessionId = null;
        callerSipCorrelationHintRef.current = null;
      }
      canonicalCallerCorrelationByCallRef.current.delete(callSessionId);
      callerSipCorrelationHintByCallRef.current.delete(callSessionId);
      phone.releasePreparedManagedCallerMedia();
      updatePublicState({ isMuted: false });
      if (!current.persistent) {
        await release({
          connectorId: current.connectorId,
          sessionId: current.sessionId,
        });
      }
      return terminated ? "terminated" : "already_ended";
    },
    [clearDialogEnrichmentTimeout, release, syncDialogs, updatePublicState],
  );

  const originatePreparedCall = useCallback(
    async (prepared: BrowserOriginatePreparedCall) => {
      const current = sessionRef.current;
      const phone = softphoneRef.current;
      if (!current || !phone || !isBrowserPhoneOwner()) {
        throw new Error("The owning Browser phone is not ready for this call.");
      }
      if (current.callSessionId || current.pendingBrowserOriginatedCall) {
        throw new Error("A Browser call is already active.");
      }
      await ensureFreshMediaCredentials(
        current,
        phone,
        current.mediaCredentialsJitMinimumMs,
        true,
      );
      // Claim the logical call before microphone access can yield. Cancellation
      // must be able to find this invocation even before JsSIP has a dialog.
      const pending = {
        preparedCallId: prepared.preparedCallId,
        correlationId: prepared.correlationId,
      };
      current.pendingBrowserOriginatedCall = pending;
      try {
        await phone.originatePreparedCall(prepared);
      } catch (error) {
        // Cancellation clears its own pending marker before the in-flight
        // microphone request resolves. It wins over any late setup failure.
        if (current.pendingBrowserOriginatedCall !== pending) return;
        current.pendingBrowserOriginatedCall = null;
        throw error;
      }
      if (current.pendingBrowserOriginatedCall !== pending) {
        // The cancellation path terminates a dialog if one was created while
        // this invocation was awaiting its continuation.
        return;
      }
      syncDialogs(phone);
    },
    [ensureFreshMediaCredentials, isBrowserPhoneOwner, syncDialogs],
  );

  const bindPreparedCall = useCallback(
    (input: {
      sessionId: string;
      preparedCallId: string;
      correlationId: string;
      callSessionId: string;
    }): boolean => {
      const current = sessionRef.current;
      const phone = softphoneRef.current;
      const pending = current?.pendingBrowserOriginatedCall;
      if (
        !current ||
        !phone ||
        current.sessionId !== input.sessionId ||
        !pending ||
        pending.preparedCallId !== input.preparedCallId ||
        pending.correlationId !== input.correlationId ||
        !phone
          .getDialogs()
          .some(
            (dialog) =>
              dialog.direction === "outgoing" &&
              dialog.sipCorrelationId === input.correlationId &&
              ["dialing", "ringing", "answering", "connected"].includes(
                dialog.state,
              ),
          )
      )
        return false;
      attachCall(input.sessionId, input.callSessionId, input.correlationId);
      current.pendingBrowserOriginatedCall = null;
      return true;
    },
    [attachCall],
  );

  const cancelPreparedCall = useCallback(
    async (preparedCallId: string): Promise<boolean> => {
      const current = sessionRef.current;
      const phone = softphoneRef.current;
      const pending = current?.pendingBrowserOriginatedCall;
      if (
        !current ||
        !phone ||
        !pending ||
        pending.preparedCallId !== preparedCallId
      )
        return false;
      // Clear first so the in-flight originator cannot publish a late dialog.
      current.pendingBrowserOriginatedCall = null;
      const cancelledInvocation = phone.cancelPreparedOutgoingCall(
        pending.preparedCallId,
        pending.correlationId,
      );
      const dialogs = phone
        .getDialogs()
        .filter(
          (dialog) =>
            dialog.direction === "outgoing" &&
            dialog.sipCorrelationId === pending.correlationId &&
            ["dialing", "ringing", "answering", "connected"].includes(
              dialog.state,
            ),
        );
      for (const dialog of dialogs) phone.terminateDialog(dialog.dialogId);
      phone.releasePreparedManagedCallerMedia();
      syncDialogs(phone);
      return cancelledInvocation || dialogs.length > 0;
    },
    [syncDialogs],
  );

  const disarmManagedCaller = useCallback(
    (callSessionId: string) => {
      const current = sessionRef.current;
      const phone = softphoneRef.current;
      if (!current || !phone || current.callSessionId !== callSessionId) {
        return false;
      }
      const correlation =
        canonicalCallerCorrelationByCallRef.current.get(callSessionId) ??
        callerSipCorrelationHintByCallRef.current.get(callSessionId) ??
        null;
      if (
        phone
          .getDialogs()
          .some(
            (dialog) =>
              dialog.state === "answering" ||
              dialog.state === "connected" ||
              (dialog.state === "ringing" &&
                (!correlation || dialog.sipCorrelationId === correlation)),
          )
      ) {
        return false;
      }
      current.callSessionId = null;
      callerSipCorrelationHintRef.current = null;
      canonicalCallDialogsRef.current.delete(callSessionId);
      canonicalCallerCorrelationByCallRef.current.delete(callSessionId);
      callerSipCorrelationHintByCallRef.current.delete(callSessionId);
      addCappedSet(terminalCallIdsRef.current, callSessionId);
      if (correlation) {
        setCappedMap(canonicalByCorrelationRef.current, correlation, {
          callSessionId,
          kind: "terminal",
          role: "caller",
        });
        addCappedSet(consumedCallerCorrelationsRef.current, correlation);
      }
      phone.releasePreparedManagedCallerMedia();
      updatePublicState({ isMuted: false });
      emitRuntimeVoiceEvent({
        eventName: "browser_voice.managed_caller.disarmed",
        severity: "INFO",
        phase: "arm",
        outcome: "ended",
        callSessionId,
        ...(correlation ? { sipCorrelationId: correlation } : {}),
      });
      return true;
    },
    [emitRuntimeVoiceEvent, updatePublicState],
  );

  const hasActiveManagedCallerDialog = useCallback((callSessionId: string) => {
    const current = sessionRef.current;
    const phone = softphoneRef.current;
    if (!current || !phone || current.callSessionId !== callSessionId) {
      return false;
    }
    const correlation =
      canonicalCallerCorrelationByCallRef.current.get(callSessionId) ??
      callerSipCorrelationHintByCallRef.current.get(callSessionId) ??
      null;
    const canonicalDialogId =
      canonicalCallDialogsRef.current.get(callSessionId) ?? null;
    return phone
      .getDialogs()
      .some(
        (dialog) =>
          ["ringing", "answering", "connected"].includes(dialog.state) &&
          ((Boolean(canonicalDialogId) &&
            dialog.dialogId === canonicalDialogId) ||
            (Boolean(correlation) && dialog.sipCorrelationId === correlation)),
      );
  }, []);

  useEffect(() => {
    disarmManagedCallerRef.current = disarmManagedCaller;
  }, [disarmManagedCaller]);

  useEffect(() => {
    mountedRef.current = true;
    const onAuthLost = () => {
      authEligibleRef.current = false;
      bootstrapGenerationRef.current += 1;
      browserReadinessTimingRef.current = null;
      invalidatePendingActivations();
      bootstrapRequestedRef.current = false;
      cancelBrowserRuntimeTimer(bootstrapRetryRef);
      cancelBrowserPreparationWait();
      void suspendRegistrationAuthority("auth_lost");
    };
    const reconcileBrowserRegistration = () => {
      if (!mountedRef.current) return;
      const current = sessionRef.current;
      const phone = softphoneRef.current;
      if (current && phone && !current.authorityRevoked) {
        if (phone.getStatus() !== "registered") phone.recoverRegistration();
        if (!current.installationId) void confirmRegistrationRef.current();
        return;
      }
      if (persistentEnabledRef.current && !persistentDenialRef.current) {
        bootstrapRequestedRef.current = true;
        void bootstrapRef.current();
      }
    };
    const revalidateAndReconcileBrowserRegistration = () => {
      void probeStoredSessionAuthority().then((result) => {
        if (result.status === "valid") reconcileBrowserRegistration();
      });
    };
    const onOnline = () => revalidateAndReconcileBrowserRegistration();
    const onPageShow = () => revalidateAndReconcileBrowserRegistration();
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        revalidateAndReconcileBrowserRegistration();
      }
    };
    const onAuthChanged = () => {
      if (
        !authEligibleRef.current ||
        !voiceSubscriptionReadyRef.current ||
        !persistentEnabledRef.current ||
        sessionRef.current
      )
        return;
      if (persistentDenialRef.current) return;
      bootstrapRequestedRef.current = true;
      void bootstrapRef.current();
    };
    window.addEventListener(browserSessionAuthLostEvent, onAuthLost);
    window.addEventListener(sparkOneAuthChangedEvent, onAuthChanged);
    window.addEventListener("online", onOnline);
    window.addEventListener("pageshow", onPageShow);
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      mountedRef.current = false;
      window.removeEventListener(browserSessionAuthLostEvent, onAuthLost);
      window.removeEventListener(sparkOneAuthChangedEvent, onAuthChanged);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("pageshow", onPageShow);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      bootstrapRequestedRef.current = false;
      browserReadinessTimingRef.current = null;
      authoritativeRegistrationSessionIdsRef.current.clear();
      cancelBrowserPreparationWait();
      registrationCoordinatorRef.current?.dispose();
      preparationCoordinatorRef.current?.dispose();
      void releaseForProviderUnmount().catch(() => {
        logger.warn("sparkone.browser_voice.provider_cleanup_failed", {
          failureCategory: "local_cleanup_failed",
        });
      });
    };
  }, [
    cancelBrowserPreparationWait,
    invalidatePendingActivations,
    releaseForProviderUnmount,
    suspendRegistrationAuthority,
  ]);

  useEffect(() => {
    // The epoch changes on every successful Web Lock acquisition, including
    // handoff after an optimistic tab already received a 409 collision. That
    // guarantees the newly elected tab gets a fresh bootstrap attempt even if
    // its boolean ownership value was already true.
    if (
      authEligibleRef.current &&
      voiceSubscriptionReady &&
      ownsBrowserPhone &&
      persistentEnabled &&
      !sessionRef.current
    ) {
      void bootstrapPersistent();
    }
  }, [
    bootstrapPersistent,
    browserPhoneLeadershipEpoch,
    ownsBrowserPhone,
    persistentEnabled,
    voiceSubscriptionReady,
  ]);

  useEffect(() => {
    // Dormant tab: another tab won leadership. Render a standing-by state and
    // do not touch the SIP UA. The bootstrap effect above takes over the moment
    // leadership hands back to this tab. Never clobber an in-flight session's
    // lifecycle (the lock semantics prevent this, but guard defensively).
    if (ownsBrowserPhone || !persistentEnabled || sessionRef.current) return;
    cancelBrowserPreparationWait();
    updatePublicState({
      lifecycle: "unavailable",
      message: "Calling is active in another tab. This tab is standing by.",
      dialogs: [],
    });
  }, [
    cancelBrowserPreparationWait,
    ownsBrowserPhone,
    persistentEnabled,
    updatePublicState,
  ]);

  useEffect(() => {
    if (persistentEnabled) return;
    bootstrapGenerationRef.current += 1;
    browserReadinessTimingRef.current = null;
    invalidatePendingActivations();
    bootstrapRequestedRef.current = false;
    cancelBrowserRuntimeTimer(bootstrapRetryRef);
    cancelBrowserPreparationWait();
    bootstrapAttemptRef.current = 0;
    if (sessionRef.current?.persistent) {
      void suspendRegistrationAuthority("auth_lost");
    }
  }, [
    cancelBrowserPreparationWait,
    invalidatePendingActivations,
    persistentEnabled,
    suspendRegistrationAuthority,
  ]);

  useEffect(() => {
    const unsubscribe = subscribeVoice({
      onBrowserRegistrationChanged: (payload) => {
        preparationCoordinatorRef.current?.observeRealtime(payload);
        registrationCoordinatorRef.current?.observeRealtime(payload);
        const current = sessionRef.current;
        if (
          !current?.installationId ||
          payload.installationId !== current.installationId
        ) {
          // Wake-on-release: a dormant tab (no current installation of its own)
          // still observes the registered installation's terminal events. When
          // the active installation dies, prime a bootstrap request so this tab
          // takes over the moment it wins leadership back. The lock callback is
          // what actually flips ownership; this keeps the dormant tab ready.
          if (
            authEligibleRef.current &&
            !current?.installationId &&
            !ownsBrowserPhoneRef.current &&
            ["revoked", "expired", "failed"].includes(payload.status)
          ) {
            bootstrapRequestedRef.current = true;
          }
          return;
        }
        const matchesCurrent =
          payload.generation === current.installationGeneration &&
          payload.sessionId === current.sessionId;
        const pendingInstallationTarget = current.pendingInstallationTarget;
        const matchesPending =
          payload.generation === pendingInstallationTarget?.generation &&
          payload.sessionId === pendingInstallationTarget?.sessionId;
        if (!matchesCurrent && !matchesPending) return;
        const authorityPath = authorityPathForPayload(payload);
        const providerToSocketMs = trustedProviderToSocketLatency(payload);
        logger.info("sparkone.browser_voice.authoritative_socket_received", {
          generation: payload.generation,
          registrationState: payload.status,
          authorityPath,
          ...(providerToSocketMs === null ? {} : { providerToSocketMs }),
        });
        emitRuntimeVoiceEvent({
          eventName: "browser_voice.registration.authoritative_socket_received",
          severity: "INFO",
          phase: "registration",
          outcome: "succeeded",
          registrationState:
            payload.status === "registered" ? "registered" : "registering",
          authorityPath,
          ...(providerToSocketMs === null
            ? {}
            : { elapsedMs: providerToSocketMs }),
        });
        current.installationStateVersion = Math.max(
          current.installationStateVersion ?? 0,
          payload.stateVersion,
        );
        if (matchesCurrent) {
          current.providerRegistrationState = payload.status;
          if (
            payload.status === "registered" &&
            softphoneRef.current?.getStatus() === "registered"
          ) {
            current.localRegistrationState = "local_registered";
          }
          if (payload.status === "registered") {
            const episode = current.localRegistrationEpisodes.get(
              current.sessionId,
            );
            logger.info(
              "sparkone.browser_voice.authoritative_registration_projected",
              {
                generation: payload.generation,
                authorityPath,
                ...(episode
                  ? {
                      localToAuthoritativeMs: boundedMonotonicLatency(
                        episode.observedAtMonotonic,
                      ),
                    }
                  : {}),
              },
            );
            cancelBrowserRuntimeTimer(localConfirmationWarningRef);
            updatePublicState({
              lifecycle:
                current.localRegistrationState === "local_registered"
                  ? "ready"
                  : "registering",
              localRegistrationState: current.localRegistrationState,
              providerRegistrationState: "registered",
              message:
                current.localRegistrationState === "local_registered"
                  ? "Browser phone ready."
                  : "Connecting Browser phone…",
            });
          } else {
            updatePublicState({ providerRegistrationState: payload.status });
          }
        }
        if (
          matchesCurrent &&
          ["revoked", "expired", "failed"].includes(payload.status)
        ) {
          const pendingTarget = current.pendingInstallationTarget;
          const installationWasRevoked = payload.lifecycleState === "REVOKED";
          if (
            current.pendingRenewal &&
            pendingTarget &&
            !installationWasRevoked
          ) {
            // The predecessor can expire or be retired while its exact successor
            // is already registering. Reconcile that successor immediately; an
            // installation DELETE would revoke both generations and destroy the
            // valid recovery path.
            recoveryEpisodeRef.current += 1;
            void registrationCoordinatorRef.current
              ?.recoverOnce(
                `predecessor-terminal:${payload.status}:${recoveryEpisodeRef.current}`,
                pendingTarget,
              )
              .catch(() => undefined)
              .finally(() => {
                if (
                  sessionRef.current === current &&
                  current.pendingRenewal &&
                  current.pendingInstallationTarget === pendingTarget &&
                  !current.authorityRevoked
                ) {
                  // Whether recovery registered the target, remained
                  // inconclusive, or failed, run the exact-target path now. A
                  // resolved read may have populated only the coordinator cache
                  // when its original waiter already timed out.
                  scheduleInstallationRef.current(current, 0);
                }
              });
            return;
          }
          if (
            payload.lifecycleState === "REVOKED" ||
            payload.lifecycleState === "DISABLED"
          ) {
            void suspendRegistrationAuthority(
              payload.lifecycleState === "DISABLED"
                ? "voice_disabled"
                : "administrative_revoke",
            );
            return;
          }
          if (current.persistent) {
            // Expiry/failure is recoverable session state, not proof that the
            // user or installation was administratively disabled.
            scheduleInstallationRef.current(current, 0);
            return;
          }
          void suspendRegistrationAuthority("session_expired");
        }
      },
      onRecover: () => {
        const current = sessionRef.current;
        if (
          !USE_AUTHORITATIVE_BROWSER_INSTALLATIONS ||
          !current?.installationId ||
          current.installationGeneration === null
        )
          return;
        recoveryEpisodeRef.current += 1;
        return voiceApi
          .reconcileBrowserInstallation(current.installationId)
          .catch(() => undefined)
          .then(() => {
            return registrationCoordinatorRef.current?.recoverOnce(
              `realtime:${recoveryEpisodeRef.current}`,
              {
                installationId: current.installationId!,
                generation: current.installationGeneration!,
                sessionId: current.sessionId,
              },
            );
          })
          .then(() => undefined)
          .catch((error: unknown) => {
            logger.warn(
              "sparkone.browser_voice.registration_recovery_read_failed",
              {
                failureCategory:
                  error instanceof Error ? "not_ready" : "request_failed",
              },
            );
          });
      },
      onCallChanged: (payload) => {
        const current = sessionRef.current;
        const phone = softphoneRef.current;
        if (current && phone && payload.connectorId === current.connectorId) {
          const priorSequence = canonicalSequenceByCallRef.current.get(
            payload.callSessionId,
          );
          const isNewCanonicalProjection =
            priorSequence === undefined ||
            payload.stateSequence > priorSequence;
          if (isNewCanonicalProjection) {
            setCappedMap(
              canonicalSequenceByCallRef.current,
              payload.callSessionId,
              payload.stateSequence,
            );
            for (const participant of payload.participants ?? []) {
              const correlation = participant.sipCorrelationId;
              if (
                !validSipCorrelation(correlation) ||
                participant.endpointDeviceId !== current.endpointDeviceId ||
                participant.handleUserRef !== current.clientUserRef
              )
                continue;

              const participantState = participant.state.toLowerCase();
              const payloadTerminal = ["ENDED", "FAILED"].includes(
                payload.state,
              );
              const participantTerminal =
                TERMINAL_PARTICIPANT_STATES.has(participantState);
              let classification: CanonicalCorrelationClassification | null =
                null;
              const participantRole =
                participant.role === "caller"
                  ? "caller"
                  : participant.role === "device_leg"
                    ? "device_leg"
                    : null;
              if (!participantRole) continue;

              // Inbound Answer is fail-closed. An opaque SIP correlation is
              // necessary but not sufficient: the canonical provider event
              // must bind the current tenant Handle, Browser installation,
              // Browser endpoint, call, and live device leg. Internal
              // extension calls use INTERNAL; carrier DID calls use INBOUND.
              const exactInboundDeviceLeg =
                participantRole === "device_leg" &&
                (payload.direction === "INBOUND" ||
                  payload.direction === "INTERNAL") &&
                participant.endpointDeviceId === current.endpointDeviceId &&
                participant.endpointType === "browser_webrtc" &&
                participant.handleUserRef === current.clientUserRef;

              const existingClassification =
                canonicalByCorrelationRef.current.get(correlation);
              const correlationIdentityConflict = Boolean(
                existingClassification &&
                (existingClassification.callSessionId !==
                  payload.callSessionId ||
                  existingClassification.role !== participantRole),
              );
              if (correlationIdentityConflict && existingClassification) {
                failClosedCallerCorrelation(
                  phone,
                  [existingClassification.callSessionId, payload.callSessionId],
                  [correlation],
                );
                logger.warn(
                  "sparkone.browser_voice.correlation_identity_conflict",
                  {
                    callSessionIdMatched:
                      existingClassification.callSessionId ===
                      payload.callSessionId,
                    participantRoleMatched:
                      existingClassification.role === participantRole,
                  },
                );
                continue;
              }

              if (
                participantRole === "caller" &&
                (payload.direction === "OUTBOUND" ||
                  payload.direction === "INTERNAL") &&
                (!current.callSessionId ||
                  current.callSessionId === payload.callSessionId)
              ) {
                const correlationHintConflict = Boolean(
                  callerSipCorrelationHintRef.current &&
                  callerSipCorrelationHintRef.current !== correlation,
                );
                if (correlationHintConflict) {
                  failClosedCallerCorrelation(
                    phone,
                    [payload.callSessionId],
                    [callerSipCorrelationHintRef.current, correlation],
                  );
                  logger.warn(
                    "sparkone.browser_voice.originate_correlation_hint_mismatch",
                    { callSessionIdMatched: true },
                  );
                  continue;
                }
                const matchingManagedClassification =
                  existingClassification?.kind === "managed_caller" &&
                  existingClassification.callSessionId ===
                    payload.callSessionId &&
                  existingClassification.role === "caller";
                classification = {
                  callSessionId: payload.callSessionId,
                  kind:
                    payloadTerminal ||
                    participantTerminal ||
                    existingClassification?.kind === "terminal" ||
                    (consumedCallerCorrelationsRef.current.has(correlation) &&
                      !matchingManagedClassification) ||
                    terminalCallIdsRef.current.has(payload.callSessionId)
                      ? "terminal"
                      : "managed_caller",
                  role: "caller",
                };
                if (classification.kind !== "terminal") {
                  setCappedMap(
                    canonicalCallerCorrelationByCallRef.current,
                    payload.callSessionId,
                    correlation,
                  );
                }
                if (
                  !current.callSessionId &&
                  classification.kind !== "terminal"
                ) {
                  current.callSessionId = payload.callSessionId;
                }
              } else if (participantRole === "device_leg") {
                classification = {
                  callSessionId: payload.callSessionId,
                  kind:
                    payloadTerminal ||
                    participantTerminal ||
                    existingClassification?.kind === "terminal"
                      ? "terminal"
                      : exactInboundDeviceLeg
                        ? "actionable_inbound"
                        : "unresolved_inbound",
                  role: "device_leg",
                };
                if (
                  classification.kind !== "terminal" &&
                  participantState === "answered" &&
                  payload.state === "ANSWERED" &&
                  (!current.callSessionId ||
                    current.callSessionId === payload.callSessionId)
                ) {
                  // Outbound calls are attached when originate completes.
                  // Inbound calls acquire that same exact-call attachment
                  // only after the canonical projection confirms that this
                  // Browser endpoint won and answered the device leg.
                  current.callSessionId = payload.callSessionId;
                }
              }
              if (!classification) continue;

              if (classification.kind === "terminal") {
                addCappedSet(
                  consumedCallerCorrelationsRef.current,
                  correlation,
                );
                addCappedSet(terminalCallIdsRef.current, payload.callSessionId);
              }

              setCappedMap(
                canonicalByCorrelationRef.current,
                correlation,
                classification,
              );
              for (const dialog of phone.getDialogs()) {
                if (dialog.sipCorrelationId !== correlation) continue;
                setCappedMap(
                  canonicalCallDialogsRef.current,
                  payload.callSessionId,
                  dialog.dialogId,
                );
                if (
                  classification.kind === "actionable_inbound" &&
                  payload.state === "RINGING" &&
                  participantState === "ringing" &&
                  exactInboundDeviceLeg &&
                  participant.endpointDeviceId
                ) {
                  setCappedMap(
                    canonicalDialogTargetsRef.current,
                    dialog.dialogId,
                    {
                      connectorId: current.connectorId,
                      callSessionId: payload.callSessionId,
                      participantId: participant.participantId,
                      endpointDeviceId: participant.endpointDeviceId,
                      sipCorrelationId: correlation,
                      answerAuthorized:
                        payload.availableCommands?.includes("answer") === true,
                      rejectAuthorized:
                        payload.availableCommands?.includes("reject") === true,
                    },
                  );
                } else {
                  canonicalDialogTargetsRef.current.delete(dialog.dialogId);
                }
                if (!inviteEnrichedRef.current.has(dialog.dialogId)) {
                  const inviteObservedAt = inviteObservedAtRef.current.get(
                    dialog.dialogId,
                  );
                  logger.info("sparkone.browser_voice.invite_enriched", {
                    ...(inviteObservedAt === undefined
                      ? {}
                      : { latencyMs: boundedLatency(inviteObservedAt) }),
                    callState: callStateCategory(payload.state),
                    participantOutcome: participantOutcomeCategory(
                      participant.state,
                    ),
                  });
                }
                applyCanonicalClassification(phone, dialog, classification);
                if (
                  classification.kind === "terminal" &&
                  participant.role === "device_leg"
                ) {
                  updatePublicState({
                    lifecycle: "ready",
                    message: "Call answered on another phone.",
                    notice: {
                      kind: "answered_elsewhere",
                      message: "Call answered on another phone.",
                      observedAt: Date.now(),
                    },
                  });
                }
              }

              if (classification.kind === "managed_caller") {
                updatePublicState({
                  lifecycle:
                    payload.state === "ANSWERED" ? "connected" : "ringing",
                  message:
                    payload.state === "ANSWERED"
                      ? "Call connected."
                      : "Calling…",
                });
              } else if (
                classification.kind === "actionable_inbound" &&
                participantState === "answered" &&
                payload.state === "ANSWERED" &&
                current.callSessionId === payload.callSessionId
              ) {
                updatePublicState({
                  lifecycle: "connected",
                  message: "Call connected.",
                });
              }
            }
          }
          for (const [
            dialogId,
            pendingAction,
          ] of pendingCanonicalActionsRef.current) {
            if (!pendingAction.sipCorrelationId) continue;
            const participant = payload.participants?.find(
              (candidate) =>
                candidate.sipCorrelationId === pendingAction.sipCorrelationId &&
                candidate.endpointDeviceId === current.endpointDeviceId &&
                candidate.handleUserRef === current.clientUserRef,
            );
            if (!participant) continue;
            const participantOutcome = participantOutcomeCategory(
              participant.state,
            );
            if (
              participantOutcome === "ringing" ||
              participantOutcome === "other"
            )
              continue;
            pendingCanonicalActionsRef.current.delete(dialogId);
            logger.info("sparkone.browser_voice.action_canonicalized", {
              action: pendingAction.action,
              latencyMs: boundedLatency(pendingAction.startedAt),
              participantOutcome,
              callState: callStateCategory(payload.state),
            });
          }
        }
        if (
          current?.callSessionId === payload.callSessionId &&
          payload.clientUserRef === current.clientUserRef &&
          ["ENDED", "FAILED"].includes(payload.state)
        ) {
          void endCall(payload.callSessionId);
        }
      },
      onEmployeeVoiceChanged: (payload) => {
        const current = sessionRef.current;
        if (
          current?.clientUserRef ===
            `sparkone:user:${payload.tenantMembershipId}` &&
          !payload.voiceEnabled
        )
          void suspendRegistrationAuthority("voice_disabled");
      },
    });
    voiceSubscriptionReadyRef.current = true;
    setVoiceSubscriptionReady(true);
    return () => {
      voiceSubscriptionReadyRef.current = false;
      setVoiceSubscriptionReady(false);
      unsubscribe();
    };
  }, [
    applyCanonicalClassification,
    endCall,
    emitRuntimeVoiceEvent,
    failClosedCallerCorrelation,
    subscribeVoice,
    suspendRegistrationAuthority,
    updatePublicState,
  ]);

  const value = useMemo<RuntimeContext>(
    () => ({
      ownsBrowserPhone,
      lifecycle: publicState.lifecycle,
      incomingDialogs: publicState.dialogs,
      hasActiveCall: publicState.hasActiveCall,
      isTenantSwitchBlocked,
      runtimeMessage: publicState.message,
      localRegistrationState: publicState.localRegistrationState,
      providerRegistrationState: publicState.providerRegistrationState,
      runtimeNotice: publicState.notice ?? null,
      mediaState: publicState.mediaState,
      audioDevices,
      selectedInputDeviceId,
      selectedOutputDeviceId,
      supportsOutputSelection,
      audioPreferenceWarning,
      sipStatus,
      terminalEvent,
      isMuted: publicState.isMuted,
      isTransitioning,
      isBrowserPhoneOwner,
      isSessionActive,
      getActivePersistentRegistration,
      getActiveCallIdentity,
      isCallActive,
      hasActiveManagedCallerDialog,
      answerIncoming,
      rejectIncoming,
      refreshAudioDevices,
      selectInputDevice,
      selectOutputDevice,
      retryAudio,
      retryRegistration,
      observeReadiness,
      setMuted,
      start,
      attachCall,
      armManagedCaller,
      originatePreparedCall,
      bindPreparedCall,
      cancelPreparedCall,
      disarmManagedCaller,
      endCall,
      release,
    }),
    [
      answerIncoming,
      audioDevices,
      audioPreferenceWarning,
      attachCall,
      armManagedCaller,
      originatePreparedCall,
      bindPreparedCall,
      cancelPreparedCall,
      disarmManagedCaller,
      endCall,
      getActivePersistentRegistration,
      getActiveCallIdentity,
      hasActiveManagedCallerDialog,
      isBrowserPhoneOwner,
      isCallActive,
      isTenantSwitchBlocked,
      isSessionActive,
      isTransitioning,
      ownsBrowserPhone,
      publicState,
      refreshAudioDevices,
      rejectIncoming,
      retryAudio,
      retryRegistration,
      observeReadiness,
      release,
      setMuted,
      selectedInputDeviceId,
      selectedOutputDeviceId,
      selectInputDevice,
      selectOutputDevice,
      sipStatus,
      start,
      terminalEvent,
      supportsOutputSelection,
    ],
  );
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useBrowserVoiceRuntime(): RuntimeContext {
  const value = useContext(Context);
  if (!value) throw new Error("BrowserVoiceRuntimeProvider is required");
  return value;
}

export function useOptionalBrowserVoiceRuntime(): RuntimeContext | null {
  return useContext(Context);
}
